import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DraggableGoalCard from '../components/DraggableGoalCard';
import GoalDropZone from '../components/GoalDropZone';
import {
    Button,
    AlertBar,
    CircularLoader,
    colors,
    IconView16,
    IconChevronDown24, IconChevronRight24,
    IconDelete16,
    IconAdd16,
    IconEdit16,
    IconAddCircle16,
    IconCheckmark16,
    IconUpload16,
    IconDownload16,
    IconLink16,
} from '@dhis2/ui';
import { Link, Goal, ClipboardList, Pencil, Crown } from 'lucide-react'
import { useDataEngine } from '@dhis2/app-runtime';
import { usePerformanceContext, dataElementMapping } from '../PerformanceContext';
import { useAuth } from '../../auth/AuthContext';
import { fetchObjectiveWithDetails, saveObjectiveEvent, createEventPayload } from '../hooks/ObjectiveService';
import { ORG_GOALS_PROGRAM, ORG_GOALS_STAGE } from '../api/GoalsConstants';

// import useNotificationService from '../../notifications/services/notificationService';
import { POSITION_ATTRIBUTES } from '../../staff/api/constants';
import { isWithinFiscalYear, isFiscalYearActive, getFiscalYearLabel, isFiscalYearConfigValid, getFallbackFiscalYear, resolveActiveFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers';
import * as XLSX from 'xlsx';
import { parseImportedObjectives, downloadObjectiveTemplate, exportPastYearObjectivesToExcel } from '../utils/objectiveImportExport';
import { GiCancel, GiLinkedRings, GiTargetArrows, GiTargeted, GiTargetShot } from 'react-icons/gi';
// import useEmailNotification from '../hooks/useEmailNotification';
import emailApi from '../services/emailApi';


// ===== UPDATED COMPONENT WITH PER-OBJECTIVE GOAL SELECTION =====
const EditModeObjectiveList = memo(({
    objectives,
    expandedObjectiveIndex,
    totalWeight,
    isSubmitting,
    onCancel,
    handleSubmit,
    handleRemoveObjectiveInEditMode,
    handleAddObjectiveInEditMode,
    updateObjectiveField,
    handleEditObjectiveWeightChange,
    setExpandedObjectiveIndex,
    // NEW PROPS
    goals,
    loadingGoals,
    isTopLevelUser,
    onGoalSelect, // New callback for when goal is selected
    selectedHigherLevelObjective,
    higherLevelObjectives,
    loadingHigherLevel,
    loadingHigherLevelObjectives,
    onHigherLevelObjectiveSelect,
    userRole, // To determine if CEO or not
    handleKraChange,
    handleAddKra,
    handleRemoveKra,
    handleKpiChange,
    handleAddKpi,
    handleRemoveKpi
    }) => {
        if (!objectives?.length) return null;
        const remainingWeight = 100 - totalWeight;

        // Helper to render goal dropdown
        const renderGoalDropdown = (objective, index) => {
            if (!isTopLevelUser) return null;

            return (
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <strong>Company Goal Alignment *</strong>
                    
                    {/* ✅ FULLY EDITABLE - No read-only restrictions */}
                    <select
                        value={objective.companyAlignedGoal || ''}
                        onChange={(e) => {
                            const goalId = e.target.value;
                            updateObjectiveField(index, 'companyAlignedGoal', goalId);
                            if (onGoalSelect) {
                                onGoalSelect(goalId, index);
                            }
                        }}
                        style={{
                            padding: '10px',
                            border: `1px solid ${objective.companyAlignedGoal ? colors.blue300 : colors.orange400}`,
                            borderRadius: '4px',
                            fontSize: '0.95rem',
                            backgroundColor: objective.companyAlignedGoal ? colors.blue050 : 'white',
                            fontWeight: objective.companyAlignedGoal ? 'bold' : 'normal',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                        disabled={loadingGoals || isSubmitting} // ✅ Only disabled while loading/submitting
                    >
                        <option value="">-- Select a Company Goal --</option>
                        {goals.map(goal => (
                            <option key={goal.id} value={goal.id}>
                                {goal.name} {goal.description ? `- ${goal.description.substring(0, 30)}...` : ''}
                            </option>
                        ))}
                    </select>
                    {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                </label>

                {/* Show goal details if selected */}
                {objective.companyAlignedGoal && (
                    <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: colors.green050,
                        border: `1px solid ${colors.green300}`,
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '1.2rem' }}><Goal size={16} /></span>
                            <span>
                                <strong>Aligned Goal:</strong>{' '}
                                {goals.find(g => g.id === objective.companyAlignedGoal)?.name || 'Unknown Goal'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };
  


    // Add this helper function at the top of your EditModeObjectiveList component
    const findLinkedGoal = (objId) => {
        if (!objId || !higherLevelObjectives.length || !goals.length) return null;

        const selectedObj = higherLevelObjectives.find(obj => obj.id === objId);
        if (!selectedObj) return null;

        // Try to find direct goal linkage
        const directGoalId = selectedObj.dataValues?.find(
            dv => dv.dataElement === 'VhrIQQIVpB9'
        )?.value;

        if (directGoalId) {
            const goal = goals.find(g => g.id === directGoalId);
            if (goal) return goal;
        }

        // Try to find through parent chain (for level 6 objectives that might link to CEO objectives)
        const parentId = selectedObj.dataValues?.find(
            dv => dv.dataElement === 'neuilg3L4d2' || dv.dataElement === 'plJovUlxfQ0'
        )?.value;

        if (parentId) {
            const parentObj = higherLevelObjectives.find(obj => obj.id === parentId);
            if (parentObj) {
                const parentGoalId = parentObj.dataValues?.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                )?.value;

                if (parentGoalId) {
                    return goals.find(g => g.id === parentGoalId);
                }
            }
        }

        return null;
    };



     
    // Helper to render higher-level objective dropdown (for non-CEO)        
        const renderHigherLevelDropdown = (objective, index) => {
            if (isTopLevelUser) return null;

            // Find which manager objective is currently linked
            const currentManagerObjId = objective.objectiveAlignment;
            const currentGoalId = objective.companyAlignedGoal || objective._tempGoalId;

            return (
                <div style={{ marginBottom: '16px' }}>
                    {/* 1. FIRST: Company Goal Alignment (Mandatory) */}
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.blue050, borderRadius: '6px' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Goal size={16} />
                                Link to Company Goal 
                                {!objective.companyAlignedGoal && !objective._tempGoalId && (
                                    <span style={{ color: 'red', fontSize: '12px' }}>* Required</span>
                                )}
                            </strong>
                            
                            <select
                                value={objective.companyAlignedGoal || objective._tempGoalId || ''}
                                onChange={(e) => {
                                    const goalId = e.target.value;
                                    updateObjectiveField(index, 'companyAlignedGoal', goalId);
                                    updateObjectiveField(index, '_tempGoalId', goalId);
                                    
                                    // Auto-populate manager's goal name if available
                                    const goal = goals.find(g => g.id === goalId);
                                    if (goal) {
                                        updateObjectiveField(index, '_managerGoalName', goal.name);
                                    }
                                }}
                                style={{
                                    padding: '10px',
                                    border: `1px solid ${(objective.companyAlignedGoal || objective._tempGoalId) ? colors.green400 : colors.orange400}`,
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    backgroundColor: (objective.companyAlignedGoal || objective._tempGoalId) ? colors.green050 : 'white',
                                    fontWeight: (objective.companyAlignedGoal || objective._tempGoalId) ? 'bold' : 'normal',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                disabled={loadingGoals} // ✅ Only disabled while loading
                            >
                                <option value="">-- Select a Company Goal --</option>
                                {goals.map(goal => (
                                    <option key={goal.id} value={goal.id}>
                                        {goal.name}
                                    </option>
                                ))}
                            </select>
                            {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                        </label>
                        
                        {/* Show selected goal details */}
                        {(objective.companyAlignedGoal || objective._tempGoalId) && (
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                backgroundColor: colors.green100,
                                borderLeft: `3px solid ${colors.green600}`,
                                borderRadius: '4px',
                                fontSize: '0.85rem'
                            }}>
                                🎯 <strong>Aligned Goal:</strong> {goals.find(g => g.id === (objective.companyAlignedGoal || objective._tempGoalId))?.name}
                            </div>
                        )}
                    </div>

                    {/* 2. SECOND: Manager Objective Alignment (Optional) */}
                    <div style={{ padding: '12px', backgroundColor: colors.purple050, borderRadius: '6px' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                👥 Link to Manager Objective 
                                <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>(Optional)</span>
                            </strong>
                            
                            <select
                                value={objective.objectiveAlignment || ''}
                                onChange={(e) => {
                                    const objectiveId = e.target.value;
                                    const selectedObj = higherLevelObjectives.find(obj => obj.id === objectiveId);

                                    if (selectedObj && objectiveId !== '') {
                                        updateObjectiveField(index, 'objectiveAlignment', selectedObj.id);
                                        updateObjectiveField(index, 'higherLevelObjectiveId', selectedObj.id);
                                        updateObjectiveField(index, 'higherLevelObjectiveName', selectedObj.name);
                                        
                                        if (selectedObj.companyAlignedGoal && !objective.companyAlignedGoal && !objective._tempGoalId) {
                                            updateObjectiveField(index, 'companyAlignedGoal', selectedObj.companyAlignedGoal);
                                            updateObjectiveField(index, '_tempGoalId', selectedObj.companyAlignedGoal);
                                            const goal = goals.find(g => g.id === selectedObj.companyAlignedGoal);
                                            if (goal) {
                                                updateObjectiveField(index, '_managerGoalName', goal.name);
                                            }
                                        }
                                    } else {
                                        updateObjectiveField(index, 'objectiveAlignment', '');
                                        updateObjectiveField(index, 'higherLevelObjectiveId', '');
                                        updateObjectiveField(index, 'higherLevelObjectiveName', '');
                                        updateObjectiveField(index, '_managerGoalName', '');
                                    }
                                }}
                                style={{
                                    padding: '10px',
                                    border: `1px solid ${colors.purple300}`,
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                disabled={loadingHigherLevelObjectives} // ← USE THE PROP HERE
                            >
                                <option value="">-- Not Linked (Optional) --</option>
                                {higherLevelObjectives.map(obj => (
                                    <option key={obj.id} value={obj.id}>
                                        {obj.name}                                         
                                    </option>
                                ))}
                            </select>
                            {loadingHigherLevelObjectives && <small style={{ color: colors.blue600 }}>Loading manager objectives...</small>} 
                        </label>
                        
                        {/* Show selected manager objective details */}
                        {objective.objectiveAlignment && (
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                backgroundColor: colors.green100,
                                borderLeft: `3px solid ${colors.purple600}`,
                                borderRadius: '4px',
                                fontSize: '0.85rem'
                            }}>
                                👥 <strong>Aligned to:</strong> {objective.higherLevelObjectiveName}
                                {(objective.companyAlignedGoal || objective._tempGoalId) && objective.companyAlignedGoal !== (objective._tempGoalId) && (
                                    <div style={{ marginTop: '4px', fontSize: '0.8rem' }}>
                                        🎯 <strong>Company Goal:</strong> {goals.find(g => g.id === (objective.companyAlignedGoal || objective._tempGoalId))?.name}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Help text */}
                        <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                            Linking to a manager objective is optional. You can link directly to a company goal above.
                        </div>
                    </div>
                </div>
            );
        };

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* Weight Summary Banner - unchanged */}
            <div style={{
                backgroundColor: colors.blue050,
                border: `1px solid ${colors.blue300}`,
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '16px'
            }}>
                <h4 style={{
                    margin: '0 0 12px 0',
                    color: colors.blue900,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '28px',
                        height: '28px',
                        backgroundColor: colors.blue600,
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                    }}>
                        {objectives.length}
                    </div>
                    Edit Weight Group Objectives
                </h4>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.95rem', color: colors.blue800 }}>
                    You have {objectives.length} objective{objectives.length > 1 ? 's' : ''} sharing 100% total weight.
                    {remainingWeight > 0 && (
                        <span style={{ fontWeight: 'bold', color: colors.orange700 }}>
                            {' '}⚠️ {remainingWeight}% weight not allocated yet.
                        </span>
                    )}
                </p>

                {/* Weight Visualization */}
                <div style={{ marginTop: '12px' }}>
                    <div style={{
                        height: '8px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                    }}>
                        {objectives.map((obj, idx) => (
                            <div
                                key={`weight-bar-${obj.id || obj._tempId || idx}`}
                                style={{
                                    width: `${obj.weight || 0}%`,
                                    height: '100%',
                                    backgroundColor: idx % 2 === 0 ? colors.blue500 : colors.blue700,
                                    float: 'left',
                                    transition: 'width 0.3s ease'
                                }}
                                title={`${obj.name || `Objective ${idx + 1}`}: ${obj.weight || 0}%`}
                            />
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <strong style={{ color: totalWeight === 100 ? colors.green700 : colors.blue700 }}>
                            Total: {totalWeight}%
                        </strong>
                        {totalWeight === 100 ? (
                            <span style={{ color: colors.green700, fontWeight: 500 }}>✅ Complete</span>
                        ) : remainingWeight > 0 ? (
                            <span style={{ color: colors.orange700, fontWeight: 500 }}>
                                ⚠️ {remainingWeight}% remaining
                            </span>
                        ) : (
                            <span style={{ color: colors.red700, fontWeight: 500 }}>
                                ❌ Over by {Math.abs(remainingWeight)}%
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* CRITICAL: Stable keys on EVERY objective card */}
            {objectives.map((objective, index) => (
                <div
                    key={objective.id || objective._tempId || `obj-${index}`}
                    id={`objective-card-${index}`}
                    style={{
                        marginBottom: '12px',
                        border: `1px solid ${colors.grey300}`,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        boxShadow: expandedObjectiveIndex === index ? `0 4px 12px ${colors.blue400}` : 'none'
                    }}
                >
                    {/* Card Header */}
                    <div
                        onClick={() => {
                            // Toggle logic: if this index is already expanded, set to null (close), otherwise set to this index (open)
                            if (expandedObjectiveIndex === index) {
                                setExpandedObjectiveIndex(null);  // Close if already open
                            } else {
                                setExpandedObjectiveIndex(index); // Open if closed
                            }
                        }}
                        style={{
                            padding: '16px',
                            backgroundColor: expandedObjectiveIndex === index ? colors.blue100 : colors.blue200,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: expandedObjectiveIndex === index ? `1px solid ${colors.blue200}` : 'none'
                        }}
                    >
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: colors.blue600,
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                flexShrink: 0
                            }}>
                                {index + 1}
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <strong style={{
                                        color: colors.grey900,
                                        fontSize: '1.1rem',
                                        maxWidth: '60%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {objective.name || `Objective ${index + 1}`}
                                    </strong>

                                    {/* THE BADGE HERE - right after the objective name */}
                                        {objective._isImported && (
                                            <span style={{
                                            fontSize: '10px',
                                            padding: '2px 8px',
                                            backgroundColor: '#ffc107',
                                            color: '#856404',
                                            borderRadius: '12px',
                                            marginLeft: '8px',
                                            whiteSpace: 'nowrap'
                                            }}>
                                            <IconUpload16 /> Imported - Needs Linking
                                            </span>
                                        )}

                                    {objective.id && (
                                        <span style={{
                                            fontSize: '0.8rem',
                                            backgroundColor: colors.green100,
                                            color: colors.green800,
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontWeight: 500
                                        }}>
                                            ✓ Saved
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: colors.blue100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: `2px solid ${colors.blue500}`
                                    }}>
                                        <span style={{
                                            fontWeight: 'bold',
                                            color: colors.blue900,
                                            fontSize: '1.1rem'
                                        }}>
                                            {objective.weight || 0}%
                                        </span>
                                    </div>
                                    <span style={{ fontSize: '0.85rem', color: colors.grey900 }}>Weight</span>
                                </div>
                            </div>

                            {/* Alignment status badge */}
                            {objective.objectiveAlignment && (
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: colors.purple100,
                                    color: colors.purple800,
                                    borderRadius: '12px',
                                    fontSize: '0.8rem',
                                    fontWeight: 500
                                }}>
                                    ⬆️ Aligned
                                </div>
                            )}
                            {objective.companyAlignedGoal && (
                                <div style={{
                                    padding: '4px 8px 8px',
                                    backgroundColor: colors.green100,
                                    color: colors.green800,
                                    borderRadius: '12px',
                                    fontSize: '0.8rem',
                                    fontWeight: 500
                                }}>
                                    <Goal size={16} /> Goal Linked
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: colors.grey200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {expandedObjectiveIndex === index ? <IconChevronDown24 /> : <IconChevronRight24 />}
                            </div>
                        </div>
                    </div>

                    {/* Card Content - ONLY RENDER WHEN EXPANDED */}
                    {expandedObjectiveIndex === index && (
                        <div style={{ padding: '20px', backgroundColor: colors.blue050 }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '16px',
                                marginBottom: '16px'
                            }}>
                                {/* NAME INPUT */}
                                <div>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <strong>Name *</strong>
                                        <input
                                            type="text"
                                            value={objective.name || ''}
                                            onChange={e => {
                                                const val = e?.target?.value;
                                                if (val !== undefined && val !== null) {
                                                    updateObjectiveField(index, 'name', val);
                                                }
                                            }}
                                            style={{
                                                padding: '10px',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.95rem',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </label>
                                </div>

                                {/* WEIGHT INPUT */}
                                <div>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <strong>Weight (%) *</strong>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={objective.weight || ''}
                                            onChange={e => {
                                                const rawValue = e?.target?.value;
                                                if (rawValue === undefined || rawValue === '' || rawValue === null) return;
                                                const val = parseInt(rawValue, 10);
                                                if (!isNaN(val) && val >= 1 && val <= 100) {
                                                    handleEditObjectiveWeightChange(index, val);
                                                }
                                            }}
                                            style={{
                                                padding: '10px',
                                                border: `1px solid ${colors.blue300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.95rem',
                                                backgroundColor: colors.blue050,
                                                fontWeight: 'bold',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <small style={{ fontSize: '0.8rem', color: colors.grey600 }}>
                                            Current: {objective.weight || 0}%, Total: {totalWeight}%
                                        </small>
                                    </label>
                                </div>
                            </div>

                            {/* ALIGNMENT SECTION - GOAL DROPDOWN FOR CEO / HIGHER-LEVEL FOR STAFF */}
                            {isTopLevelUser ? renderGoalDropdown(objective, index) : renderHigherLevelDropdown(objective, index)}

                            {/* Description */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <strong>Description</strong>
                                    <textarea
                                        value={objective.description || ''}
                                        onChange={e => {
                                            const val = e?.target?.value;
                                            if (val !== undefined && val !== null) {
                                                updateObjectiveField(index, 'description', val);
                                            }
                                        }}
                                        rows="3"
                                        style={{
                                            padding: '10px',
                                            border: `1px solid ${colors.grey300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.95rem',
                                            resize: 'vertical',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </label>
                            </div>

                            {/* ===== KRA & KPI EDITING SECTION ===== */}
                            <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                border: `1px solid ${colors.grey300}`,
                                borderRadius: '6px',
                                backgroundColor: colors.grey050
                            }}>
                                {/*
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px',
                                    paddingBottom: '12px',
                                    borderBottom: `2px solid ${colors.grey300}`
                                }}>
                               

                                </div>

                             {/* KRAs Section (OPTIONAL) */}
                             {/*
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px',
                                        padding: '8px',
                                        backgroundColor: colors.grey100,
                                        borderRadius: '4px'
                                    }}>
                                        <div>
                                            <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                Key Result Areas
                                            </label>
                                            <p style={{
                                                margin: '4px 0 0 0',
                                                fontSize: '0.8rem',
                                                color: colors.grey600,
                                                fontStyle: 'italic'
                                            }}>
                                                Optional - High-level areas of focus
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            style={{
                                                boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                color: 'white',
                                                backgroundColor: 'rgb(35, 59, 240)',
                                                borderRadius: '8px',
                                                border: 'none',
                                                width: '100px',
                                                height: '35px',
                                                cursor: 'pointer',
                                                margin: '10px'
                                            }}
                                            onClick={() => handleAddKra(index)}
                                        >
                                            <IconAdd16 /> Add KRA
                                        </button>
                                    </div>


                                    {console.log('🔍 [RENDER DEBUG] Objective', index, {
                                        id: objective?.id,
                                        krasCount: objective?.kras?.length,
                                        kpisCount: objective?.kpis?.length,
                                        firstKpi: objective?.kpis?.[0]?.name
                                    }) || null}

                                    {objective.kras?.map((kra, kraIndex) => (
                                        <div key={kraIndex} style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 100px 45px',
                                            gap: '12px',
                                            marginBottom: '12px',
                                            alignItems: 'center',
                                            padding: '12px',
                                            backgroundColor: 'white',
                                            border: `1px solid ${colors.grey300}`,
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <input
                                                    placeholder="e.g., Improve customer satisfaction"
                                                    value={kra?.name || ''}
                                                    onChange={(e) => handleKraChange(index, kraIndex, 'name', e.target.value)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: `1px solid ${colors.grey300}`,
                                                        borderRadius: '4px',
                                                        fontSize: '0.9rem',
                                                        width: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                {kra?.name?.trim() && (
                                                    <textarea
                                                        placeholder="Describe this KRA in detail..."
                                                        value={kra?.description || ''}
                                                        onChange={(e) => handleKraChange(index, kraIndex, 'description', e.target.value)}
                                                        rows="2"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: `1px solid ${colors.grey200}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.85rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            resize: 'vertical'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                    Weight (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={kra?.weight || ''}
                                                    onChange={(e) => handleKraChange(index, kraIndex, 'weight', Number(e.target.value) || 0)}
                                                    min="0"
                                                    max="100"
                                                    style={{
                                                        padding: '8px',
                                                        border: `1px solid ${colors.grey300}`,
                                                        borderRadius: '4px',
                                                        fontSize: '0.9rem',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                small
                                                destructive
                                                onClick={() => handleRemoveKra(index, kraIndex)}
                                                style={{ minWidth: '40px', height: '40px' }}
                                                disabled={objective.kras?.length <= 1}
                                            >
                                                <IconDelete16 />
                                            </Button>
                                        </div>
                                    ))}

                                    {objective.kras?.length === 0 && (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '20px',
                                            backgroundColor: colors.grey050,
                                            borderRadius: '4px',
                                            border: `1px dashed ${colors.grey300}`
                                        }}>
                                            <p style={{
                                                margin: '8px 0 0 0',
                                                fontSize: '0.8rem',
                                                color: colors.grey300,
                                                fontStyle: 'italic'
                                            }}>
                                                Click "Add KRA" above if you want to define key result areas
                                            </p>
                                        </div>
                                    )}
                                </div>

                                */}

                                {/* KPIs Section (MANDATORY) */}
                                <div style={{ borderTop: `2px solid ${colors.grey300}`, paddingTop: '20px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px',
                                        padding: '8px',
                                        backgroundColor: colors.orange050,
                                        borderRadius: '4px',
                                        border: `1px solid ${colors.orange300}`
                                    }}>
                                        <div>
                                            <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                Key Performance Indicators <span style={{ color: colors.red500 }}>*</span>
                                            </label>
                                            <p style={{
                                                margin: '4px 0 0 0',
                                                fontSize: '0.8rem',
                                                color: colors.grey600,
                                                fontStyle: 'italic'
                                            }}>
                                                Required - Specific, measurable metrics
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            style={{
                                                boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                color: 'white',
                                                backgroundColor: 'rgb(35, 59, 240)',
                                                borderRadius: '8px',
                                                border: 'none',
                                                width: '100px',
                                                height: '35px',
                                                cursor: 'pointer',
                                                margin: '10px'
                                            }}
                                            onClick={() => handleAddKpi(index)}
                                        >
                                            <IconAdd16 /> Add KPI
                                        </button>
                                    </div>

                                    {objective.kpis?.map((kpi, kpiIndex) => (
                                        <div key={kpiIndex} style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 100px 45px',
                                            gap: '12px',
                                            marginBottom: '12px',
                                            alignItems: 'center',
                                            padding: '12px',
                                            backgroundColor: 'white',
                                            border: kpi?.name?.trim() ? `1px solid ${colors.green300}` : `1px solid ${colors.grey300}`,
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <input
                                                    placeholder="e.g., Revenue growth, Response time"
                                                    value={kpi?.name || ''}
                                                    onChange={(e) => handleKpiChange(index, kpiIndex, 'name', e.target.value)}
                                                    required
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: `1px solid ${kpi?.name?.trim() ? colors.green300 : colors.grey300}`,
                                                        borderRadius: '4px',
                                                        fontSize: '0.9rem',
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                        fontWeight: kpi?.name?.trim() ? 500 : 400,
                                                        backgroundColor: kpi?.name?.trim() ? colors.green050 : 'white'
                                                    }}
                                                />
                                                {kpi?.name?.trim() && (
                                                    <textarea
                                                        placeholder="Describe how this KPI will be measured..."
                                                        value={kpi?.description || ''}
                                                        onChange={(e) => handleKpiChange(index, kpiIndex, 'description', e.target.value)}
                                                        rows="2"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: `1px solid ${colors.grey200}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.85rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            resize: 'vertical'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                    Target Value
                                                </label>
                                                <input
                                                    placeholder="e.g., 90%, 15%, 24 hours"
                                                    value={kpi?.target || ''}
                                                    onChange={(e) => handleKpiChange(index, kpiIndex, 'target', e.target.value)}
                                                    required
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: `1px solid ${kpi?.target?.trim() ? colors.green300 : colors.grey300}`,
                                                        borderRadius: '4px',
                                                        fontSize: '0.9rem',
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: kpi?.target?.trim() ? colors.green050 : 'white'
                                                    }}
                                                />
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: kpi?.name?.trim() && kpi?.target?.trim() ? colors.green100 : colors.grey100,
                                                borderRadius: '50%',
                                                width: '40px',
                                                height: '40px'
                                            }}>
                                                {kpi?.name?.trim() && kpi?.target?.trim() ? (
                                                    <span style={{ color: colors.green900, fontSize: '1.2rem' }}>✓</span>
                                                ) : (
                                                    <span style={{ color: colors.grey500, fontSize: '0.9rem' }}>%</span>
                                                )}
                                            </div>
                                            <Button
                                                type="button"
                                                small
                                                destructive
                                                onClick={() => handleRemoveKpi(index, kpiIndex)}
                                                style={{ minWidth: '40px', height: '40px' }}
                                                disabled={objective.kpis?.length <= 1}
                                            >
                                                <IconDelete16 />
                                            </Button>
                                        </div>
                                    ))}

                                    {/* KPI Validation Warning */}
                                    {!objective.kpis?.some(k => k?.name?.trim()) && (
                                        <div style={{
                                            marginTop: '12px',
                                            padding: '12px',
                                            backgroundColor: colors.orange050,
                                            border: `1px solid ${colors.orange500}`,
                                            borderRadius: '4px',
                                            fontSize: '0.9rem',
                                            color: colors.orange800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}>
                                            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                            <div>
                                                <strong>At least one KPI is required</strong>
                                            </div>
                                        </div>
                                    )}

                                    {/* KPI Summary */}
                                    {objective.kpis?.filter(k => k?.name?.trim()).length > 0 && (
                                        <div style={{
                                            marginTop: '12px',
                                            padding: '10px',
                                            backgroundColor: colors.green050,
                                            border: `1px solid ${colors.green300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.85rem'
                                        }}>
                                            <strong>{objective.kpis?.filter(k => k?.name?.trim()).length} KPI(s) defined:</strong>
                                            <ul style={{ margin: '6px 0 0 20px', paddingLeft: 0 }}>
                                                {objective.kpis
                                                    .filter(k => k?.name?.trim())
                                                    .map((kpi, idx) => (
                                                        <li key={idx} style={{ margin: '2px 0' }}>
                                                            <strong>{kpi?.name}</strong> - Target: {kpi?.target || 'Not set'}
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>



                            {/* Dates */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px',
                                marginBottom: '16px'
                            }}>
                                <div>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <strong>Objective Start Date</strong>
                                        <input
                                            type="date"
                                            value={objective.startDate || ''}
                                            onChange={e => {
                                                const val = e?.target?.value;
                                                if (val !== undefined && val !== null) {
                                                    updateObjectiveField(index, 'startDate', val);
                                                }
                                            }}
                                            style={{
                                                padding: '10px',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.95rem',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </label>
                                </div>
                                <div>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <strong>Objective Completion Date</strong>
                                        <input
                                            type="date"
                                            value={objective.completionDate || ''}
                                            onChange={e => {
                                                const val = e?.target?.value;
                                                if (val !== undefined && val !== null) {
                                                    updateObjectiveField(index, 'completionDate', val);
                                                }
                                            }}
                                            style={{
                                                padding: '10px',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.95rem',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Remove button */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                paddingTop: '16px',
                                borderTop: `1px solid ${colors.grey200}`
                            }}>
                            </div>

                            {/* Remarks Field */}
                            {objective.remarks && (
                                <div style={{
                                    marginTop: '20px',
                                    paddingTop: '16px',
                                    borderTop: `2px solid ${colors.orange300}`,
                                    backgroundColor: colors.orange050,
                                    borderRadius: '6px'
                                }}>
                                    <label style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            color: colors.orange800,
                                            fontWeight: 'bold',
                                            fontSize: '1.05rem'
                                        }}>
                                            <span role="img" aria-label="comment">💬</span>
                                            <span>Manager's Remarks</span>
                                            {objective.approvalStatus === 'returned' && (
                                                <span style={{
                                                    fontSize: '0.8rem',
                                                    backgroundColor: colors.orange200,
                                                    color: colors.orange900,
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontWeight: 500
                                                }}>
                                                    ⚠️ Returned for Revision
                                                </span>
                                            )}
                                            {/* Draft Badge */}
                                            {String(objective.status || '').toLowerCase() === 'draft' && (
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    backgroundColor: '#6c757d',
                                                    color: 'white',
                                                    padding: '3px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: 600,
                                                    marginLeft: '8px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    📝 Draft
                                                </span>
                                            )}

                                        </div>
                                        <textarea
                                            value={objective.remarks}
                                            readOnly
                                            rows="4"
                                            style={{
                                                padding: '12px',
                                                border: `2px solid ${colors.orange400}`,
                                                borderRadius: '4px',
                                                backgroundColor: colors.orange100,
                                                fontSize: '0.95rem',
                                                fontStyle: 'italic',
                                                resize: 'vertical',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                color: colors.grey900,
                                                fontWeight: 500
                                            }}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {/* Add New Button */}
            {remainingWeight > 0 && (
                <Button
                    primary
                    onClick={handleAddObjectiveInEditMode}
                    style={{ marginTop: '16px', width: '100%' }}
                    disabled={isSubmitting}
                >
                    <IconAddCircle16 /> Add New Objective ({remainingWeight}% remaining)
                </Button>
            )}

            {/* Save Buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: `2px solid ${colors.grey200}`
            }}>
                <button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    style={{
                        boxShadow: "0 2px 4px rgba(241, 18, 18, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(208, 9, 9)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '90px',
                        height: '35px',
                        cursor: 'pointer'
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    style={{
                        boxShadow: "0 2px 4px rgba(225, 205, 55, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(228, 210, 14)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '90px',
                        height: '35px',
                        cursor: 'pointer',
                    }}
                >
                    Close
                </button>
                {/* NEW: Save as Draft Button */}
                <button
                    type="button"
                    onClick={() => handleSubmit(null, 'draft')} // Pass 'draft' mode
                    disabled={isSubmitting}
                    style={{
                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: isSubmitting ? colors.grey400 : '#6c757d',
                        borderRadius: '8px',
                        border: 'none',
                        width: '150px',
                        height: '35px',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}
                >
                    {isSubmitting ? (
                        <>
                            <CircularLoader small />
                            <span>Wait saving...</span>
                        </>
                    ) : (
                        'Save as Draft'
                    )}
                </button>
                {/* Existing: Save Final Button */}
                <button
                    type="button"
                    style={{
                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(35, 59, 240)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '150px',
                        height: '35px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}
                    onClick={() => handleSubmit(null, 'final')}
                    disabled={isSubmitting || (totalWeight !== 100)}
                >
                    {isSubmitting ? (
                        <>
                            <CircularLoader small />
                            Wait saving...
                        </>
                    ) : totalWeight !== 100 ? (
                        `Save (${100 - totalWeight}% remaining)`
                    ) : (
                        'Save as Final'
                    )}
                </button>

            </div>
        </div>
    );
});

const ObjectiveForm = ({
    mode,
    objective,
    selectedHigherLevelObjective: propSelectedHigherLevelObjective,
    setSelectedHigherLevelObjective: propSetSelectedHigherLevelObjective,
    onSave,
    onCancel,
    onComplete,
    onCancelisCEO = false,
    isHOD = false,
    isTopStaff = false,
    isSeniorStaff = false,
    title = "Objectives",
    protectedObjectiveIds = new Set(),
    childCounts = {}
}) => {

    const { user } = useAuth();
    const [statusOptions, setStatusOptions] = useState([]);
    const engine = useDataEngine();
    const [notification, setNotification] = useState(null);
    const [goals, setGoals] = useState([]);
    const [selectedGoal, setSelectedGoal] = useState('');
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [currentUserPosition, setCurrentUserPosition] = useState(null);
    const [isTopLevelUser, setIsTopLevelUser] = useState(false);
    const [reportingStructureLoaded, setReportingStructureLoaded] = useState(false);

    const [higherLevelObjectives, setHigherLevelObjectives] = useState([]);
    const [loadingHigherLevelObjectives, setLoadingHigherLevelObjectives] = useState(false);

    const [goalItems, setGoalItems] = useState([]);
    const [weightGroupEnrollment, setWeightGroupEnrollment] = useState(null);
    const [saveMode, setSaveMode] = useState('draft');
    const submissionInProgress = useRef(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const [filteredManagerObjectives, setFilteredManagerObjectives] = useState([]);
    const [loadingFilteredObjectives, setLoadingFilteredObjectives] = useState(false);
    const [mounted, setMounted] = useState(true);
    const [expandedObjectiveIndex, setExpandedObjectiveIndex] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState([]);
    const [importing, setImporting] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);

    const [isFormLocked, setIsFormLocked] = useState(false);
    const [lockReason, setLockReason] = useState('');

    const [showDraftsOnly, setShowDraftsOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [programAccess, setProgramAccess] = useState({
        isAssigned: false,
        checked: false,
        missingOrgUnit: null,
        assignedOrgUnits: []
    });

    const emailNotificationSent = useRef(false);

    /**
     * Send email notification via backend API
     */
    const sendObjectiveNotification = async (savedObjective, parentObjectiveId, actionType = 'new') => {
        // Prevent duplicate emails
        const notificationKey = `email_sent_${savedObjective.id}_${parentObjectiveId}_${actionType}`;
        const alreadySent = sessionStorage.getItem(notificationKey);
        
        if (alreadySent) {
            console.log('🔄 [EMAIL] Notification already sent, skipping');
            return { success: true, message: 'Notification already sent', duplicate: true };
        }

        if (!parentObjectiveId) {
            console.log('📧 [EMAIL] No parent objective - skipping email');
            return { success: true, message: 'No parent objective, email not needed' };
        }

        try {
            console.log('📧 [EMAIL] Sending via backend...', {
                objectiveId: savedObjective.id,
                parentId: parentObjectiveId,
                actionType: actionType
            });

            // Get auth token
            const token = localStorage.getItem('authToken') || 
                        localStorage.getItem('token') ||
                        sessionStorage.getItem('authToken') || 
                        sessionStorage.getItem('token');

            const API_BASE_URL = process.env.REACT_APP_API_URL || 
                (process.env.NODE_ENV === 'production' 
                ? '/backend-api'  // ✅ Relative path (uses same protocol as page)
                : 'http://localhost:3001');
            
            const response = await fetch(`${API_BASE_URL}/api/email/objective-notification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    savedObjective: savedObjective,
                    parentObjectiveId: parentObjectiveId,
                    actionType: actionType,
                    currentUser: {
                        id: user.id,
                        displayName: user.displayName || user.name || user.username,
                        username: user.username,
                        email: user.email
                    }
                })
            });

            // Handle 401 specifically - don't show error to user
            if (response.status === 401) {
                console.warn('⚠️ [EMAIL] Authentication failed (401) - skipping email');
                return { 
                    success: false, 
                    error: 'Authentication failed',
                    warning: 'Email notification skipped due to authentication issue'
                };
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.warn('⚠️ [EMAIL] Backend error:', errorData);
                return {
                    success: false,
                    error: errorData.error || `HTTP error: ${response.status}`,
                    warning: 'Email notification failed but objective was saved'
                };
            }

            const result = await response.json();

            if (result.success) {
                sessionStorage.setItem(notificationKey, 'true');
                setTimeout(() => sessionStorage.removeItem(notificationKey), 3600000);
                console.log('✅ [EMAIL] Notification sent successfully');
                return result;
            } else {
                console.warn('⚠️ [EMAIL] Backend returned error:', result.error);
                return {
                    success: false,
                    error: result.error || 'Email sending failed',
                    warning: 'Objective saved but email notification may not have been sent'
                };
            }
        } catch (error) {
            console.error('❌ [EMAIL] Error:', error);
            // Don't throw - just return failure
            return {
                success: false,
                error: error.message,
                warning: 'Email notification failed but objective was saved'
            };
        }
    };

    const isResubmittedObjective = (objective) => {
        const status = String(objective?.approvalStatus || objective?.status || '').toLowerCase();
        return status === 'returned' || status === 'resubmitted';
    };

    // Add to your state declarations
    const [editingWeightFor, setEditingWeightFor] = useState(null);
    const [weightEditValue, setWeightEditValue] = useState(0);
    const getObjectiveWeight = useCallback((objective) => {
        const parsed = Number.parseFloat(objective?.weight);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);
    const sumObjectiveWeights = useCallback((objectiveList = []) => (
        objectiveList.reduce((sum, o) => sum + getObjectiveWeight(o), 0)
    ), [getObjectiveWeight]);

     // Add this helper function near the top of ObjectiveForm.jsx
    const preservePrivateNotes = async (objectiveId, engine) => {
        if (!objectiveId) {
            return { managerPrivateNote: '', noteCreatedBy: '', noteCreatedAt: '' };
        }
        
        try {
            const response = await engine.query({
                event: {
                    resource: `events/${objectiveId}`,
                    params: {
                        fields: 'dataValues[dataElement,value]'
                    }
                }
            });

            const dataValues = response.event?.dataValues || [];
            
            // Extract private note fields
            const noteDV = dataValues.find(dv => dv.dataElement === 'rJZyovd5akX');
            const noteCreatedByDV = dataValues.find(dv => dv.dataElement === 'x56k8a6rLss');
            const noteCreatedAtDV = dataValues.find(dv => dv.dataElement === 'TlhIr80b8Vp');
            
            return {
                managerPrivateNote: noteDV?.value || '',
                noteCreatedBy: noteCreatedByDV?.value || '',
                noteCreatedAt: noteCreatedAtDV?.value || ''
            };
        } catch (error) {
            console.warn('⚠️ Could not fetch private notes:', error);
            return { managerPrivateNote: '', noteCreatedBy: '', noteCreatedAt: '' };
        }
    };

    //==================================FUNCTION TO HANDLE BULK IMPORT====================

  // Handle file import - UPDATED to use the utility's fiscal year filtering
      const handleImportFile = async (file) => {
            if (!file) return;
            
            setImporting(true);
            try {
                // The utility now handles fiscal year filtering internally
                const importedObjectives = await parseImportedObjectives(
                    file, 
                    fiscalYear,  // Pass current fiscal year for filtering
                    true  // isPastYearMigration = true - strips all linkages
                );
                
                // Validate weights before showing preview
                const importedTotalWeight = importedObjectives.reduce((sum, o) => sum + (o.weight || 0), 0);
                const currentTotalWeight = objectives.reduce((sum, o) => sum + (o.weight || 0), 0);
                
                if (currentTotalWeight + importedTotalWeight > 100) {
                    showAlert(
                        'Weight Limit Exceeded',
                        'error',
                        `Cannot import: Current total ${currentTotalWeight}% + Imported ${importedTotalWeight}% = ${currentTotalWeight + importedTotalWeight}% (exceeds 100%)`
                    );
                    setImporting(false);
                    return;
                }
                
                setImportPreview(importedObjectives);
                setShowImportModal(true);
            } catch (error) {
                console.error('Import failed:', error);
                showAlert('Import Failed', 'error', error.message);
            } finally {
                setImporting(false);
            }
     };

    const createWeightGroupForImport = async () => {
        if (weightGroupEnrollment) return weightGroupEnrollment;
        
        try {
            // Create a new enrollment for the weight group
            const enrollmentData = {
            trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
            program: 'yiT00XKrrHP',
            orgUnit: user?.organisationUnits?.[0]?.id,
            enrollmentDate: new Date().toISOString().slice(0, 10),
            incidentDate: new Date().toISOString().slice(0, 10),
            status: 'ACTIVE'
            };
            
            const createResponse = await engine.mutate({
            resource: 'enrollments',
            type: 'create',
            data: enrollmentData
            });
            
            let newEnrollmentId = createResponse.response?.importSummaries?.[0]?.reference ||
                                createResponse.response?.enrollment ||
                                createResponse.enrollment;
            
            if (newEnrollmentId) {
            setWeightGroupEnrollment(newEnrollmentId);
            return newEnrollmentId;
            }
        } catch (error) {
            console.error('Failed to create weight group for import:', error);
        }
        return null;
  };


  // Handle confirm import - MODIFIED TO RESPECT WEIGHT GROUPS
  const handleConfirmImport = async () => {

    const userPositionTei = currentUserPosition?.trackedEntityInstance;
    
    const ownObjectives = objectives.filter(obj => {
        // Only include objectives that belong to the current user
        // For new objectives (no id yet), they are considered the user's
        return !obj.id || obj.positionTeiId === userPositionTei;
    });

    // Calculate weights
    const currentTotalWeight = objectives.reduce((sum, o) => sum + (o.weight || 0), 0);
    const importedTotalWeight = importPreview.reduce((sum, o) => sum + (o.weight || 0), 0);
    const newTotalWeight = currentTotalWeight + importedTotalWeight;
    
    // Check if adding these objectives would exceed 100%
    if (newTotalWeight > 100) {
        showAlert(
            'Weight Limit Exceeded',
            'error',
            `Importing these objectives would exceed 100% total weight. Current: ${currentTotalWeight}%, Imported: ${importedTotalWeight}%, Total would be: ${newTotalWeight}%. Please reduce weights or remove some objectives before importing.`
        );
        return;
    }
    
    //  Filter out any existing empty/untitled objectives before adding imports
    // This preserves existing behavior but cleans up the "Untitled" objective
    const validExistingObjectives = objectives.filter(obj => {
        const hasName = obj.name && obj.name.trim() !== '';
        const hasWeight = (obj.weight || 0) > 0;
        const hasId = !!obj.id;
        const hasKpi = obj.kpis?.some(k => k.name?.trim());
        const hasKra = obj.kras?.some(k => k.name?.trim());
        
        //  Also check if it's NOT an empty untitled objective
        const isNotUntitledEmpty = !(obj.name === '' && obj.weight === 0 && !obj.id && !hasKpi && !hasKra);
        
        return (hasName || hasWeight || hasId || hasKpi || hasKra) && isNotUntitledEmpty;
    });
    
    // Ensure we have a weight group enrollment
    let enrollmentId = weightGroupEnrollment;
    
    // If no weight group exists, create one
    if (!enrollmentId) {
        try {
            console.log('🔄 Creating new weight group for import...');
            const enrollmentData = {
                trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
                program: 'yiT00XKrrHP',
                orgUnit: user?.organisationUnits?.[0]?.id,
                enrollmentDate: new Date().toISOString().slice(0, 10),
                incidentDate: new Date().toISOString().slice(0, 10),
                status: 'ACTIVE'
            };
            
            const createResponse = await engine.mutate({
                resource: 'enrollments',
                type: 'create',
                data: enrollmentData
            });
            
            enrollmentId = createResponse.response?.importSummaries?.[0]?.reference ||
                            createResponse.response?.enrollment ||
                            createResponse.enrollment;
            
            if (enrollmentId) {
                setWeightGroupEnrollment(enrollmentId);
                console.log('✅ Created new weight group:', enrollmentId);
            } else {
                throw new Error('Failed to create enrollment');
            }
        } catch (error) {
            console.error('Failed to create weight group:', error);
            showAlert('Import Failed', 'error', 'Could not create weight group. Please save a draft objective first, then try importing again.');
            return;
        }
    }

        
    // Create mapped imports
    const mappedImports = importPreview.map(obj => ({
        ...obj,
        id: null,
        _tempId: `import_${Date.now()}_${Math.random()}`,
        enrollment: enrollmentId,
        orgUnit: user?.organisationUnits?.[0]?.id,
        positionTeiId: currentUserPosition?.trackedEntityInstance,
        // For staff users, preserve the manager objective ID for re-linking
        objectiveAlignment: isTopLevelUser ? '' : (obj.objectiveAlignment || ''),
        higherLevelObjectiveId: isTopLevelUser ? '' : (obj.higherLevelObjectiveId || ''),
        higherLevelObjectiveName: isTopLevelUser ? '' : (obj.higherLevelObjectiveName || ''),
        companyAlignedGoal: '',  // Clear company goal (will be re-linked)
        _tempGoalId: '',
        status: 'draft',
        kras: obj.kras || [],
        kpis: obj.kpis?.length ? obj.kpis : [{ name: '', description: '', target: '' }],
        _isImported: true,
        _importedFrom: obj.companyAlignedGoal ? 'past_fy' : 'unknown'
    }));
    
    // 🔥 NEW: If we have valid existing objectives, append; otherwise replace
    let newObjectives;
    if (validExistingObjectives.length === 0 && mappedImports.length > 0) {
        // No valid existing objectives - replace with imports (gets rid of Untitled)
        console.log('🔄 Replacing empty state with imported objectives');
        newObjectives = mappedImports;
    } else {
        // Keep existing valid objectives and add imports
        newObjectives = [...validExistingObjectives, ...mappedImports];
    }
    
    setObjectives(newObjectives);
    setShowImportModal(false);
    setImportPreview([]);
    setImportFile(null);
    
    const remainingWeight = 100 - (currentTotalWeight + importedTotalWeight);
    const weightMessage = remainingWeight > 0 
        ? `${remainingWeight}% remaining to reach 100%.` 
        : remainingWeight === 0 
        ? 'Weight target reached!' 
        : 'Weight exceeded!';
    
    showAlert(
        'Import Successful', 
        'success', 
        `${mappedImports.length} objective(s) added to your weight group.\n` +
        `Total weight is now ${currentTotalWeight + importedTotalWeight}%. ${weightMessage}\n\n` +
        `⚠️ IMPORTANT: All goal linkages have been removed. Please link each objective to current goals/manager objectives before finalizing.`
    );
};

    
    // Modified export function - EXCLUDES goal and manager objective IDs
   const handleExportPastYearObjectives = async () => {
        if (!fiscalYear.start || !fiscalYear.end) {
            showAlert('Fiscal Year Not Set', 'error', 'Please wait for fiscal year to load.');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await engine.query({
                objectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        ouMode: 'ACCESSIBLE',
                        fields: 'event,dataValues[dataElement,value],trackedEntityInstance',
                        pageSize: 500,
                        order: 'eventDate:desc'
                    }
                }
            });
            
            const events = response.objectives?.events || [];

            const currentPositionTei = currentUserPosition?.trackedEntityInstance;
            
            // Filter and transform past year objectives
            const pastYearObjectivesData = [];
            
            for (const event of events) {
            const dvMap = {};
            event.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });
            
            const startDate = dvMap['eDcVy661j9e'] || '';
                const completionDate = dvMap['J0rjxrDKn1l'] || '';

                const objectiveOwner = dvMap['SlgO60cMK3F']; // Position TEI of owner
                const isOwnedByCurrentUser = objectiveOwner === currentPositionTei;


                // Skip if not owned by current user
                if (!isOwnedByCurrentUser) {
                    console.log('📋 Skipping objective not owned by current user:', dvMap['EuN5ztFnXsQ']);
                    continue;
                }
                
                // Check if objective is from a past fiscal year
                const isCurrentFy = isWithinFiscalYear(startDate, fiscalYear) || isWithinFiscalYear(completionDate, fiscalYear);
                
                if (!isCurrentFy && (startDate || completionDate)) {
                    // Fetch KRAs and KPIs for this objective
                    let kras = [];
                    let kpis = [];
                    
                    try {
                    const details = await fetchObjectiveWithDetails(event.event, engine);
                    kras = details.kras || [];
                    kpis = details.kpis || [];
                    } catch (error) {
                    console.warn(`Could not fetch details for objective ${event.event}:`, error);
                    }
                
                pastYearObjectivesData.push({
                    name: dvMap['EuN5ztFnXsQ'] || '',
                    description: dvMap['nWqW5BrcgP1'] || '',
                    weight: parseInt(dvMap['yfb9RCRpMB2']) || 0,
                    startDate: startDate,
                    completionDate: completionDate,
                    kras: kras,
                    kpis: kpis
                    });
                }
            }
            
            if (pastYearObjectivesData.length === 0) {
            showAlert('No Past Objectives', 'info', 'No objectives found from previous fiscal years to export.');
            return;
            }
            
            // Use the new export function
            exportPastYearObjectivesToExcel(pastYearObjectivesData, fiscalYear);
            
            showAlert('Export Successful', 'success', 
            `Exported ${pastYearObjectivesData.length} objective(s) from past fiscal years.\n\n` +
            `⚠️ Note: Goal linkages have been removed. You will need to re-link objectives after import.`
            );
        } catch (error) {
            console.error('Export failed:', error);
            showAlert('Export Failed', 'error', error.message);
        } finally {
            setIsLoading(false);
        }
     };
     
    //==============================END============================================


    // this is weight editor modal component
    const WeightEditorModal = memo(() => {
    if (editingWeightFor == null) return null;

    const objective = objectives[editingWeightFor];
    if (!objective) return null;

    const currentTotal = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
    const otherObjectivesTotal = currentTotal - getObjectiveWeight(objective);
    const maxAllowed = 100 - otherObjectivesTotal;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '8px',
                minWidth: '400px',
                maxWidth: '600px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}>
                <h3 style={{ marginTop: 0, color: colors.blue700 }}>
                    Edit Weight for: {objective.name}
                </h3>

                <div style={{ marginBottom: '20px' }}>
                    <p><strong>Current Weight:</strong> {objective.weight || 0}%</p>
                    <p><strong>Other Objectives Total:</strong> {otherObjectivesTotal}%</p>
                    <p><strong>Maximum Allowed:</strong> {maxAllowed}%</p>
                    <p><strong>Total Must Equal:</strong> 100%</p>
                    
                    {/* ✅ Show current total with status */}
                    <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: currentTotal === 100 ? colors.green050 : 
                                      currentTotal > 100 ? colors.red050 : colors.orange050,
                        border: `1px solid ${currentTotal === 100 ? colors.green300 : 
                                currentTotal > 100 ? colors.red300 : colors.orange300}`,
                        textAlign: 'center'
                    }}>
                        <strong>Current Total: {currentTotal}%</strong>
                        {currentTotal === 100 ? ' ✅ Balanced' : 
                         currentTotal > 100 ? ` ⚠️ Over by ${currentTotal - 100}%` : 
                         ` ⚠️ ${100 - currentTotal}% remaining`}
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                        New Weight (%):
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                            type="range"
                            min="1"
                            max={Math.max(100, maxAllowed)}
                            value={weightEditValue}
                            onChange={(e) => setWeightEditValue(parseInt(e.target.value))}
                            style={{ flex: 1 }}
                        />
                        <input
                            type="number"
                            min="1"
                            max={Math.max(100, maxAllowed)}
                            value={weightEditValue}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val) && val >= 1) {
                                    setWeightEditValue(val);
                                }
                            }}
                            style={{
                                width: '80px',
                                padding: '8px',
                                border: `2px solid ${colors.blue300}`,
                                borderRadius: '4px',
                                textAlign: 'center',
                                fontSize: '1.1rem'
                            }}
                        />
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>%</span>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                        fontSize: '0.85rem',
                        color: colors.grey600
                    }}>
                        <span>1%</span>
                        <span>Max: {Math.max(100, maxAllowed)}%</span>
                    </div>
                </div>

                {/* Visual representation */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{
                        height: '20px',
                        backgroundColor: colors.grey200,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                    }}>
                        {/* Other objectives */}
                        <div style={{
                            width: `${Math.min(otherObjectivesTotal, 100)}%`,
                            height: '100%',
                            backgroundColor: colors.grey400,
                            float: 'left'
                        }}></div>
                        {/* This objective (current) */}
                        <div style={{
                            width: `${Math.min(objective.weight || 0, 100)}%`,
                            height: '100%',
                            backgroundColor: colors.orange400,
                            float: 'left'
                        }}></div>
                        {/* This objective (new) - show as overlay */}
                        {weightEditValue !== (objective.weight || 0) && (
                            <div style={{
                                width: `${Math.min(weightEditValue, 100)}%`,
                                height: '100%',
                                backgroundColor: 'rgba(76, 175, 80, 0.6)',
                                float: 'left',
                                marginLeft: `-${Math.min(objective.weight || 0, 100)}%`,
                                border: '2px solid #4caf50'
                            }}></div>
                        )}
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.85rem'
                    }}>
                        <div>
                            <span style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                backgroundColor: colors.grey400,
                                marginRight: '4px'
                            }}></span>
                            Other: {otherObjectivesTotal}%
                        </div>
                        <div>
                            <span style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                backgroundColor: colors.orange400,
                                marginRight: '4px'
                            }}></span>
                            Current: {objective.weight || 0}%
                        </div>
                        {weightEditValue !== (objective.weight || 0) && (
                            <div>
                                <span style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#4caf50',
                                    marginRight: '4px'
                                }}></span>
                                New: {weightEditValue}%
                            </div>
                        )}
                    </div>
                </div>

                {/* ✅ Show what the new total would be */}
                <div style={{
                    marginBottom: '20px',
                    padding: '10px',
                    backgroundColor: colors.blue050,
                    borderRadius: '4px',
                    border: `1px solid ${colors.blue300}`
                }}>
                    <strong>New Total Would Be:</strong>
                    <span style={{
                        marginLeft: '8px',
                        fontWeight: 'bold',
                        color: (otherObjectivesTotal + weightEditValue) === 100 ? colors.green700 :
                               (otherObjectivesTotal + weightEditValue) > 100 ? colors.red700 : colors.orange700
                    }}>
                        {otherObjectivesTotal + weightEditValue}%
                    </span>
                    {(otherObjectivesTotal + weightEditValue) === 100 ? ' ✅' :
                     (otherObjectivesTotal + weightEditValue) > 100 ? ` ⚠️ Over by ${(otherObjectivesTotal + weightEditValue) - 100}%` :
                     ` ⚠️ ${100 - (otherObjectivesTotal + weightEditValue)}% remaining`}
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px'
                }}>
                    <Button
                        destructive
                        onClick={() => {
                            setEditingWeightFor(null);
                            setWeightEditValue(0);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        primary
                        onClick={() => {
                            // ✅ ONLY update the single objective - NO AUTO-ADJUSTMENT
                            handleEditObjectiveWeightChange(editingWeightFor, weightEditValue);
                            
                            // Close modal
                            setEditingWeightFor(null);
                            setWeightEditValue(0);
                        }}
                    >
                        Update Weight
                    </Button>
                </div>
            </div>
        </div>
    );
})

    // ============HELPERS function to extract attributes from position===============
    const extractAttributes = (attributes = []) => {
        const map = {};
        attributes.forEach((attr) => {
            map[attr.attribute] = attr.value;
        });
        return map;
    };

    const normalizeKraEntries = (kras = [], parentObjectiveId = null) => {
        const entries = Array.isArray(kras) && kras.length > 0 ? kras : [];
        return entries.map(kra => {
            let name = kra.name || '';
            let description = kra.description || '';
            let weight = 0;

            // Extract from dataValues if direct properties missing
            if (kra.dataValues && Array.isArray(kra.dataValues)) {
                kra.dataValues.forEach(dv => {
                    if (dv.dataElement === 'obapz6Gnyla') name = dv.value || '';
                    if (dv.dataElement === 'DEECWPTj42E') description = dv.value || '';
                    if (dv.dataElement === 'Xl4VEFoIFIw') weight = parseInt(dv.value) || 0;
                });
            }

            return {
                id: kra.id || kra.eventId || kra.event || null,
                name: String(name || '').trim(),
                description: String(description || '').trim(),
                weight: Number.isFinite(Number(weight)) ? Number(weight) : 0,
                dataValues: Array.isArray(kra.dataValues) ? kra.dataValues : [],
                parentObjectiveId,
            };
        });
    };

    const normalizeKpiEntries = (kpis = [], parentObjectiveId = null) => {
        const entries = Array.isArray(kpis) && kpis.length > 0 ? kpis : [];
        return entries.map(kpi => {
            let name = kpi.name || '';
            let description = kpi.description || '';
            let target = kpi.target || '';

            // Extract from dataValues if direct properties missing
            if (kpi.dataValues && Array.isArray(kpi.dataValues)) {
                kpi.dataValues.forEach(dv => {
                    if (dv.dataElement === 'DBmylDQA9KX') name = dv.value || '';
                    if (dv.dataElement === 'rQqF75V9t7K') description = dv.value || '';
                    if (dv.dataElement === 'Ru68Ax2Xo2L') target = dv.value || '';
                });
            }

            return {
                id: kpi.id || kpi.eventId || kpi.event || null,
                name: String(name || '').trim(),
                description: String(description || '').trim(),
                target: String(target || '').trim(),
                dataValues: Array.isArray(kpi.dataValues) ? kpi.dataValues : [],
                parentObjectiveId,
            };
        });
    };


    const mapDataValuesToObjective = (event) => {
        const dataValuesMap = {};
        event.dataValues?.forEach(dv => {
            dataValuesMap[dv.dataElement] = dv.value;
        });
        return {
            id: event.event,
            name: dataValuesMap['EuN5ztFnXsQ'] || '',
            description: dataValuesMap['nWqW5BrcgP1'] || '',
            // Weight parsing with fallback
            weight: (() => {
                const rawWeight = dataValuesMap['yfb9RCRpMB2'];
                if (rawWeight === null || rawWeight === undefined || rawWeight === '') return 0;
                const parsed = parseInt(String(rawWeight).trim(), 10);
                return isNaN(parsed) ? 0 : parsed;
            })(),
            startDate: dataValuesMap['eDcVy661j9e'] || '',
            completionDate: dataValuesMap['J0rjxrDKn1l'] || '',
            status: dataValuesMap['DL7KxNhiV0x'] || '',
            objectiveAlignment: dataValuesMap['neuilg3L4d2'] || '',
            companyAlignedGoal: dataValuesMap['VhrIQQIVpB9'] || '',
            objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
            performanceYear: dataValuesMap['CVaNcskWvmN'] || '',
            remarks: dataValuesMap['KeGWxm1IDYF'] || '',
            approvalStatus: dataValuesMap['DzQorireRBf'] || '',
            positionTeiId: dataValuesMap['SlgO60cMK3F'] || '',
            managerPrivateNote: dataValuesMap['rJZyovd5akX'] || '',
            noteCreatedBy: dataValuesMap['x56k8a6rLss'] || '',
            noteCreatedAt: dataValuesMap['TlhIr80b8Vp'] || '',
            orgUnit: event.orgUnit,
            trackedEntityInstance: event.trackedEntityInstance,
            enrollment: event.enrollment,
            enrollmentId: event.enrollment,
            eventDate: event.eventDate,
            kras: [],
            kpis: []
        };
    };

    const enrichObjectivesWithDetails = useCallback(async (objectiveList = []) => {
        if (!objectiveList?.length) return objectiveList;

        const loaded = await Promise.all(objectiveList.map(async (obj) => {
            if (!obj?.id) return obj;
            try {
                const { kras, kpis } = await fetchObjectiveWithDetails(obj.id, engine);
                const normalizedKras = normalizeKraEntries(kras, obj.id);
                const normalizedKpis = normalizeKpiEntries(kpis, obj.id);

                const enriched = {
                    ...obj,
                    kras: normalizedKras,
                    kpis: normalizedKpis
                };

                console.log('🌱 [ENRICH DETAIL]', {
                    objectiveId: obj.id,
                    kraCount: normalizedKras.length,
                    kpiCount: normalizedKpis.length,
                    kras: normalizedKras.map(k => ({ id: k.id, parentObjectiveId: k.parentObjectiveId, name: k.name })),
                    kpis: normalizedKpis.map(k => ({ id: k.id, parentObjectiveId: k.parentObjectiveId, name: k.name }))
                });

                return enriched;
            } catch (error) {
                console.warn('🚨 [DETAIL ENRICH] Failed to load KPIs/KRAs for objective:', obj.id, error);
                return obj; // Return original if enrichment fails
            }
        }));
        return loaded;
    }, [engine]);

    const adjustWeightsProportionally = (changedIdx, newWeight, objectivesArray) => {
        const total = objectivesArray.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const otherObjectives = objectivesArray.filter((_, idx) => idx !== changedIdx);
        const otherTotal = otherObjectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const difference = newWeight - (objectivesArray[changedIdx].weight || 0);

        if (otherTotal === 0 || difference === 0) {
            return objectivesArray.map((obj, idx) =>
                idx === changedIdx ? { ...obj, weight: newWeight } : obj
            );
        }

        // Adjust other objectives proportionally
        return objectivesArray.map((obj, idx) => {
            if (idx === changedIdx) {
                return { ...obj, weight: newWeight };
            }

            const proportionalReduction = (obj.weight / otherTotal) * difference;
            const newObjWeight = Math.max(1, Math.round(obj.weight - proportionalReduction));

            return { ...obj, weight: newObjWeight };
        });
    };

    // Handle goal selection
    const handleGoalSelect = (goalId) => {
        setSelectedGoal(goalId);
        setSelectedHigherLevelObjective(null);

        loadManagerObjectivesForGoal(goalId);
    };

    const loadObjectivesInEnrollment = async (engine, enrollmentId, options = {}) => {
        if (!enrollmentId) return [];

        //  Do NOT proceed if fiscal year dates are missing
        if (!fiscalYear.start || !fiscalYear.end) {
            console.log('⏳ [LOAD GROUP] Fiscal year not loaded yet. Returning empty...');
            return [];
        }

        try {
            console.log('🎯 [LOAD GROUP] Fetching objectives in enrollment:', enrollmentId);
            const response = await engine.query({
                groupObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        orgUnit: user?.organisationUnits?.[0]?.id,
                        enrollment: enrollmentId,
                        fields: 'event,dataValues[dataElement,value],createdBy,enrollment,eventDate,orgUnit',
                        ouMode: 'ACCESSIBLE',
                        pageSize: 1000
                    }
                }
            });

            const events = response.groupObjectives?.events || [];
            console.log('✅ [LOAD GROUP] Total events fetched:', events.length);

            const currentUserPositionTei = currentUserPosition?.trackedEntityInstance;

            // STRICT FISCAL YEAR FILTER - NO EXCEPTIONS
            const filteredEvents = events.filter(event => {
                const dvMap = {};
                event.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });

                const objectiveOwner = dvMap['SlgO60cMK3F'];
                const isOwnedByCurrentUser = objectiveOwner === currentUserPositionTei;

                if (!isOwnedByCurrentUser) {
                    console.log('📋 Skipping objective owned by another user:', {
                        objectiveName: dvMap['EuN5ztFnXsQ'],
                        owner: objectiveOwner,
                        currentUser: currentUserPositionTei
                    });
                return false;
              }

                // Get dates
                const startDate = dvMap['eDcVy661j9e'] || '';
                const completionDate = dvMap['J0rjxrDKn1l'] || '';
                
                // Get status
                const objectiveStatus = String(dvMap['DL7KxNhiV0x'] || '').toLowerCase();
                
                // For DRAFT objectives, still check if they belong to current FY
                // If draft has dates outside FY, mark as historical and skip
                if (objectiveStatus === 'draft') {
                    const startInFy = isWithinFiscalYear(startDate, fiscalYear);
                    const endInFy = isWithinFiscalYear(completionDate, fiscalYear);
                    
                    // If draft has NO dates OR dates are outside FY, use current FY dates
                    if (!startDate || !completionDate) {
                        // This is a new draft, keep it but dates will be auto-filled
                        return true;
                    }
                    
                    // If draft has dates outside current FY, skip it (historical draft)
                    if (!startInFy && !endInFy) {
                        console.log(`❌ [FILTER] Skipping historical draft objective: ${event.event}`, { startDate, completionDate });
                        return false;
                    }
                    return true;
                }

                // For non-draft (final/pending) objectives - MUST have BOTH dates in current FY
                if (!startDate || !completionDate) {
                    console.log(`❌ [FILTER] Missing dates on objective: ${event.event}`);
                    return false;
                }

                // Require BOTH start and completion dates to be in current fiscal year
                const startInFy = isWithinFiscalYear(startDate, fiscalYear);
                const endInFy = isWithinFiscalYear(completionDate, fiscalYear);

                if (!startInFy || !endInFy) {
                    console.log('❌ [FILTER] Blocking objective outside current FY:', event.event, { startDate, completionDate, startInFy, endInFy });
                    return false;
                }
                
                return true;
            });

            console.log('✅ [LOAD GROUP] Filtered to', filteredEvents.length, 'objectives for', fiscalYear.start, 'to', fiscalYear.end);
            
            // Log filtered out objectives for debugging
            const filteredOut = events.length - filteredEvents.length;
            if (filteredOut > 0) {
                console.log(`⚠️ [LOAD GROUP] Filtered out ${filteredOut} objectives not in current fiscal year`);
            }

            const groupedObjectives = filteredEvents.map(event => mapDataValuesToObjective(event));
            const enrichedObjectives = await enrichObjectivesWithDetails(groupedObjectives);
            return enrichedObjectives;
        } catch (error) {
            console.error('❌ Error loading objectives in enrollment:', error);
            return [];
        }
    };

    
    const TotalWeightTracker = memo(({ currentWeight, isComplete }) => {
        const remaining = 100 - currentWeight;
        const overage = currentWeight - 100;

        // Calculate weight distribution summary
        const weightSummary = objectives
            .filter(obj => obj.weight > 0)
            .map((obj, idx) => `${idx + 1}. ${obj.name.substring(0, 30)}...: ${obj.weight}%`)
            .join('\n');

        return (
            <div style={{
                backgroundColor: isComplete ? colors.green050 : colors.blue050,
                border: `1px solid ${isComplete ? colors.green300 : colors.blue300}`,
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                position: 'relative'
            }}>
                <div style={{
                    height: '8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${Math.min(currentWeight, 100)}%`,
                        height: '100%',
                        backgroundColor: isComplete ? colors.green500 :
                            (currentWeight > 100 ? colors.red500 : colors.blue500),
                        transition: 'width 0.3s ease'
                    }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                    <strong style={{ color: isComplete ? colors.green700 : colors.grey800 }}>
                        Total Weight: {currentWeight}%
                    </strong>
                    {!isComplete && currentWeight < 100 && (
                        <span style={{ color: colors.blue700, fontWeight: 500 }}>
                            Need {remaining}% more to reach 100%
                        </span>
                    )}
                    {currentWeight > 100 && (
                        <span style={{ color: colors.red700, fontWeight: 500 }}>
                            ⚠️ Over by {overage}% - Reduce weights
                        </span>
                    )}
                    {isComplete && (
                        <span style={{ color: colors.green700, fontWeight: 500 }}>
                            ✅ Target reached!
                        </span>
                    )}
                </div>

                {/* Show weight breakdown on hover */}
                <div style={{
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    color: colors.grey600,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px dashed rgba(0,0,0,0.1)'
                }}>
                    <strong>Weight Distribution:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                        {objectives
                            .filter(obj => obj.weight > 0)
                            .map((obj, idx) => (
                                <li key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '2px'
                                }}>
                                    <span style={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {obj.name || `Objective ${idx + 1}`}
                                    </span>
                                    <span style={{ fontWeight: 'bold' }}>{obj.weight}%</span>
                                </li>
                            ))
                        }
                    </ul>
                    {objectives.some(obj => obj.weight === 0) && (
                        <div style={{
                            marginTop: '4px',
                            fontStyle: 'italic',
                            color: colors.orange700
                        }}>
                            ⚠️ {objectives.filter(obj => obj.weight === 0).length} objective(s) have 0% weight
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const resolvePositionManagerNames = async (positionIds) => {
        if (!positionIds?.length || !engine) return {};

        try {
            // 🔹 Remove duplicates and validate IDs
            const uniquePositionIds = [...new Set(positionIds.filter(id =>
                id && typeof id === 'string' && id.trim() !== ''
            ))];

            console.log('🔹 [RESOLVE MANAGERS] Position IDs:', uniquePositionIds);

            if (uniquePositionIds.length === 0) return {};

            // 🔹 Fetch positions individually to avoid filter issues
            const positions = [];
            for (const id of uniquePositionIds) {
                try {
                    const positionResponse = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${id}`,
                            params: {
                                fields: 'trackedEntityInstance,attributes[attribute,value]'
                            }
                        }
                    });
                    if (positionResponse.position) {
                        positions.push(positionResponse.position);
                    }
                } catch (error) {
                    console.warn(`⚠️ Failed to fetch position ${id}:`, error);
                }
            }
            console.log('✅ [POSITIONS LOADED]', positions.length, 'positions');

            // Debug: Log each position's incumbent
            positions.forEach(pos => {
                const attrs = {};
                pos.attributes?.forEach(attr => { attrs[attr.attribute] = attr.value; });
                console.log('🔍 [POSITION DEBUG]', {
                    positionTei: pos.trackedEntityInstance,
                    incumbentId: attrs['V66IFsw5kq6'],
                    positionName: attrs['w3bwqUZoc6V']
                });
            });

            // 🔹 Extract incumbent IDs efficiently
            const positionMap = new Map();
            const incumbentIdSet = new Set();

            positions.forEach(position => {
                const attributes = {};
                position.attributes?.forEach(attr => {
                    attributes[attr.attribute] = attr.value;
                });

                const incumbentId = attributes['V66IFsw5kq6']; // Current Incumbent Staff ID
                const reportingToTei = attributes['SjP47qi3JeB']; // Reporting To Position TEI

                if (incumbentId) {
                    positionMap.set(position.trackedEntityInstance, {
                        positionId: position.trackedEntityInstance,
                        incumbentId,
                        reportingToTei,
                        attributes
                    });
                    incumbentIdSet.add(incumbentId);
                } else {
                    console.warn('⚠️ No incumbent found for position:', position.trackedEntityInstance);
                    positionMap.set(position.trackedEntityInstance, {
                        positionId: position.trackedEntityInstance,
                        incumbentId: null,
                        reportingToTei,
                        attributes
                    });
                }
            });

            // 🔹 Fetch reporting to positions if not already fetched
            const reportingToTeis = Array.from(positionMap.values())
                .map(pos => pos.reportingToTei)
                .filter(tei => tei && !positionMap.has(tei));

            if (reportingToTeis.length > 0) {
                console.log('🔹 Fetching additional reporting to positions:', reportingToTeis);
                for (const tei of reportingToTeis) {
                    try {
                        const positionResponse = await engine.query({
                            position: {
                                resource: `trackedEntityInstances/${tei}`,
                                params: {
                                    fields: 'trackedEntityInstance,attributes[attribute,value]'
                                }
                            }
                        });
                        if (positionResponse.position) {
                            const pos = positionResponse.position;
                            const attributes = {};
                            pos.attributes?.forEach(attr => {
                                attributes[attr.attribute] = attr.value;
                            });
                            const incumbentId = attributes['V66IFsw5kq6'];
                            const reportingToTei = attributes['SjP47qi3JeB'];
                            positionMap.set(tei, {
                                positionId: tei,
                                incumbentId,
                                reportingToTei,
                                attributes
                            });
                            if (incumbentId) incumbentIdSet.add(incumbentId);
                        }
                    } catch (error) {
                        console.warn(`⚠️ Failed to fetch reporting to position ${tei}:`, error);
                    }
                }
            }

            const incumbentIds = Array.from(incumbentIdSet);
            console.log('✅ [INCUMBENT IDS FOUND]', incumbentIds.length, 'unique staff IDs');

            // 🔹 Get staff details in batch
            const staffMap = new Map();

            if (incumbentIds.length > 0) {
                // Try to get usernames from staff TEIs first
                const staffFilter = incumbentIds.map(id => `trackedEntityInstance:eq:${id}`).join(';');

                try {
                    const staffResponse = await engine.query({
                        staff: {
                            resource: 'trackedEntityInstances',
                            params: {
                                trackedEntityType: 'OaAbpIs1cV7', // Staff TE type
                                filter: staffFilter,
                                fields: 'trackedEntityInstance,attributes[attribute,value]',
                                pageSize: incumbentIds.length
                            }
                        }
                    });

                    const staffMembers = staffResponse.staff?.trackedEntityInstances || [];

                    // 🔹 Collect usernames to batch user API calls
                    const usernames = [];
                    const staffUsernameMap = new Map();

                    staffMembers.forEach(staff => {
                        const attributes = {};
                        staff.attributes?.forEach(attr => {
                            attributes[attr.attribute] = attr.value;
                        });

                        const username = attributes['g3RRIfDiQra']?.trim();
                        if (username) {
                            staffUsernameMap.set(staff.trackedEntityInstance, username);
                            usernames.push(username);
                        }
                    });

                    // 🔹 Batch fetch user details
                    if (usernames.length > 0) {
                        const userFilter = usernames.map(u => `username:eq:${u}`).join(';');

                        try {
                            const usersResponse = await engine.query({
                                users: {
                                    resource: 'users',
                                    params: {
                                        filter: userFilter,
                                        fields: 'id,username,displayName,firstName,surname',
                                        pageSize: usernames.length
                                    }
                                }
                            });

                            const users = usersResponse.users?.users || [];

                            // Create username to displayName map
                            const userDisplayNameMap = new Map();
                            users.forEach(user => {
                                const displayName = user.displayName?.trim() ||
                                    `${user.firstName?.trim() || ''} ${user.surname?.trim() || ''}`.trim() ||
                                    user.username?.trim();
                                if (displayName) {
                                    userDisplayNameMap.set(user.username, displayName);
                                }
                            });

                            // Map staff IDs to display names
                            staffUsernameMap.forEach((username, staffId) => {
                                const displayName = userDisplayNameMap.get(username) || username;
                                staffMap.set(staffId, displayName);
                            });

                        } catch (userError) {
                            console.warn('⚠️ Failed to batch fetch user details:', userError);
                            // Fallback: use usernames directly
                            staffUsernameMap.forEach((username, staffId) => {
                                staffMap.set(staffId, username);
                            });
                        }
                    }

                } catch (staffError) {
                    console.error('❌ Failed to fetch staff details:', staffError);
                }
            }

            // 🔹 Create final position-to-name mapping
            // BUG FIX: Return the INCUMBENT (person) of the position, not the manager
            const managerNameMap = {};

            Array.from(positionMap.entries()).forEach(([positionId, posData]) => {
                // Primary: Return the incumbent (person holding the position)
                const incumbentId = posData.incumbentId;
                if (incumbentId && staffMap.has(incumbentId)) {
                    managerNameMap[positionId] = staffMap.get(incumbentId);
                    console.log('🔍 [NAME RESOLUTION]', {
                        positionId,
                        incumbentId,
                        resolvedName: staffMap.get(incumbentId),
                        source: 'incumbent'
                    });
                } else {
                    // Fallback: Use position title
                    const positionTitle = posData.attributes['w3bwqUZoc6V'] ||
                        posData.attributes['NWe61Sa41CS'] ||
                        'Unknown Position';
                    managerNameMap[positionId] = positionTitle;
                    console.log('🔍 [NAME RESOLUTION]', {
                        positionId,
                        incumbentId,
                        fallbackTo: positionTitle,
                        source: 'position title'
                    });
                }
            });

            console.log('✅ [MANAGER NAMES RESOLVED]', managerNameMap);
            return managerNameMap;

        } catch (error) {
            console.error('❌ Error resolving position manager names:', error);

            // Graceful fallback
            const fallbackMap = {};
            if (positionIds?.length) {
                positionIds.forEach(id => {
                    if (id) {
                        fallbackMap[id] = `Manager (${id.substring(0, 8)}...)`;
                    }
                });
            }
            return fallbackMap;
        }
    };

const loadManagerObjectivesForGoal = async (goalId) => {
    if (!currentUserPosition || !engine || !mounted || !user?.organisationUnits?.[0]?.id) return;

    setLoadingFilteredObjectives(true);
    setLoadingHigherLevelObjectives(true);

    try {
        console.log('🔍 [LOADING MANAGER OBJECTIVES] For goal:', goalId);

        // Get ALL objectives for this org unit
        const allObjectivesResponse = await engine.query({
            allObjectives: {
                resource: 'events',
                params: {
                    program: 'yiT00XKrrHP',
                    programStage: 'VIBtpvkpHFR',
                    fields: 'event,dataValues[dataElement,value],createdBy,storedBy',
                    orgUnit: user.organisationUnits[0].id,
                    ouMode: 'ACCESSIBLE',
                    pageSize: 500
                }
            }
        });

        const allObjectives = allObjectivesResponse.allObjectives?.events || [];
        console.log('✅ [ALL OBJECTIVES LOADED]', allObjectives.length, 'total objectives');

        // 🔹 Get the DIRECT manager's position ID (only the immediate manager)
        const userAttrs = extractAttributes(currentUserPosition.attributes || []);
        const directManagerPositionTei = userAttrs['SjP47qi3JeB']; // REPORTING_TO UID

        console.log('🔍 [DIRECT MANAGER] User position TEI:', currentUserPosition.trackedEntityInstance);
        console.log('🔍 [DIRECT MANAGER] Direct manager position TEI:', directManagerPositionTei);

        // If no direct manager, user is top-level
        if (!directManagerPositionTei) {
            console.log('🔍 [DIRECT MANAGER] No direct manager found - user is top level');
            setFilteredManagerObjectives([]);
            setLoadingFilteredObjectives(false);
            setLoadingHigherLevelObjectives(false);
            return;
        }

        // 🔹 VERIFY the manager position exists and get its details
        let managerExists = false;
        let managerIncumbentId = null;
        let managerPositionName = 'Unknown Manager';

        try {
            const managerPosResponse = await engine.query({
                managerPos: {
                    resource: `trackedEntityInstances/${directManagerPositionTei}`,
                    params: {
                        fields: 'trackedEntityInstance,attributes[attribute,value]'
                    }
                }
            });

            if (managerPosResponse.managerPos) {
                managerExists = true;
                const managerAttrs = extractAttributes(managerPosResponse.managerPos.attributes || []);
                managerIncumbentId = managerAttrs['V66IFsw5kq6'];
                managerPositionName = managerAttrs['w3bwqUZoc6V'] || 'Unknown Position';
                
                console.log('🔍 [DIRECT MANAGER] Manager found:', {
                    positionId: directManagerPositionTei,
                    positionName: managerPositionName,
                    incumbentId: managerIncumbentId
                });
            }
        } catch (error) {
            console.warn('⚠️ [DIRECT MANAGER] Could not verify manager position:', error);
        }

        if (!managerExists) {
            console.warn('⚠️ [DIRECT MANAGER] Manager position does not exist:', directManagerPositionTei);
            setFilteredManagerObjectives([]);
            setLoadingFilteredObjectives(false);
            setLoadingHigherLevelObjectives(false);
            return;
        }

        // 🔹 Filter objectives that belong ONLY to the direct manager's position
        const managerObjectives = allObjectives.filter(obj => {
            const dataValuesMap = {};
            obj.dataValues?.forEach(dv => {
                dataValuesMap[dv.dataElement] = dv.value;
            });

            const objectivePositionId = dataValuesMap['SlgO60cMK3F']; // Position ID of objective owner
            const goalAlignmentValue = dataValuesMap['VhrIQQIVpB9']; // Direct goal alignment
            const parentObjectiveId = dataValuesMap['neuilg3L4d2'] || dataValuesMap['plJovUlxfQ0']; // Parent objective
            const objectiveName = dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed';

            // ✅ CRITICAL: ONLY objectives from the DIRECT manager's position
            const belongsToDirectManager = objectivePositionId === directManagerPositionTei;

            // Check if aligned to the goal (directly or via parent)
            const matchesGoalDirectly = goalAlignmentValue === goalId;
            
            let matchesGoalViaParent = false;
            if (!matchesGoalDirectly && parentObjectiveId) {
                const parentObj = allObjectives.find(p => p.event === parentObjectiveId);
                if (parentObj) {
                    const parentDvMap = {};
                    parentObj.dataValues?.forEach(dv => {
                        parentDvMap[dv.dataElement] = dv.value;
                    });
                    const parentGoalAlignment = parentDvMap['VhrIQQIVpB9'];
                    matchesGoalViaParent = parentGoalAlignment === goalId;
                    
                    if (matchesGoalViaParent) {
                        console.log('🔗 [PARENT CHAIN]', {
                            objectiveId: obj.event,
                            name: objectiveName,
                            parentId: parentObjectiveId,
                            parentGoal: parentGoalAlignment,
                            targetGoal: goalId
                        });
                    }
                }
            }

            const matchesGoal = matchesGoalDirectly || matchesGoalViaParent;

            // DEBUG: Log if this objective belongs to the manager but doesn't match the goal
            if (belongsToDirectManager && !matchesGoal) {
                console.log('🔍 [MANAGER OBJECTIVE - NOT MATCHING GOAL]', {
                    objectiveId: obj.event,
                    name: objectiveName,
                    goalAlignment: goalAlignmentValue,
                    parentGoal: parentObjectiveId ? 'has parent' : 'no parent',
                    targetGoal: goalId
                });
            }

            // Return TRUE only if it belongs to the DIRECT manager AND matches the goal
            return belongsToDirectManager && matchesGoal;
        });

        console.log('✅ [MANAGER OBJECTIVES FILTERED]', managerObjectives.length, 'objectives from DIRECT manager');

        // If no objectives found, check if manager has ANY objectives (not just for this goal)
        if (managerObjectives.length === 0) {
            const allManagerObjectives = allObjectives.filter(obj => {
                const dataValuesMap = {};
                obj.dataValues?.forEach(dv => {
                    dataValuesMap[dv.dataElement] = dv.value;
                });
                return dataValuesMap['SlgO60cMK3F'] === directManagerPositionTei;
            });

            console.log(`📊 [DEBUG] Manager has ${allManagerObjectives.length} total objectives (not filtered by goal)`);
            
            if (allManagerObjectives.length > 0) {
                console.log('📊 [DEBUG] Manager objectives found but none aligned to goal:', goalId);
                console.log('📊 [DEBUG] Manager objectives goals:', allManagerObjectives.map(obj => {
                    const dvMap = {};
                    obj.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });
                    return {
                        name: dvMap['EuN5ztFnXsQ'],
                        goal: dvMap['VhrIQQIVpB9']
                    };
                }));
                
                // Show a more user-friendly alert
                showAlert(
                    'No Manager Objectives for This Goal',
                    'info',
                    `Your manager (${managerPositionName}) has objectives but none are aligned to the selected goal. Please select a different goal or ask your manager to align objectives to this goal.`
                );
            } else {
                console.warn('⚠️ [DIRECT MANAGER] Manager has NO objectives at all');
                showAlert(
                    'Manager Has No Objectives',
                    'info',
                    `Your manager (${managerPositionName}) has not created any objectives yet. Please check back later.`
                );
            }
        }

        // 🔹 Extract position IDs from filtered objectives
        const positionIds = managerObjectives
            .map(obj => {
                const dataValuesMap = {};
                obj.dataValues?.forEach(dv => {
                    dataValuesMap[dv.dataElement] = dv.value;
                });
                return dataValuesMap['SlgO60cMK3F'];
            })
            .filter(id => id && typeof id === 'string');

        console.log('🔹 [POSITION IDS TO RESOLVE]', positionIds);

        // 🔹 Resolve manager names
        const managerNameMap = await resolvePositionManagerNames(positionIds);

        // 🔹 Format objectives with resolved manager names
        const formattedObjectives = await Promise.all(
            managerObjectives.map(async (obj) => {
                const dataValuesMap = {};
                obj.dataValues?.forEach(dv => {
                    dataValuesMap[dv.dataElement] = dv.value;
                });

                const positionId = dataValuesMap['SlgO60cMK3F'];

                // Get manager name
                let managerName = managerPositionName; // Use the resolved manager name
                if (positionId && managerNameMap[positionId]) {
                    managerName = managerNameMap[positionId];
                } else if (obj.storedBy) {
                    try {
                        const userResponse = await engine.query({
                            creatorUser: {
                                resource: `users/${obj.storedBy}`,
                                params: {
                                    fields: 'displayName,firstName,surname'
                                }
                            }
                        });
                        if (userResponse.creatorUser) {
                            managerName = userResponse.creatorUser.displayName?.trim() ||
                                userResponse.creatorUser.name?.trim() ||
                                obj.storedBy;
                        }
                    } catch (error) {
                        console.warn('⚠️ Could not fetch creator details:', error);
                        managerName = obj.storedBy;
                    }
                }

                const directGoalId = dataValuesMap['VhrIQQIVpB9'] || null;
                const parentObjId = dataValuesMap['neuilg3L4d2'] || dataValuesMap['plJovUlxfQ0'] || null;

                return {
                    id: obj.event,
                    name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                    description: dataValuesMap['nWqW5BrcgP1'] || '',
                    createdBy: managerName,
                    userRole: dataValuesMap['vFoMLF5eZ5X'] || '',
                    objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
                    positionId: positionId || '',
                    dataValues: obj.dataValues || [],
                    storedBy: obj.storedBy,
                    dateCreated: obj.created || new Date().toISOString(),
                    directGoalId: directGoalId,
                    parentObjectiveId: parentObjId,
                    _managerGoalName: goals.find(g => g.id === goalId)?.name || 'Unknown Goal',
                    _goalId: goalId,
                    progress: parseInt(dataValuesMap['LEEqJmI9Lyi']) || 0,
                    companyAlignedGoal: directGoalId || null
                };
            })
        );

        console.log('✅ [FORMATTED OBJECTIVES]', formattedObjectives.length, 'objectives from DIRECT manager');

        // ✅ Check if editing objective is aligned to one of these
        if (editingObjective?.objectiveAlignment && formattedObjectives.length > 0) {
            const alignedObjective = formattedObjectives.find(
                obj => obj.id === editingObjective.objectiveAlignment
            );

            if (alignedObjective && !selectedHigherLevelObjective?.id) {
                console.log('✅ [EDIT MODE] Found aligned objective in manager list:', alignedObjective.name);
                setSelectedHigherLevelObjective(alignedObjective);

                updateObjectiveField(0, 'objectiveAlignment', alignedObjective.id);
                updateObjectiveField(0, 'higherLevelObjectiveId', alignedObjective.id);
                updateObjectiveField(0, 'higherLevelObjectiveName', alignedObjective.name);

                const managerGoalId = alignedObjective.dataValues?.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                )?.value;

                if (managerGoalId && goals.length > 0) {
                    const managerGoal = goals.find(g => g.id === managerGoalId);
                    if (managerGoal) {
                        updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                    }
                }
            }
        }

        setFilteredManagerObjectives(formattedObjectives);

    } catch (error) {
        console.error('❌ Error loading manager objectives for goal:', error);
        setFilteredManagerObjectives([]);
        
        let errorMessage = 'Failed to load manager objectives for this goal';
        if (error.message?.includes('403') || error.message?.includes('401')) {
            errorMessage = 'You don\'t have permission to view manager objectives. Please contact your administrator.';
        }
        showAlert(errorMessage, 'error');
    } finally {
        if (mounted) {
            setLoadingFilteredObjectives(false);
            setLoadingHigherLevelObjectives(false);
        }
    }
};

    // Clear goal selection
    const handleClearGoalSelection = () => {
        setSelectedGoal('');
        setFilteredManagerObjectives([]);
        setSelectedHigherLevelObjective(null);
    };


    // Create memoized mapping of goal ID to name
    const goalNameById = useMemo(() => {
        const map = {};
        goalItems.forEach(goal => {
            map[goal.id] = goal.name;
        });
        return map;
    }, [goalItems]);



    // Helper function to check if current user is the objective creator
    const isObjectiveCreator = () => {
        if (!editingObjective || !user || !currentUserPosition) return false;

        // Check if current user's position TEI matches the objective's position TEI
        const currentPositionTei = currentUserPosition?.trackedEntityInstance;
        const objectivePositionTei = editingObjective.positionTeiId ||
            editingObjective.positionTrackedEntityInstance;

        return currentPositionTei === objectivePositionTei;
    };

    
    // State to store manager status
    const [isUserDirectManager, setIsUserDirectManager] = useState(false);
    const [permissionsCache, setPermissionsCache] = useState({
        isCreator: false,
        isManager: false,
        lastChecked: null,
        objectiveId: null
    });

    const [currentStep, setCurrentStep] = useState('objective');

    const {
        currentOrgUnit,
        handleSaveObjective,
        setUiMode,
        editingObjective: contextEditingObjective,
        handleUpdateObjective,
        selectedHigherLevelObjective: contextSelectedHigherLevelObjective,
        setSelectedHigherLevelObjective: contextSetSelectedHigherLevelObjective,
        loadHigherLevelObjectiveDetails,
        uiMode,
        refreshObjectives,
        fiscalYear,
        setFiscalYear,
        fiscalYearLoaded,
        setFiscalYearLoaded,
    } = usePerformanceContext();

    const createBlankObjective = useCallback((indexHint = 0) => ({
        id: null,
        _tempId: `temp_${Date.now()}_${indexHint}`,
        name: '',
        description: '',
        weight: 0,
        startDate: fiscalYear.start || '',
        completionDate: fiscalYear.end || '',
        orgUnit: user?.organisationUnits?.[0]?.id || '',
        remarks: '',
        score: 0,
        performanceYear: new Date().getFullYear(),
        workType: '',
        companyAlignedGoal: '',
        kras: [],
        kpis: [{ name: '', description: '', target: '' }],
    }), [fiscalYear.start, fiscalYear.end, user?.organisationUnits]);


    const selectedHigherLevelObjective = React.useMemo(() => {
        // If we have the prop, use it
        if (propSelectedHigherLevelObjective !== undefined) {
            return propSelectedHigherLevelObjective;
        }

        // If context has just an ID string, we need to find the full object
        if (typeof contextSelectedHigherLevelObjective === 'string') {
            // Try to find it in higherLevelObjectives first
            const foundInList = higherLevelObjectives.find(
                obj => obj.id === contextSelectedHigherLevelObjective
            );

            if (foundInList) {
                console.log('✅ [OBJECTIVE RESOLUTION] Found full object from ID:', foundInList.name);
                return foundInList;
            }

            // Try to find it in filteredManagerObjectives
            const foundInFiltered = filteredManagerObjectives.find(
                obj => obj.id === contextSelectedHigherLevelObjective
            );

            if (foundInFiltered) {
                console.log('✅ [OBJECTIVE RESOLUTION] Found in filtered list:', foundInFiltered.name);
                return foundInFiltered;
            }

            // If not found, return a placeholder with the ID
            console.log('🟡 [OBJECTIVE RESOLUTION] Creating placeholder for ID:', contextSelectedHigherLevelObjective);
            return {
                id: contextSelectedHigherLevelObjective,
                name: `Objective ${contextSelectedHigherLevelObjective.substring(0, 8)}...`,
                description: 'Loading details...',
                isLoading: true
            };
        }

        // Otherwise, return whatever context has
        return contextSelectedHigherLevelObjective;
    }, [propSelectedHigherLevelObjective, contextSelectedHigherLevelObjective, higherLevelObjectives, filteredManagerObjectives]);

    const setSelectedHigherLevelObjective = propSetSelectedHigherLevelObjective !== undefined
        ? propSetSelectedHigherLevelObjective
        : contextSetSelectedHigherLevelObjective;

    const editingObjective = objective !== undefined
        ? objective
        : contextEditingObjective;


    const [objectives, setObjectives] = useState([
        {
            id: null,
            _tempId: `temp_${Date.now()}_0`,
            name: '',
            description: '',
            weight: 0,
            startDate: '',
            completionDate: '',
            orgUnit: user?.organisationUnits?.[0]?.id || '',
            remarks: '',
            score: 0,
            performanceYear: new Date().getFullYear(),
            workType: '',
            companyAlignedGoal: '',
            kras: [{ name: '', description: '', weight: 0 }],
            kpis: [{ name: '', description: '', target: '' }],
        }
    ]);

    const isEditMode = mode === 'edit' && (objectives.some(obj => obj.id) || weightGroupEnrollment);

    const [submissionError, setSubmissionError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);


    const currentObjectiveIndex = objectives.length - 1;
    const currentObjective = objectives[currentObjectiveIndex];

    const GOAL_DATAELEMENT_MAPPING = {
        'HJqqfzhH614': 'name',
        'zS8QIgZc0Ll': 'description',
        'pOOGDRzEo98': 'progress',
        'MH7DQVegD1j': 'startDate',
        'pUvJQ9vy3A3': 'endDate',
        'Co2i5BleCcZ': 'priority',
        'uBPW95V2VoX': 'category',
        'ir4nG6zZn4w': 'createdBy',
        'VhrIQQIVpB9': 'companyAlignedGoal'
    };

    const handleEditObjectiveWeightChange = (index, newWeight) => {
    // 1. Validate input
    if (newWeight < 1) {
        showAlert('Minimum Weight', 'warning', 'Weight cannot be less than 1%');
        return;
    }
    if (newWeight > 100) {
        showAlert('Maximum Weight', 'warning', 'Weight cannot exceed 100%');
        return;
    }

    // 2. Get current state
    const currentObjectives = objectives;
    const currentWeight = getObjectiveWeight(currentObjectives[index]);
    
    // 3. If no change, do nothing
    if (newWeight === currentWeight) return;

    // 4. ✅ ONLY update the specific objective - NO AUTO-ADJUSTMENT
    setObjectives(prev => prev.map((obj, i) =>
        i === index ? { ...obj, weight: newWeight } : obj
    ));

    // 5. Calculate new total for feedback
    const newTotal = objectives.reduce((sum, o, i) => 
        i === index ? sum + newWeight : sum + getObjectiveWeight(o), 0
    );

    // 6. Show feedback (informational only - no auto-fixing)
    if (newTotal === 100) {
        showAlert('Weight Updated', 'success', `"${currentObjectives[index]?.name?.substring(0, 20) || 'Objective'}" set to ${newWeight}%. Total: ${newTotal}% ✅`);
    } else if (newTotal < 100) {
        showAlert('Weight Updated', 'info', `"${currentObjectives[index]?.name?.substring(0, 20) || 'Objective'}" set to ${newWeight}%. Total: ${newTotal}% (${100 - newTotal}% remaining)`);
    } else {
        showAlert('Weight Updated', 'warning', `"${currentObjectives[index]?.name?.substring(0, 20) || 'Objective'}" set to ${newWeight}%. Total: ${newTotal}% (⚠️ ${newTotal - 100}% over)`);
    }
};

    // Get the weight of only the staff's OWN objectives (excluding manager objectives for linking)
    const getOwnObjectivesWeight = useCallback(() => {
        // For CEO/top-level users, count all objectives
        if (isTopLevelUser) {
            return sumObjectiveWeights(objectives);
        }
        
        // For staff users, only count objectives that:
        // 1. Have no objectiveAlignment (these are their own, not linked to manager)
        // 2. OR are imported objectives (_isImported flag)
        // 3. OR have no ID (new unsaved objectives)
        const ownObjectives = objectives.filter(obj => {
            // If it has objectiveAlignment to a manager objective, it's NOT a staff's own objective
            // It's just a reference/link
            if (obj.objectiveAlignment && !obj._isImported) {
                return false;
            }
            return true;
        });
        
        return sumObjectiveWeights(ownObjectives);
    }, [objectives, isTopLevelUser, sumObjectiveWeights]);

    // Add this function to remove an objective in edit mode
    const handleRemoveObjectiveInEditMode = (index) => {
        if (objectives.length <= 1) {
            showAlert('Cannot Remove', 'warning', 'At least one objective is required');
            return;
        }

        const removedWeight = getObjectiveWeight(objectives[index]);
        const remainingWeight = objectives.reduce((sum, o, i) =>
            i !== index ? sum + getObjectiveWeight(o) : sum
            , 0);

        if (remainingWeight === 0) {
            showAlert('Cannot Remove', 'warning', 'Cannot remove the only objective with weight');
            return;
        }

        if (window.confirm(`Remove "${objectives[index]?.name || 'this objective'}"? This will free up ${removedWeight}% weight.`)) {
            setObjectives(prev => prev.filter((_, i) => i !== index));
            setExpandedObjectiveIndex(null);
            showAlert('Objective Removed', 'success', `Freed up ${removedWeight}% weight`);
        }
    };

    // Add this function to add a new objective in edit mode
    const handleAddObjectiveInEditMode = () => {
        const currentTotal = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const remaining = 100 - currentTotal;

        // Allow adding objectives even at 100% - validation will happen at save time
        // Users can exceed 100% temporarily and adjust before saving
        const defaultWeight = remaining > 0 ? Math.min(remaining, 25) : 25;

        setObjectives(prev => [
            ...prev,
            {
                id: null,
                _tempId: `temp_${Date.now()}_${prev.length}`,
                name: '',
                description: '',
                weight: defaultWeight,
                startDate: fiscalYear.start || '',
                completionDate: fiscalYear.end || '',
                orgUnit: user?.organisationUnits?.[0]?.id || '',
                remarks: '',
                score: 0,
                performanceYear: new Date().getFullYear(),
                workType: '',
                companyAlignedGoal: '',
                kras: [],
                kpis: [{ name: '', description: '', target: '' }],
            }
        ]);

        // Auto-expand the new objective
        setExpandedObjectiveIndex(objectives.length);

        if (remaining > 0) {
            showAlert('New Objective Added', 'success', `You have ${remaining}% remaining to allocate`);
        } else {
            showAlert('New Objective Added', 'info', `Total weight is now ${currentTotal + defaultWeight}%. Please adjust other objectives to maintain 100%.`);
        }
    };

    // Add this function to check program access proactively
    const checkProgramAccessProactively = async () => {
        if (!user?.organisationUnits?.[0]?.id) {
            console.warn('⚠️ No user org unit found');
            return;
        }

        const userOrgUnitId = user.organisationUnits[0].id;

        try {
            console.log('🔍 [PROACTIVE CHECK] Checking program assignment for org unit:', userOrgUnitId);

            // Check program assignment
            const response = await engine.query({
                programAssignment: {
                    resource: `programs/yiT00XKrrHP`,
                    params: {
                        fields: 'id,name,organisationUnits[id,name]'
                    }
                }
            });

            const program = response.programAssignment;
            const assignedOrgUnits = program.organisationUnits || [];

            // Check if user's org unit has the program assigned
            const isAssigned = assignedOrgUnits.some(ou => ou.id === userOrgUnitId);

            console.log('🔍 [PROACTIVE CHECK] Result:', {
                programName: program.name,
                userOrgUnit: userOrgUnitId,
                isAssigned,
                assignedOrgUnits: assignedOrgUnits.map(ou => ({ id: ou.id, name: ou.name })),
                totalAssigned: assignedOrgUnits.length
            });

            setProgramAccess({
                isAssigned,
                checked: true,
                missingOrgUnit: isAssigned ? null : userOrgUnitId,
                assignedOrgUnits
            });

            // If not assigned, show immediate warning
            if (!isAssigned) {
                showProgramAccessWarning(userOrgUnitId, assignedOrgUnits);
            }

        } catch (error) {
            console.error('❌ [PROACTIVE CHECK] Error:', error);
            setProgramAccess({
                isAssigned: false,
                checked: true,
                missingOrgUnit: userOrgUnitId,
                assignedOrgUnits: [],
                error: error.message
            });
        }
    };

    const showProgramAccessWarning = (missingOrgUnitId, assignedOrgUnits = []) => {
        // Create a detailed warning message
        const availableUnits = assignedOrgUnits.map(ou => `• ${ou.name} (${ou.id})`).join('\n');

        showAlert(
            '⚠️ Program Configuration Required',
            'warning',
            `The Performance Management App is not assigned to your department.\n\n` +
            `Please contact your administrator to assign the performance App to your department.`
        );
    };

    <TotalWeightTracker
        currentWeight={sumObjectiveWeights(objectives)}
        isComplete={sumObjectiveWeights(objectives) === 100}
    />


    const showAlert = (message, type = 'error', details = '') => {
        // Check if component is still mounted before setting state
        setNotification({
            message: String(message),
            type,
            details: String(details)
        });

        if (type !== 'error') {
            setTimeout(() => {
                setNotification(null);
            }, 10000);
        }
    };

    // Add this function to clear notifications
    const clearNotification = () => {
        setNotification(null);
    };

    const updateObjectiveField = useCallback((index, field, value) => {
        setObjectives(prev => {
            const newComps = [...prev];
            newComps[index] = {
                ...newComps[index],
                [field]: value
            };
            return newComps;
        });
    }, []);

    // KRA Management functions
    const handleKraChange = (objIndex, kraIndex, field, value) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex) {
                const updatedKras = [...(obj.kras || [])];
                updatedKras[kraIndex] = {
                    ...updatedKras[kraIndex],
                    [field]: value,
                    // Preserve the ID if it exists
                    ...(updatedKras[kraIndex].id && { id: updatedKras[kraIndex].id })
                };
                return { ...obj, kras: updatedKras };
            }
            return obj;
        }));
    };

    const handleAddKra = (objIndex) => {
        setObjectives(prev => prev.map((obj, i) =>
            i === objIndex
                ? { ...obj, kras: [...(obj.kras || []), { name: '', description: '', weight: 0 }] }
                : obj
        ));
    };

    const handleRemoveKra = (objIndex, kraIndex) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex && (obj.kras || []).length > 1) {
                const updatedKras = [...(obj.kras || [])];
                updatedKras.splice(kraIndex, 1);
                return { ...obj, kras: updatedKras };
            }
            return obj;
        }));
    };

    // KPI Management functions
    const handleKpiChange = (objIndex, kpiIndex, field, value) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex) {
                const updatedKpis = [...(obj.kpis || [])];
                updatedKpis[kpiIndex] = {
                    ...updatedKpis[kpiIndex],
                    [field]: value,
                    // Preserve the ID if it exists
                    ...(updatedKpis[kpiIndex].id && { id: updatedKpis[kpiIndex].id })
                };
                return { ...obj, kpis: updatedKpis };
            }
            return obj;
        }));
    };

    const handleAddKpi = (objIndex) => {
        setObjectives(prev => prev.map((obj, i) =>
            i === objIndex
                ? { ...obj, kpis: [...(obj.kpis || []), { name: '', description: '', target: '' }] }
                : obj
        ));
    };

    const handleRemoveKpi = (objIndex, kpiIndex) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex && (obj.kpis || []).length > 1) {
                const updatedKpis = [...(obj.kpis || [])];
                updatedKpis.splice(kpiIndex, 1);
                return { ...obj, kpis: updatedKpis };
            }
            return obj;
        }));
    };

    const canSaveAsFinal = useCallback(() => {
        const totalWeight = objectives.reduce((sum, o) => sum + (o.weight || 0), 0);
        if (totalWeight !== 100) return true;
        
        const objectivesWithContent = objectives.filter(obj => obj.name?.trim());
        if (objectivesWithContent.length === 0) return true;
        
        // Check each objective has goal linkage (mandatory for ALL users)
        const missingGoalLinkage = objectivesWithContent.some(obj => 
            !obj.companyAlignedGoal && !obj._tempGoalId
        );
        if (missingGoalLinkage) return true;
        
        // Check each objective has at least one KPI
        const missingKPI = objectivesWithContent.some(obj => 
            !obj.kpis?.some(k => k.name?.trim())
        );
        if (missingKPI) return true;
        
        return false;
    }, [objectives, goals]);

    const {
        higherLevelObjectives: contextHigherLevelObjectives,
        loadingHigherLevel
    } = usePerformanceContext();

    useEffect(() => {
        const checkProtectedStatus = () => {
            if (mode === 'edit' && objective?.id) {
                // Check if this objective has child objectives (is protected)
                if (protectedObjectiveIds.has(objective.id)) {
                    const childCount = childCounts[objective.id] || 0;
                    const reason = `This objective has ${childCount} linked subordinate objective(s) and cannot be modified. Please unlink them first.`;
                    
                    setIsFormLocked(true);
                    setLockReason(reason);
                    
                    // Show alert and close form after a delay
                    alert(reason);
                    setTimeout(() => {
                        if (onCancel) onCancel();
                    }, 3000);
                }
            }
        };
        
        checkProtectedStatus();
    }, [mode, objective?.id, protectedObjectiveIds, childCounts, onCancel]);

    useEffect(() => {
        // Reset all expanded objectives when form is first opened
       setExpandedObjectiveIndex(null);
    }, [mode]); // Runs when switching between add/edit mode

    

    useEffect(() => {
        if (isInitialLoad && editingObjective && !loadingGoals && goals.length > 0) {
            // Let the consolidated effect run once
            setTimeout(() => setIsInitialLoad(false), 100);
        }
    }, [editingObjective, loadingGoals, goals.length]);

    //----------------------TEMPORARY TO BE REMOVED
    useEffect(() => {
        console.log('📊 Objectives in state:', objectives.map(o => ({
            name: o.name,
            weight: o.weight,
            hasKpis: o.kpis?.some(k => k.name?.trim()),
            id: o.id
        })));
    }, [objectives]);
   //-------------------------------------------

    useEffect(() => {
        const beforeUnload = (e) => {
            const total = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
            if (total > 0 && total !== 100) {
                e.preventDefault();
                e.returnValue = 'You have unsaved objectives with incomplete weight allocation (not 100%). Are you sure?';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [objectives]);

    useEffect(() => {
        if (contextHigherLevelObjectives && contextHigherLevelObjectives.length > 0) {
            setHigherLevelObjectives(contextHigherLevelObjectives);
        }
    }, [contextHigherLevelObjectives]);

    useEffect(() => {
        // LOAD FISCAL YEAR FROM PROGRAM EVENT METADATA
        const loadFiscalYear = async () => {
            const orgUnitId = user?.organisationUnits?.[0]?.id;
            if (!orgUnitId || !engine) {
                console.warn('⚠️ Cannot load fiscal year: missing orgUnit or engine');
                return;
            }

            try {
                console.log('🔍 [FISCAL YEAR] Loading for orgUnit:', orgUnitId);

                // ✅ SIMPLIFIED: Query events directly without TEI lookup first
                const eventsResponse = await engine.query({
                    eventsQuery: {
                        resource: 'events',
                        params: {
                            program: 'NkReudUAW4F',
                            programStage: 'htIWgNQWEMm',
                            orgUnit: orgUnitId,  // Use orgUnit instead of ou for events query
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1,
                            order: 'eventDate:desc',
                            fields: 'event,dataValues[dataElement,value]'
                        }
                    }
                });

                const event = eventsResponse.eventsQuery?.events?.[0];

                if (!event) {
                    console.warn('⚠️ [FISCAL YEAR] No metadata event found for orgUnit:', orgUnitId);
                    return;
                }

                console.log('✅ [FISCAL YEAR] Found event:', event.event);

                // ✅ Extract dates using correct data element IDs
                let start = '', end = '';
                event.dataValues?.forEach(dv => {
                    if (dv.dataElement === 'U55MqwBcRAe') {  // FIN_START
                        start = dv.value;
                        console.log('📅 [FISCAL YEAR] Start date found:', start);
                    }
                    if (dv.dataElement === 'kgdk4NXu4f5') {  // FIN_END
                        end = dv.value;
                        console.log('📅 [FISCAL YEAR] End date found:', end);
                    }
                });

                if (start && end) {
                    // Resolve the active fiscal year based on the stored pattern (ignoring year)
                    const resolvedFy = resolveActiveFiscalYear({ start, end });

                    // Validate fiscal year is reasonable
                    if (!isFiscalYearConfigValid(resolvedFy)) {
                        console.warn(`⚠️ [FISCAL YEAR] Resolved fiscal year is invalid or too old (${resolvedFy.start} to ${resolvedFy.end}), falling back to current FY`);
                        setFiscalYear(getFallbackFiscalYear());
                    } else {
                        setFiscalYear(resolvedFy);
                    }
                    setFiscalYearLoaded(true);
                    console.log('✅ [FISCAL YEAR] Loaded successfully:', resolvedFy);
                } else {
                    // No dates found in event, use fallback
                    setFiscalYear(getFallbackFiscalYear());
                    setFiscalYearLoaded(true);
                }
            } catch (error) {
                console.error('❌ [FISCAL YEAR] Failed to load:', error.message);
                console.error('Full error:', error);
                // Fallback to current calendar year
                setFiscalYear(getFallbackFiscalYear());
                setFiscalYearLoaded(true);
            }
        };

        loadFiscalYear();
    }, [user?.organisationUnits?.[0]?.id, engine, fiscalYearLoaded]);

    useEffect(() => {
        return () => {
            setMounted(false);
            submissionInProgress.current = false;
        };
    }, []);

    useEffect(() => {
        const checkProtectedStatus = () => {
            // Only check in edit mode
            if (mode === 'edit' && objective?.id) {
                // Check if this objective has child objectives (is protected)
                if (protectedObjectiveIds?.has(objective.id)) {
                    const childCount = childCounts?.[objective.id] || 0;
                    const reason = `This objective has ${childCount} linked subordinate objective(s) and cannot be modified. Please ask your subordinates to unlink their objectives first.`;
                    
                    setIsFormLocked(true);
                    setLockReason(reason);
                    
                    // Show alert to inform the manager
                    alert(reason);
                    
                    // Optional: Auto-close form after 3 seconds
                    setTimeout(() => {
                        if (onCancel) onCancel();
                    }, 3000);
                } else {
                    // Not protected, keep current lock state from approval check
                    // Don't override setIsFormLocked here - let approval check handle it
                }
            }
        };
        
        checkProtectedStatus();
    }, [mode, objective?.id, protectedObjectiveIds, childCounts, onCancel]);


    // Refs to prevent useEffect from re-running during typing
    const hasRunRef = useRef(false);
    const lastLoadedObjectiveIdRef = useRef(null);
    const isMountedRef = useRef(false);
    const hasLoadedAddGroupRef = useRef(false);

    useEffect(() => {
        if (mode === 'add') {
            hasLoadedAddGroupRef.current = false;
        }
    }, [mode, currentUserPosition?.trackedEntityInstance]);

    // Prevent auto-creation of empty objective when import modal is open
    useEffect(() => {
        // Only create empty objective if we're in ADD mode, no objectives exist, 
        // AND the import modal is NOT showing
        if (mode === 'add' && objectives.length === 0 && !showImportModal && !importPreview.length) {
            console.log('📝 Creating initial blank objective');
            setObjectives([createBlankObjective(0)]);
        }
        
        // If we're showing import modal and there's an empty Untitled objective, remove it
        if (showImportModal && objectives.length === 1) {
            const onlyObjective = objectives[0];
            const isEmptyUntitled = !onlyObjective.name?.trim() && onlyObjective.weight === 0 && !onlyObjective.id;
            if (isEmptyUntitled) {
                console.log('🧹 Removing empty Untitled objective before import');
                setObjectives([]);
            }
        }
    }, [mode, showImportModal, importPreview.length, objectives.length]);

    useEffect(() => {
        // Reset only when the objective id actually changes.
        if (objective?.id && lastLoadedObjectiveIdRef.current !== objective.id) {
            lastLoadedObjectiveIdRef.current = objective.id;
            hasRunRef.current = false;
        }
        isMountedRef.current = true;

        const loadExistingData = async () => {
            // Wait for position to be loaded before processing
            if (!currentUserPosition?.trackedEntityInstance) {
                console.log('🎯 [LOAD EXISTING] Waiting for position to be loaded...');
                return; // Will re-run when position changes
            }

            if (!objective || !objective.id || !isMountedRef.current || hasRunRef.current) return;
            hasRunRef.current = true;

            try {
                setUiMode('edit');

                const { objective: objectiveDetails, kras, kpis } = await fetchObjectiveWithDetails(
                    objective.id,
                    engine
                );

                //  STRICT FISCAL YEAR CHECK - BLOCK ALL USERS FROM VIEWING PAST YEAR OBJECTIVES
                const isHistorical =
                    !isWithinFiscalYear(objectiveDetails.startDate, fiscalYear) &&
                    !isWithinFiscalYear(objectiveDetails.completionDate, fiscalYear);

                // Block ALL users (including Admin/CEO) from editing historical objectives in this form
                if (isHistorical) {
                    console.warn('⚠️ [LOAD EXISTING] Objective is from past fiscal year - blocking load', {
                        objectiveId: objectiveDetails.id,
                        startDate: objectiveDetails.startDate,
                        completionDate: objectiveDetails.completionDate,
                        fiscalYear: `${fiscalYear.start} to ${fiscalYear.end}`
                    });
                    showAlert(
                        'Historical Objective',
                        'info',
                        `This objective is from a previous fiscal year and cannot be viewed or edited in this form.`
                    );
                    onCancel?.(); // Close the form
                    return; // Exit early
                }

                // ✅ STEP 1: FIRST get enrollment ID from the specific objective
                const eventResponse = await engine.query({
                    event: {
                        resource: `events/${objective.id}`,
                        params: { fields: 'event,enrollment,dataValues[dataElement,value]' }
                    }
                });

                const enrollmentId = eventResponse.event?.enrollment;
                console.log('🎯 [EDIT MODE] Objective enrollment ID:', enrollmentId);

                //  STEP 2: LOAD ENTIRE WEIGHT GROUP IF ENROLLMENT EXISTS
                let groupObjectives = [];
                if (enrollmentId) {
                    groupObjectives = await loadObjectivesInEnrollment(engine, enrollmentId);
                    setWeightGroupEnrollment(enrollmentId); // Track for saving later
                }

                //  STEP 3: PROCESS OBJECTIVES (use group if available, else single)
                const objectivesToSet = groupObjectives.length > 0
                    ? groupObjectives
                    : [await fetchObjectiveWithDetails(objective.id, engine).objective];

                // Map to form state format (critical: preserve all fields)
                const mappedObjectives = (objectivesToSet || [])
                    .filter(obj => obj !== null && obj !== undefined) // ← Add this filter
                    .map(obj => {

                    const rawDataValues = obj.rawDataValues || obj.dataValues || [];
                    const managerGoalId = rawDataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;

                    const managerGoalName = managerGoalId ? goalNameById[managerGoalId] : null;

                    return {
                        id: obj.id || null,
                        name: obj.name || '',
                        _managerGoalName: managerGoalName,
                        description: obj.description || '',
                        //  CRITICAL: Use CORRECT weight DE value
                        weight: obj.weight || parseInt(obj.rawDataValues?.find(dv => dv.dataElement === 'yfb9RCRpMB2')?.value) || 0,
                        startDate: obj.startDate || fiscalYear.start,
                        completionDate: obj.completionDate || fiscalYear.end,
                        orgUnit: obj.orgUnit || user?.organisationUnits?.[0]?.id || '',
                        remarks: obj.remarks || '',
                        score: obj.score || 0,
                        performanceYear: obj.performanceYear || new Date().getFullYear(),
                        workType: obj.workType || '',
                        companyAlignedGoal: obj.companyAlignedGoal || obj.rawDataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value || '',
                        objectiveAlignment: obj.objectiveAlignment || obj.rawDataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value || '',
                        objectiveRoleAssigned: obj.objectiveRoleAssigned || '',

                        // In loadExistingData, update the KRA mapping:
                        kras: obj.kras?.length > 0
                            ? obj.kras.map(kra => {
                                let kraName = '';
                                let kraDescription = '';
                                let kraWeight = 0;

                                if (kra.dataValues && Array.isArray(kra.dataValues)) {
                                    kra.dataValues.forEach(dv => {
                                        if (dv.dataElement === 'obapz6Gnyla') kraName = dv.value || '';
                                        if (dv.dataElement === 'DEECWPTj42E') kraDescription = dv.value || '';
                                        if (dv.dataElement === 'Xl4VEFoIFIw') kraWeight = parseInt(dv.value) || 0;
                                    });
                                } else {
                                    kraName = kra.name || '';
                                    kraDescription = kra.description || '';
                                    kraWeight = kra.weight || 0;
                                }

                                return {
                                    id: kra.id || kra.eventId || kra.event || null,
                                    name: String(kraName || '').trim(),
                                    description: String(kraDescription || '').trim(),
                                    weight: kraWeight,
                                    dataValues: kra.dataValues || [],
                                };
                            })
                            : [],

                        // Update the KPI mapping:
                        kpis: obj.kpis?.length > 0
                            ? obj.kpis.map(kpi => {
                                let kpiName = '';
                                let kpiDescription = '';
                                let kpiTarget = '';

                                if (kpi.dataValues && Array.isArray(kpi.dataValues)) {
                                    kpi.dataValues.forEach(dv => {
                                        if (dv.dataElement === 'DBmylDQA9KX') kpiName = dv.value || '';
                                        if (dv.dataElement === 'rQqF75V9t7K') kpiDescription = dv.value || '';
                                        if (dv.dataElement === 'Ru68Ax2Xo2L') kpiTarget = dv.value || '';
                                    });
                                } else {
                                    kpiName = kpi.name || '';
                                    kpiDescription = kpi.description || '';
                                    kpiTarget = kpi.target || '';
                                }

                                return {
                                    id: kpi.id || kpi.eventId || kpi.event || null,
                                    name: String(kpiName || '').trim(),
                                    description: String(kpiDescription || '').trim(),
                                    target: String(kpiTarget || '').trim(),
                                    dataValues: kpi.dataValues || [],
                                };
                            })
                            : [],
                    };
                });

                const validMappedObjectives = mappedObjectives.filter(obj => obj !== null);

                setObjectives(prev => {
                    if (!validMappedObjectives.length) return prev;
                    return validMappedObjectives.map((mappedObj, idx) => {
                        const existing = prev.find(p => p.id === mappedObj.id);
                        const hasExistingDetailedKpis = existing?.kpis?.some(k =>
                            k?.name?.trim() || k?.description?.trim() || k?.target?.trim()
                        );
                        const hasExistingDetailedKras = existing?.kras?.some(k =>
                            k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                        );

                        return {
                            ...mappedObj,
                            kras: hasExistingDetailedKras ? existing.kras : mappedObj.kras,
                            kpis: hasExistingDetailedKpis ? existing.kpis : mappedObj.kpis,
                            _tempId: existing?._tempId || mappedObj._tempId || `temp_loaded_${idx}`
                        };
                    });
                });

                // DEBUG: Verify weights are loaded correctly
                console.log('🔍 [WEIGHT LOAD DEBUG] Objectives after mapping:',
                    mappedObjectives.map(o => ({
                        id: o.id?.substring(0, 8),
                        name: o.name?.substring(0, 20),
                        weight: o.weight,
                        weightType: typeof o.weight,
                        rawData: o.rawDataValues?.find(dv => dv.dataElement === 'yfb9RCRpMB2')?.value
                    }))
                );
                const loadedTotal = mappedObjectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
                console.log(`✅ [WEIGHT LOAD DEBUG] Total loaded weight: ${loadedTotal}%`);

                // STEP 4: Restore alignment context for FIRST objective (UI consistency)
                const firstObjective = mappedObjectives[0];
                if (firstObjective?.objectiveAlignment && !selectedHigherLevelObjective?.id) {
                    try {
                        const alignedObj = await loadHigherLevelObjectiveDetails(firstObjective.objectiveAlignment);
                        if (isMountedRef.current && alignedObj) {
                            setSelectedHigherLevelObjective(alignedObj);
                            // Update form fields for alignment context
                            updateObjectiveField(0, 'objectiveAlignment', alignedObj.id);
                            updateObjectiveField(0, 'objectiveRoleAssigned', firstObjective.objectiveRoleAssigned);
                            updateObjectiveField(0, 'higherLevelObjectiveId', alignedObj.id);
                            updateObjectiveField(0, 'higherLevelObjectiveName', alignedObj.name);

                            // Set manager goal name if available
                            const alignedGoalId = alignedObj.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;
                            if (alignedGoalId && goals.length > 0) {
                                const alignedGoal = goals.find(g => g.id === alignedGoalId);
                                if (alignedGoal) {
                                    setObjectives(prev => prev.map(o => ({ ...o, _managerGoalName: alignedGoal.name })));
                                }
                            }
                        }
                    } catch (err) {
                        console.warn('⚠️ Could not restore alignment context:', err);
                    }
                }

                // Handle CEO goal linkage
                if (user?.roleIds?.includes('nLSSLch316I') && firstObjective?.companyAlignedGoal) {
                    console.log('🎯 [CEO] Restoring goal linkage:', firstObjective.companyAlignedGoal);
                    setSelectedGoal(firstObjective.companyAlignedGoal);
                }

            } catch (error) {
                console.error('❌ [LOAD EXISTING] Error:', error);
                if (isMountedRef.current) {
                    showAlert('Failed to load objective data', 'error', error.message);
                }
            }
        };

        loadExistingData();
        return () => { isMountedRef.current = false; };
    }, [objective, engine, user, currentUserPosition, , fiscalYear.start, fiscalYear.end]);

    // Add this useEffect near your other effects
    useEffect(() => {
        if (user?.organisationUnits?.[0]?.id && !programAccess.checked) {
            checkProgramAccessProactively();
        }
    }, [user, programAccess.checked]);

    // Effect to check manager permissions when editing objective
    useEffect(() => {
        const checkManagerStatus = async () => {
            if (mode === 'edit' && editingObjective?.id && currentUserPosition?.trackedEntityInstance) {
                console.log(`🎯 [EDIT MODE GROUP LOAD] Loading group for editing objective: ${editingObjective.id}`);

                //  CRITICAL FIX: Use the objective's enrollment FIRST, don't search for alternatives
                const objectiveEnrollmentId = editingObjective.enrollmentId || editingObjective.enrollment;

                if (objectiveEnrollmentId) {
                    console.log(`✅ [EDIT MODE] Using objective's enrollment directly: ${objectiveEnrollmentId}`);

                    try {
                        // Load events directly from the objective's enrollment
                        const response = await engine.query({
                            enrollmentEvents: {
                                resource: 'enrollments',
                                id: objectiveEnrollmentId,
                                params: {
                                    fields: 'events[event,orgUnit,dataValues[dataElement,value]]'
                                }
                            }
                        });

                        const enrollmentEvents = response.enrollmentEvents?.events || [];
                        console.log(`✅ Loaded ${enrollmentEvents.length} events from objective enrollment`);

                        setWeightGroupEnrollment(objectiveEnrollmentId);

                        // Process and set objectives
                        const groupedObjectives = enrollmentEvents.map(event => mapDataValuesToObjective(event));


                        // Enrich with KPIs/KRAs for each objective
                        const enrichedObjectives = await enrichObjectivesWithDetails(groupedObjectives);

                        setObjectives(prev => {
                            if (!enrichedObjectives.length) return prev;
                            return enrichedObjectives.map(groupedObj => {
                                const existingObj = prev.find(obj => obj.id === groupedObj.id);
                                // Preserve existing detailed KPIs/KRAs if they exist
                                const hasExistingDetailedKpis = existingObj?.kpis?.some(k =>
                                    k?.name?.trim() || k?.target?.trim() || k?.description?.trim()
                                );
                                const hasExistingDetailedKras = existingObj?.kras?.some(k =>
                                    k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                                );

                                if (existingObj && (hasExistingDetailedKpis || hasExistingDetailedKras)) {
                                    console.log(`🔗 [MERGE] Preserving KPIs/KRAs for objective: ${groupedObj.id}`);
                                    return { ...groupedObj, kpis: existingObj.kpis, kras: existingObj.kras };
                                }
                                return groupedObj;
                            });
                        });

                        return; // ✅ Exit early - don't fall through to fallback logic
                    } catch (error) {
                        console.error('❌ Error loading from objective enrollment:', error);
                        // Fall through to original fallback logic if direct load fails
                    }
                }
            }
        };

        checkManagerStatus();
    }, [mode, editingObjective, currentUserPosition, engine]);

   // Add this NEW useEffect to ObjectiveForm.jsx (don't remove the manager status one)
useEffect(() => {
    const loadGroupObjectives = async () => {
        // ADD MODE: Load existing objectives for new weight group
        if (mode === 'add' && currentUserPosition?.trackedEntityInstance) {
            if (hasLoadedAddGroupRef.current) return;
            
            // ✅ CRITICAL FIX: Check if we already have manager objectives loaded
            // If higherLevelObjectives has items AND they are not empty, we should NOT overwrite
            const hasManagerObjectives = higherLevelObjectives.length > 0 && !loadingHigherLevelObjectives;
            
            // Check if we're in the initial blank state
            const isInitialBlankState =
                objectives.length === 1 &&
                !objectives[0]?.id &&
                !objectives[0]?.name?.trim() &&
                getObjectiveWeight(objectives[0]) === 0;
            
            // ✅ If we have manager objectives, skip loading enrollment entirely
            if (hasManagerObjectives && isInitialBlankState) {
                console.log('🔄 [ADD MODE] Manager objectives already loaded, skipping enrollment load');
                hasLoadedAddGroupRef.current = true;
                
                // Just ensure we have a blank objective if needed
                const hasBlankObjective = objectives.some(obj => !obj.id && !obj.name?.trim());
                if (!hasBlankObjective) {
                    setObjectives(prev => [...prev, createBlankObjective(prev.length)]);
                }
                return;
            }

            if (!fiscalYear.start || !fiscalYear.end) {
                console.log('⏳ [ADD MODE] Waiting for fiscal year metadata...');
                return;
            }

            try {
                console.log(`🎯 [ADD MODE GROUP LOAD] Loading existing objectives for TEI: ${currentUserPosition.trackedEntityInstance}`);
                const enrollmentResponse = await engine.query({
                    teiEnrollments: {
                        resource: 'enrollments',
                        params: {
                            trackedEntityInstance: currentUserPosition.trackedEntityInstance,
                            program: 'yiT00XKrrHP',
                            status: 'ACTIVE',
                            fields: 'enrollment,status',
                            ouMode: 'ACCESSIBLE'
                        }
                    }
                });
                const enrollments = enrollmentResponse.teiEnrollments?.enrollments || [];
                const activeEnrollmentId = enrollments[0]?.enrollment;
                if (!activeEnrollmentId) {
                    hasLoadedAddGroupRef.current = true;
                    console.log('🟡 [ADD MODE GROUP LOAD] No active enrollment found yet.');
                    return;
                }
                
                // ✅ CRITICAL: Only load enrollment if we don't have manager objectives
                // OR if we're in a clean state (no objectives yet)
                const shouldLoadEnrollment = !hasManagerObjectives || objectives.length === 0;
                
                if (shouldLoadEnrollment) {
                    const groupedObjectives = await loadObjectivesInEnrollment(engine, activeEnrollmentId);
                    setWeightGroupEnrollment(activeEnrollmentId);
                    
                    if (groupedObjectives.length > 0) {
                        console.log(`✅ [ADD MODE GROUP LOAD] Hydrated ${groupedObjectives.length} objectives from enrollment.`);
                        
                        // ✅ Only set objectives if we don't have manager objectives
                        // or if we're in a clean state with only a blank objective
                        const hasOnlyBlank = objectives.length === 1 && !objectives[0]?.id && !objectives[0]?.name?.trim();
                        
                        if (!hasManagerObjectives || hasOnlyBlank) {
                            // Add a blank objective at the end for new entries
                            setObjectives([...groupedObjectives, createBlankObjective(groupedObjectives.length)]);
                        } else {
                            // Keep existing objectives and just add a blank one if needed
                            console.log('🔄 [ADD MODE] Preserving existing objectives, adding blank one if needed');
                            const hasBlankObjective = objectives.some(obj => !obj.id && !obj.name?.trim());
                            if (!hasBlankObjective) {
                                setObjectives(prev => [...prev, createBlankObjective(prev.length)]);
                            }
                        }
                    } else {
                        console.log('🟡 [ADD MODE GROUP LOAD] Enrollment found, but no objectives to hydrate.');
                        // If no objectives found and we have manager objectives, keep them
                        if (!hasManagerObjectives) {
                            // Only add blank if we don't have objectives
                            setObjectives([createBlankObjective(0)]);
                        }
                    }
                } else {
                    console.log('🔄 [ADD MODE] Skipping enrollment load - manager objectives already loaded');
                    // Just ensure we have a blank objective if needed
                    const hasBlankObjective = objectives.some(obj => !obj.id && !obj.name?.trim());
                    if (!hasBlankObjective && objectives.length > 0) {
                        setObjectives(prev => [...prev, createBlankObjective(prev.length)]);
                    }
                }
                hasLoadedAddGroupRef.current = true;
            } catch (error) {
                hasLoadedAddGroupRef.current = true;
                console.error('❌ [ADD MODE GROUP LOAD] Failed to hydrate objectives:', error);
            }
        }

        // EDIT MODE: Load group for editing objective (unchanged)
        if (mode === 'edit' && editingObjective?.id && currentUserPosition?.trackedEntityInstance) {

            if (!fiscalYear.start || !fiscalYear.end) return;

            const objectivesProgramId = 'yiT00XKrrHP';
            try {
                // Step 1: Get current user's enrollments
                console.log(`🔍 [LOAD GROUP] Fetching TEI enrollments for: ${currentUserPosition.trackedEntityInstance}`);
                const teiResponse = await engine.query({
                    teiEnrollments: {
                        resource: 'trackedEntityInstances',
                        id: currentUserPosition.trackedEntityInstance,
                        params: {
                            fields: 'enrollments[enrollment,program,orgUnit,status]'
                        }
                    }
                });
                const enrollments = teiResponse.teiEnrollments?.enrollments || [];
                console.log(`✅ [LOAD GROUP] Found ${enrollments.length} enrollments for TEI`);

                // Find valid enrollment for objectives program
                let validEnrollment = enrollments.find(e =>
                    e.program === objectivesProgramId && e.status === 'ACTIVE'
                );
                if (!validEnrollment) {
                    validEnrollment = enrollments.find(e => e.status === 'ACTIVE');
                }

                let enrollmentEvents = [];

                if (validEnrollment) {
                    const validEnrollmentId = validEnrollment.enrollment;
                    console.log(`✅ [LOAD GROUP] Using valid enrollment: ${validEnrollmentId} (orgUnit: ${validEnrollment.orgUnit})`);
                    const response = await engine.query({
                        enrollmentEvents: {
                            resource: 'enrollments',
                            id: validEnrollmentId,
                            params: {
                                fields: 'events[event,orgUnit,dataValues[dataElement,value]]'
                            }
                        }
                    });
                    enrollmentEvents = response.enrollmentEvents?.events || [];
                    console.log(`✅ Loaded ${enrollmentEvents.length} events from enrollment`);
                    setWeightGroupEnrollment(validEnrollmentId);
                } else {
                    console.log('🔄 [LOAD GROUP] No valid enrollment found, using program-based query...');
                    const fallbackResponse = await engine.query({
                        fallbackObjectives: {
                            resource: 'events',
                            params: {
                                filter: `program:eq:${objectivesProgramId},orgUnit:eq:${user?.organisationUnits?.[0]?.id}`,
                                fields: 'event,orgUnit,dataValues[dataElement,value],enrollment'
                            }
                        }
                    });
                    enrollmentEvents = fallbackResponse.fallbackObjectives?.events || [];
                    console.log(`✅ Fallback loaded ${enrollmentEvents.length} events`);
                }

                // STRICT FISCAL YEAR FILTER
                const filteredEvents = enrollmentEvents.filter(event => {
                    const dvMap = {};
                    event.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });

                    const startDate = dvMap['eDcVy661j9e'] || '';
                    const completionDate = dvMap['J0rjxrDKn1l'] || '';
                    
                    if (!startDate || !completionDate) {
                        return true;
                    }
                    
                    const date = new Date(startDate);
                    const fyStart = new Date(fiscalYear.start);
                    const fyEnd = new Date(fiscalYear.end);
                    
                    const overlapsFY = date <= fyEnd && new Date(completionDate) >= fyStart;
                    return overlapsFY;
                });

                console.log('✅ [GROUP FILTER] Filtered from', enrollmentEvents.length, 'to', filteredEvents.length, 'objectives');

                // Keep only the current owner's objectives
                const ownedEnrollmentEvents = filteredEvents.filter(event => {
                    const dvMap = {};
                    event.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });

                    const isOwner = dvMap['SlgO60cMK3F'] === currentUserPosition.trackedEntityInstance;
                    if (!isOwner) return false;

                    const status = String(dvMap['DL7KxNhiV0x'] || '').toLowerCase();
                    if (status === 'draft') return true;

                    const startDate = dvMap['eDcVy661j9e'] || '';
                    const completionDate = dvMap['J0rjxrDKn1l'] || '';

                    return isWithinFiscalYear(startDate, fiscalYear) ||
                        isWithinFiscalYear(completionDate, fiscalYear);
                });

                // Process and set objectives
                const groupedObjectives = ownedEnrollmentEvents.map(event =>
                    mapDataValuesToObjective(event)
                );
                console.log(`✅ Processed ${groupedObjectives.length} objectives`);

                console.log('🔍 [ENRICH] Enriching all objectives with KPI/KRA details...');
                const enrichedObjectives = await enrichObjectivesWithDetails(groupedObjectives);
                console.log('✅ [ENRICH] All objectives enriched with KPI/KRA details');

                setObjectives(prev => {
                    if (!enrichedObjectives.length) return prev;
                    return enrichedObjectives.map(groupedObj => {
                        const existingObj = prev.find(obj => obj.id === groupedObj.id);
                        const hasExistingDetailedKpis = existingObj?.kpis?.some(k =>
                            k?.name?.trim() || k?.target?.trim() || k?.description?.trim()
                        );
                        const hasExistingDetailedKras = existingObj?.kras?.some(k =>
                            k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                        );

                        if (existingObj && (hasExistingDetailedKpis || hasExistingDetailedKras)) {
                            console.log(`🔗 [MERGE] Preserving KPIs/KRAs for objective: ${groupedObj.id}`);
                            return { ...groupedObj, kpis: existingObj.kpis, kras: existingObj.kras };
                        }
                        return groupedObj;
                    });
                });
            } catch (error) {
                console.error('❌ Error loading group objectives:', error);
                // Final fallback...
            }
        }
    };

    loadGroupObjectives();
}, [mode, editingObjective?.id, currentUserPosition?.trackedEntityInstance, user?.organisationUnits?.[0]?.id, fiscalYear.start,
    fiscalYear.end, higherLevelObjectives, loadingHigherLevelObjectives]);

    // ✅ Only log when specific values change
    const totalWeight = sumObjectiveWeights(objectives);
    const objectiveCount = objectives.length;

    useEffect(() => {
        console.log('🔍 [WEIGHT GROUP DEBUG]', {
            mode,
            weightGroupEnrollment,
            objectiveCount,
            totalWeight
        });
    }, [weightGroupEnrollment, objectiveCount, totalWeight, mode]);

    useEffect(() => {
        let isMounted = true;

        const loadCompanyGoals = async () => {
            if (!user?.organisationUnits?.[0]?.id || !mounted || !reportingStructureLoaded || !currentUserPosition) return;

            setLoadingGoals(true);

            try {
                console.log('🎯 [GOALS LOADING] Starting dynamic goal loading...');

                // ✅ DYNAMIC: Find ANY org unit that has the goals program assigned
                // First, get the goals program details
                const programResponse = await engine.query({
                    goalsProgram: {
                        resource: 'programs',
                        params: {
                            filter: `id:eq:${ORG_GOALS_PROGRAM}`,
                            fields: 'id,name,organisationUnits[id,name,level,path]'
                        }
                    }
                });

                const program = programResponse.goalsProgram?.programs?.[0];
                if (!program) {
                    console.warn('⚠️ Goals program not found');
                    if (isMounted && mounted) {
                        showAlert('Company goals program not found. Please contact administrator.', 'warning');
                    }
                    return;
                }

                const assignedOrgUnits = program.organisationUnits || [];
                console.log('🔍 [GOALS PROGRAM]', {
                    programName: program.name,
                    assignedOrgUnits: assignedOrgUnits.map(ou => ({ id: ou.id, level: ou.level, name: ou.name })),
                    totalAssigned: assignedOrgUnits.length
                });

                if (assignedOrgUnits.length === 0) {
                    console.warn('⚠️ Goals program not assigned to any org unit');
                    if (isMounted && mounted) {
                        showAlert('Company goals program is not assigned to any department. Please contact administrator.', 'warning');
                    }
                    return;
                }

                // DYNAMIC: Strategy 1 - Find the highest level (strategic) org unit
                // Typically goals should be at higher levels (Level 4-6 for strategic goals)
                const strategicLevels = [4, 5, 6]; // Levels where strategic goals typically reside

                let goalOrgUnitId = null;

                // Try to find a strategic level org unit
                for (const level of strategicLevels) {
                    const strategicOU = assignedOrgUnits.find(ou => ou.level === level);
                    if (strategicOU) {
                        goalOrgUnitId = strategicOU.id;
                        console.log(`🎯 Found strategic org unit (Level ${level}):`, strategicOU.name);
                        break;
                    }
                }

                // Strategy 2: If no strategic level found, use the first assigned org unit
                if (!goalOrgUnitId && assignedOrgUnits.length > 0) {
                    goalOrgUnitId = assignedOrgUnits[0].id;
                    console.log(`🎯 Using first assigned org unit:`, assignedOrgUnits[0].name);
                }

                // Strategy 3: If still no org unit, use user's org unit (with warning)
                if (!goalOrgUnitId) {
                    goalOrgUnitId = user.organisationUnits[0].id;
                    console.warn(`⚠️ No assigned org units, falling back to user org unit:`, goalOrgUnitId);
                }

                console.log('🎯 [FINAL GOAL ORG UNIT]', {
                    id: goalOrgUnitId,
                    details: assignedOrgUnits.find(ou => ou.id === goalOrgUnitId) || { id: goalOrgUnitId, name: 'User Org Unit' }
                });

                // ✅ DYNAMIC: Load goals from the selected org unit
                const response = await engine.query({
                    goalsQuery: {
                        resource: 'events',
                        params: {
                            program: ORG_GOALS_PROGRAM,
                            programStage: ORG_GOALS_STAGE,
                            fields: 'event,orgUnit,eventDate,dataValues[dataElement,value]',
                            orgUnit: goalOrgUnitId,
                            ouMode: 'ACCESSIBLE', // Get goals from this org unit and all accessible ones
                            pageSize: 1000,
                        },
                    },
                });

                const events = response.goalsQuery?.events || [];
                console.log('✅ [GOALS LOADED] Count:', events.length);

                // Map goals with proper field mapping
                const mappedGoals = events.map(event => {
                    const dataValuesMap = {};
                    event.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });

                    return {
                        id: event.event,
                        name: dataValuesMap['HJqqfzhH614'] || dataValuesMap['zS8QIgZc0Ll'] || 'Unnamed Goal',
                        description: dataValuesMap['zS8QIgZc0Ll'] || '',
                        progress: parseInt(dataValuesMap['pOOGDRzEo98']) || 0,
                        eventDate: event.eventDate || '',
                        startDate: dataValuesMap['MH7DQVegD1j'] || '',
                        endDate: dataValuesMap['pUvJQ9vy3A3'] || '',
                        priority: dataValuesMap['Co2i5BleCcZ'] || '',
                        category: dataValuesMap['uBPW95V2VoX'] || '',
                        createdBy: dataValuesMap['nZDkNlZwX9I'] || '',
                        orgUnit: event.orgUnit,
                        // Add level info if available
                        level: assignedOrgUnits.find(ou => ou.id === event.orgUnit)?.level || 'unknown'
                    };
                });

                if (isMounted && mounted) {
                    console.log('🔍 [GOAL FILTERING DEBUG - loadCompanyGoals] fiscalYear:', fiscalYear, 'fiscalYearLoaded:', fiscalYearLoaded);
                    // Only filter if fiscalYear is fully loaded, otherwise show all goals
                    const shouldFilter = fiscalYearLoaded && fiscalYear.start && fiscalYear.end;
                    const fiscalFilteredGoals = shouldFilter
                        ? mappedGoals.filter(goal => {
                            console.log('🔍 [GOAL FILTER - loadCompanyGoals] Checking goal:', goal.name, 'eventDate:', goal.eventDate, 'startDate:', goal.startDate, 'endDate:', goal.endDate);
                            // Primary: use eventDate (DHIS2 creation date) — most authoritative
                            if (goal.eventDate) {
                                const result = isWithinFiscalYear(goal.eventDate, fiscalYear);
                                console.log('   eventDate in FY:', result);
                                return result;
                            }
                            // Fallback: check user-entered start/end dates
                            const startResult = isWithinFiscalYear(goal.startDate, fiscalYear);
                            const endResult = isWithinFiscalYear(goal.endDate, fiscalYear);
                            console.log('   startDate in FY:', startResult, 'endDate in FY:', endResult);
                            return startResult || endResult;
                        })
                        : mappedGoals;
                    setGoals(fiscalFilteredGoals);
                    console.log('✅ [GOALS SET]', fiscalFilteredGoals.length, 'goals available (filtered from', mappedGoals.length, 'total)');
                }
            } catch (error) {
                console.error('❌ Error fetching goals:', error);
                if (isMounted && mounted) {
                    showAlert('Failed to load company goals. Please contact your administrator.', 'error');
                }
            } finally {
                if (isMounted && mounted) setLoadingGoals(false);
            }
        };

        loadCompanyGoals();

        return () => {
            isMounted = false;
        };
    }, [
        user?.organisationUnits?.[0]?.id,
        engine,
        reportingStructureLoaded,
        currentUserPosition,
        mounted,
        fiscalYear,
        fiscalYearLoaded,
        ORG_GOALS_PROGRAM,
        ORG_GOALS_STAGE
    ]);


    useEffect(() => {
        let isMounted = true;

        const fetchGoals = async () => {
            if (!user?.organisationUnits?.[0]?.id || !engine) {
                if (isMounted) setLoadingGoals(false);
                return;
            }

            setLoadingGoals(true);

            try {
                // Get user's org unit to determine hierarchy
                const userOrgUnit = user.organisationUnits[0];

                // Determine Level 5 org unit ID using multiple strategies
                let goalOrgUnitId = null;

                // Strategy 1: Try to get from user's org unit path
                if (userOrgUnit.path) {
                    const pathParts = userOrgUnit.path.split('/').filter(Boolean);
                    if (pathParts.length >= 5) { // Level 5 = index 4 (0-based)
                        goalOrgUnitId = pathParts[4];
                    }
                }

                // Strategy 2: Use program metadata to find org units with goals
                if (!goalOrgUnitId) {
                    try {
                        const programResponse = await engine.query({
                            programMeta: {
                                resource: 'programs',
                                params: {
                                    filter: `id:eq:${ORG_GOALS_PROGRAM}`,
                                    fields: 'id,name,organisationUnits[id,path,level]'
                                }
                            }
                        });

                        const program = programResponse.programMeta?.programs?.[0];
                        if (program) {
                            const level5OrgUnits = program.organisationUnits?.filter(ou => ou.level === 5) || [];
                            if (level5OrgUnits.length > 0) {
                                goalOrgUnitId = level5OrgUnits[0].id;
                            }
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to get program metadata, falling back to user org unit', error);
                    }
                }

                // Strategy 3: Final fallback to user's org unit
                if (!goalOrgUnitId) {
                    goalOrgUnitId = userOrgUnit.id;
                    console.log('🔄 [FALLBACK] Using user org unit as goal org unit:', goalOrgUnitId);
                }

                console.log('🎯 Fetching goals using org unit ID:', goalOrgUnitId);


                const response = await engine.query({
                    goalsQuery: {
                        resource: 'events',
                        params: {
                            program: ORG_GOALS_PROGRAM,
                            programStage: ORG_GOALS_STAGE,
                            fields: 'event,orgUnit,eventDate,dataValues[dataElement,value]',
                            orgUnit: goalOrgUnitId,
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1000,
                        },
                    },
                });

                const events = response.goalsQuery?.events || [];
                console.log('✅ [GOALS LOADED] Count:', events.length);

                // Detailed debug logging
                events.forEach((event, i) => {
                    const nameDV = event.dataValues?.find(dv => dv.dataElement === 'HJqqfzhH614');
                    console.log(`   Goal ${i}: ID=${event.event}, Name=${nameDV?.value || 'MISSING'}, eventDate=${event.eventDate || 'MISSING'}`);
                });

                // Map goals properly
                const mappedGoals = events.map(event => {
                    const dataValuesMap = {};
                    event.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });

                    return {
                        id: event.event,
                        name: dataValuesMap['HJqqfzhH614'] || dataValuesMap['zS8QIgZc0Ll'] || 'Unnamed Goal',
                        description: dataValuesMap['zS8QIgZc0Ll'] || '',
                        progress: parseInt(dataValuesMap['pOOGDRzEo98']) || 0,
                        eventDate: event.eventDate || '',
                        startDate: dataValuesMap['MH7DQVegD1j'] || '',
                        endDate: dataValuesMap['pUvJQ9vy3A3'] || '',
                        priority: dataValuesMap['Co2i5BleCcZ'] || '',
                        category: dataValuesMap['uBPW95V2VoX'] || '',
                        orgUnit: event.orgUnit,
                    };
                });

                if (isMounted) {
                    console.log('🔍 [GOAL FILTERING DEBUG] fiscalYear:', fiscalYear, 'fiscalYearLoaded:', fiscalYearLoaded);
                    // Only filter if fiscalYear is fully loaded, otherwise show all goals
                    const shouldFilter = fiscalYearLoaded && fiscalYear.start && fiscalYear.end;
                    const fiscalFilteredGoals = shouldFilter
                        ? mappedGoals.filter(goal => {
                            console.log('🔍 [GOAL FILTER] Checking goal:', goal.name, 'eventDate:', goal.eventDate, 'startDate:', goal.startDate, 'endDate:', goal.endDate);
                            // Primary: use eventDate (DHIS2 creation date) — most authoritative
                            if (goal.eventDate) {
                                const result = isWithinFiscalYear(goal.eventDate, fiscalYear);
                                console.log('   eventDate in FY:', result);
                                return result;
                            }
                            // Fallback: check user-entered start/end dates
                            const startResult = isWithinFiscalYear(goal.startDate, fiscalYear);
                            const endResult = isWithinFiscalYear(goal.endDate, fiscalYear);
                            console.log('   startDate in FY:', startResult, 'endDate in FY:', endResult);
                            return startResult || endResult;
                        })
                        : mappedGoals;
                    setGoals(fiscalFilteredGoals);
                    console.log('✅ [GOALS SET]', fiscalFilteredGoals.length, 'goals available (filtered from', mappedGoals.length, 'total)');
                }
            } catch (error) {
                console.error('❌ Error fetching goals:', error);
                if (isMounted) {
                    // Show user-friendly error messages based on specific errors
                    let errorMessage = 'Failed to load company goals';
                    if (error.message?.includes('403') || error.message?.includes('401')) {
                        errorMessage = 'You don\'t have permission to view company goals. Please contact your administrator.';
                    } else if (error.message?.includes('404')) {
                        errorMessage = 'Company goals not found. Please contact your administrator.';
                    }
                    showAlert(errorMessage, 'error');
                }
            } finally {
                if (isMounted) setLoadingGoals(false);
            }
        };

        fetchGoals();

        return () => {
            isMounted = false;
        };
    }, [
        user?.organisationUnits?.[0]?.id,
        engine,
        ORG_GOALS_PROGRAM,
        ORG_GOALS_STAGE,
        fiscalYear,
        fiscalYearLoaded
    ]);

    // ✅ ENHANCED CONSOLIDATED: Single source of truth for goal AND objective alignment
    useEffect(() => {
        const detectAndSetAlignment = async () => {
            // Skip if we're not in edit mode or still loading
            if (!editingObjective || loadingGoals) return;

            console.log('🎯 [ALIGNMENT DETECTION] Starting detection', {
                mode,
                isCEO: user?.roleIds?.includes('nLSSLch316I'),
                hasObjectiveAlignment: !!editingObjective.objectiveAlignment,
                hasSelectedHigherLevelObjective: !!selectedHigherLevelObjective?.id,
                goalsLoaded: goals.length
            });

            // STRATEGY 1: CEO USERS - Link to goals directly
            const isCEO = user?.roleIds?.includes('nLSSLch316I');

            if (isCEO) {
                console.log('🎯 [GOAL DETECTION] User is CEO, checking for direct goal linkage');

                // Source 1: Direct companyAlignedGoal property
                let goalId = editingObjective.companyAlignedGoal;

                // Source 2: Check rawDataValues
                if (!goalId && editingObjective.rawDataValues) {
                    console.log('🎯 [GOAL DETECTION] Checking rawDataValues...');
                    const goalDataValue = editingObjective.rawDataValues.find(
                        dv => dv.dataElement === 'VhrIQQIVpB9'
                    );
                    goalId = goalDataValue?.value;
                    console.log('🎯 [GOAL DETECTION] Found in rawDataValues:', goalId);
                }

                // Set the goal if found
                if (goalId && goalId !== '') {
                    const goalExists = goals.some(g => g.id === goalId);
                    if (goalExists && selectedGoal !== goalId) {
                        console.log('🎯 [GOAL DETECTION] Setting selectedGoal for CEO:', goalId);
                        setSelectedGoal(goalId);
                        updateObjectiveField(0, 'companyAlignedGoal', goalId);
                    } else if (!goalExists) {
                        console.warn('🎯 [GOAL DETECTION] Goal ID not found in available goals:', goalId);
                    }
                } else {
                    console.log('🎯 [GOAL DETECTION] No goal assignment found for CEO objective');
                }
            }

            // ✅ STRATEGY 2: SUBORDINATE USERS - Restore manager objective alignment
            else if (editingObjective.objectiveAlignment && !selectedHigherLevelObjective?.id) {
                console.log('🔗 [SUBORDINATE] Restoring manager objective alignment:', editingObjective.objectiveAlignment);

                try {
                    // FIRST: Check if we have higherLevelObjectives loaded
                    if (higherLevelObjectives.length > 0) {
                        const existingInList = higherLevelObjectives.find(
                            obj => obj.id === editingObjective.objectiveAlignment
                        );

                        if (existingInList) {
                            console.log('✅ [SUBORDINATE] Found aligned objective in higherLevelObjectives:', existingInList.name);
                            setSelectedHigherLevelObjective(existingInList);

                            // Update form fields
                            updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', existingInList.name);

                            // Set goal context if available
                            const managerGoalId = existingInList.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (managerGoalId && goals.length > 0) {
                                const managerGoal = goals.find(g => g.id === managerGoalId);
                                if (managerGoal) {
                                    updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                }
                            }
                            return; // Done!
                        }
                    }

                    // SECOND: If not in higherLevelObjectives, check filteredManagerObjectives
                    if (filteredManagerObjectives.length > 0) {
                        const existingInFiltered = filteredManagerObjectives.find(
                            obj => obj.id === editingObjective.objectiveAlignment
                        );

                        if (existingInFiltered) {
                            console.log('✅ [SUBORDINATE] Found aligned objective in filteredManagerObjectives:', existingInFiltered.name);
                            setSelectedHigherLevelObjective(existingInFiltered);

                            updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', existingInFiltered.name);

                            // Set goal context
                            const managerGoalId = existingInFiltered.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (managerGoalId && goals.length > 0) {
                                const managerGoal = goals.find(g => g.id === managerGoalId);
                                if (managerGoal) {
                                    updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                }
                            }
                            return; // Done!
                        }
                    }

                    // THIRD: If not found anywhere, fetch it directly
                    console.log('🔍 [SUBORDINATE] Aligned objective not in lists, fetching directly...');

                    const response = await engine.query({
                        alignedObjective: {
                            resource: `events/${editingObjective.objectiveAlignment}`,
                            params: {
                                fields: 'event,dataValues[dataElement,value],createdBy,storedBy',
                                program: 'yiT00XKrrHP',
                                programStage: 'VIBtpvkpHFR'
                            }
                        }
                    });

                    if (response.alignedObjective) {
                        const objectiveData = response.alignedObjective;
                        const dataValuesMap = {};
                        objectiveData.dataValues?.forEach(dv => {
                            dataValuesMap[dv.dataElement] = dv.value;
                        });

                        // Get manager name
                        let managerName = 'Unknown Manager';
                        try {
                            if (objectiveData.storedBy) {
                                const userResponse = await engine.query({
                                    user: {
                                        resource: `users/${objectiveData.storedBy}`,
                                        params: { fields: 'displayName' }
                                    }
                                });
                                managerName = userResponse.user?.displayName || objectiveData.storedBy;
                            }
                        } catch (err) {
                            console.warn('Could not fetch manager name:', err);
                        }

                        const restoredObjective = {
                            id: objectiveData.event,
                            name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                            description: dataValuesMap['nWqW5BrcgP1'] || '',
                            createdBy: managerName,
                            userRole: dataValuesMap['vFoMLF5eZ5X'] || '',
                            objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
                            dataValues: objectiveData.dataValues || [],
                            dateCreated: objectiveData.created || new Date().toISOString(),
                            storedBy: objectiveData.storedBy
                        };

                        console.log('✅ [SUBORDINATE] Restored aligned objective directly:', restoredObjective);
                        setSelectedHigherLevelObjective(restoredObjective);

                        // Update form fields
                        updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                        updateObjectiveField(0, 'objectiveRoleAssigned', editingObjective.objectiveRoleAssigned);
                        updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                        updateObjectiveField(0, 'higherLevelObjectiveName', restoredObjective.name);

                        // Set goal context
                        const managerGoalId = dataValuesMap['VhrIQQIVpB9'];
                        if (managerGoalId && goals.length > 0) {
                            const managerGoal = goals.find(g => g.id === managerGoalId);
                            if (managerGoal) {
                                updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                console.log('🎯 [SUBORDINATE] Set manager goal name:', managerGoal.name);
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ [SUBORDINATE] Failed to restore aligned objective:', error);
                    // Create a placeholder
                    setSelectedHigherLevelObjective({
                        id: editingObjective.objectiveAlignment,
                        name: editingObjective.higherLevelObjectiveName || `Objective ${editingObjective.objectiveAlignment.substring(0, 8)}...`,
                        description: 'Unable to load aligned objective details',
                        createdBy: 'Unknown Manager',
                        isUnavailable: true,
                        error: 'Could not load aligned objective'
                    });
                }
            }
        };

        // Run detection
        detectAndSetAlignment();
    }, [
        editingObjective,
        loadingGoals,
        goals,
        user?.roleIds,
        higherLevelObjectives,
        filteredManagerObjectives,
        selectedHigherLevelObjective?.id,
        engine,
        updateObjectiveField,
    ]);

    // Call it in useEffect or before sending email
    useEffect(() => {
        if (currentUserPosition) {
            debugPositionData();
        }
    }, [currentUserPosition]);

    // Add this useEffect inside EditModeObjectiveList component
    useEffect(() => {
        // When an objective is expanded, scroll to it
        if (expandedObjectiveIndex !== null) {
            // Use a small timeout to ensure the DOM has updated
            setTimeout(() => {
                const expandedElement = document.getElementById(`objective-card-${expandedObjectiveIndex}`);
                if (expandedElement) {
                    expandedElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }, 150);
        }
    }, [expandedObjectiveIndex]);


    // Add this useEffect to handle editing objective linkage restoration
    useEffect(() => {
        const loadLinkedObjective = async () => {
            if (editingObjective?.objectiveAlignment && currentUserPosition && engine) {
                try {
                    console.log('🎯 [EDITING OBJECTIVE] Loading existing objective:', editingObjective);

                    const attributes = extractAttributes(currentUserPosition.attributes || []);
                    const isTopLevelUser = !attributes['SjP47qi3JeB'];

                    if (!isTopLevelUser && editingObjective.objectiveAlignment) {
                        console.log('🔍 [LOADING LINKED OBJECTIVE] ID:', editingObjective.objectiveAlignment);

                        const response = await engine.query({
                            linkedObjective: {
                                resource: `events/${editingObjective.objectiveAlignment}`,
                                params: {
                                    fields: 'event,dataValues[dataElement,value],createdBy',
                                    program: 'yiT00XKrrHP',
                                    programStage: 'VIBtpvkpHFR'
                                }
                            }
                        });

                        if (response.linkedObjective) {
                            const linkedObjective = response.linkedObjective;

                            // Extract data values
                            const dataValuesMap = {};
                            linkedObjective.dataValues?.forEach(dv => {
                                dataValuesMap[dv.dataElement] = dv.value;
                            });

                            // Get manager name - use fallback logic
                            let managerName = editingObjective.createdBy || 'Unknown Manager';
                            if (editingObjective.objectiveRoleAssigned && editingObjective.objectiveRoleAssigned !== 'true') {
                                managerName = editingObjective.objectiveRoleAssigned;
                            }

                            // Get goal name - handle both loaded and not loaded states
                            let goalName = 'Unknown Goal';
                            const goalId = dataValuesMap['VhrIQQIVpB9'];

                            // Find goal in goals array if available
                            if (goalId && goals.length > 0) {
                                const goal = goals.find(g => g.id === goalId);
                                if (goal) {
                                    goalName = goal.name;
                                }
                            }

                            // If we don't have goals yet, try to get from editingObjective
                            if (goalName === 'Unknown Goal' && editingObjective._managerGoalName) {
                                goalName = editingObjective._managerGoalName;
                            }

                            // Format the objective properly
                            const formattedObjective = {
                                id: linkedObjective.event,
                                name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                                description: dataValuesMap['nWqW5BrcgP1'] || '',
                                createdBy: managerName,
                                userRole: editingObjective.objectiveRoleAssigned || '',
                                objectiveRoleAssigned: editingObjective.objectiveRoleAssigned || '',
                                dataValues: linkedObjective.dataValues || [],
                                dateCreated: linkedObjective.created || new Date().toISOString(),
                                _managerGoalName: goalName
                            };

                            console.log('✅ [LINKED OBJECTIVE LOADED]', formattedObjective);
                            setSelectedHigherLevelObjective(formattedObjective);

                            // Update form fields
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', formattedObjective.name);
                            updateObjectiveField(0, '_managerGoalName', goalName);
                        }
                    }
                } catch (error) {
                    console.error('❌ [LINKED OBJECTIVE ERROR]', error);
                    showAlert('Failed to load linked objective. Please reselect.', 'warning');
                }
            }
        };

        if (editingObjective?.objectiveAlignment) {
            loadLinkedObjective();
        }
    }, [editingObjective?.objectiveAlignment]);


    const getCurrentUserPosition = async () => {
        if (!user?.id) {
            console.warn('No user found');
            return { position: null, staffOrgUnits: [], positionOrgUnit: null };
        }

        const fallbackOrgUnits = user.organisationUnits?.map(ou => ou.id) || [];

        try {
            const searchOrgUnits = fallbackOrgUnits.length ? fallbackOrgUnits.join(';') : null;
            if (!searchOrgUnits) {
                return { position: null, staffOrgUnits: fallbackOrgUnits, positionOrgUnit: null };
            }

            const { positions } = await engine.query({
                positions: {
                    resource: 'trackedEntityInstances',
                    params: {
                        trackedEntityType: 'UGGo0Ebjl0E',
                        filter: `${POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${user.id}`,
                        ou: searchOrgUnits,
                        ouMode: 'ACCESSIBLE',
                        fields: 'orgUnit,trackedEntityInstance,attributes[attribute,value]',
                        pageSize: 1,
                    },
                },
            });

            const position = positions.trackedEntityInstances?.[0] || null;
            let staffOrgUnits = fallbackOrgUnits;
            let positionOrgUnit = position?.orgUnit || null;

            // Optional: staff username logic (keep if needed)
            const staffUsernameAttr = position?.attributes?.find(
                attr => attr.attribute === 'g3RRIfDiQra'
            );

            if (staffUsernameAttr?.value) {
                try {
                    const userResponse = await engine.query({
                        staffUser: {
                            resource: 'users',
                            params: {
                                filter: `username:eq:${staffUsernameAttr.value}`,
                                fields: 'organisationUnits[id]',
                            },
                        },
                    });
                    const staffUser = userResponse.staffUser?.users?.[0];
                    if (staffUser?.organisationUnits) {
                        staffOrgUnits = staffUser.organisationUnits.map(ou => ou.id);
                    }
                } catch (err) {
                    console.error('Failed to fetch staff user:', err);
                }
            }

            return { position, positionOrgUnit, staffOrgUnits };
        } catch (error) {
            console.error('Error fetching current user position:', error);
            return { position: null, positionOrgUnit: null, staffOrgUnits: fallbackOrgUnits };
        }
    };

    // Replace your current position loading useEffect with this
    useEffect(() => {
        const loadPosition = async () => {
            if (!user?.id || !mounted || !engine) return;

            try {
                // Get current user position directly
                const response = await engine.query({
                    currentUserPosition: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: 'UGGo0Ebjl0E', // Position TE type
                            filter: `V66IFsw5kq6:eq:${user.id}`, // Current Incumbent Staff ID
                            fields: 'trackedEntityInstance,attributes[attribute,value]',
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1
                        }
                    }
                });

                const position = response.currentUserPosition?.trackedEntityInstances?.[0];
                if (!mounted || !position) return;

                setCurrentUserPosition(position);

                // Extract attributes to determine if top-level user
                const attributes = {};
                position.attributes?.forEach(attr => {
                    attributes[attr.attribute] = attr.value;
                });

                const hasManager = !!attributes['SjP47qi3JeB'];
                setIsTopLevelUser(!hasManager);
                setReportingStructureLoaded(true);

                console.log('🎯 [POSITION LOADED] User is top-level:', !hasManager);
            } catch (error) {
                console.error('❌ Failed to load position ', error);
                if (mounted) setReportingStructureLoaded(true);
            }
        };

        loadPosition();
    }, [user?.id, mounted, engine]);


    useEffect(() => {
        const loadPosition = async () => {
            const result = await getCurrentUserPosition();
            setCurrentUserPosition(result.position);
        };
        if (user?.id) {
            loadPosition();
        }
    }, [user?.id, engine]);

    // Update the alignment detection
    useEffect(() => {
        // Only create placeholder if we have alignment data BUT no selected objective
        if (editingObjective && editingObjective.objectiveAlignment && !selectedHigherLevelObjective?.id) {
            const placeholder = {
                id: editingObjective.objectiveAlignment,
                name: `Objective ${editingObjective.objectiveAlignment}`,
                description: 'Loading alignment details...',
                userRole: editingObjective.objectiveRoleAssigned,
                creatorRole: editingObjective.objectiveRoleAssigned,
                isLoading: true
            };

            setSelectedHigherLevelObjective(placeholder);

            if (loadHigherLevelObjectiveDetails) {
                loadHigherLevelObjectiveDetails(editingObjective.objectiveAlignment);
            }
        }



        console.log('🔍 CURRENT USER POSITION ATTRIBUTES:', currentUserPosition?.attributes);
        const reportingTo = currentUserPosition?.attributes?.find(a => a.attribute === 'SjP47qi3JeB');
        console.log('🔍 REPORTING TO (SjP47qi3JeB):', reportingTo?.value);


        if (isTopLevelUser && editingObjective?.companyAlignedGoal) {

        }
    }, [editingObjective, selectedHigherLevelObjective, loadHigherLevelObjectiveDetails, setSelectedHigherLevelObjective, user]);

    // ✅ RESTORE ALIGNMENT ON EDIT — POSITION-AWARE
    useEffect(() => {
        if (editingObjective?.objectiveAlignment && !selectedHigherLevelObjective) {
            // Create placeholder to preserve UI state
            const placeholder = {
                id: editingObjective.objectiveAlignment,
                name: editingObjective.higherLevelObjectiveName
                    || `Objective ${editingObjective.objectiveAlignment.substring(0, 8)}...`,
                description: editingObjective.description || 'Loading alignment details...',
                userRole: editingObjective.objectiveRoleAssigned,
                creatorRole: editingObjective.objectiveRoleAssigned,
                isLoading: true,
            };
            setSelectedHigherLevelObjective(placeholder);

            // Try to reload full details (even if not in current manager list)
            if (loadHigherLevelObjectiveDetails) {
                loadHigherLevelObjectiveDetails(editingObjective.objectiveAlignment)
                    .then(fullObj => {
                        if (fullObj) {
                            setSelectedHigherLevelObjective(fullObj);
                        } else {
                            // Mark as deleted/unavailable if not found
                            setSelectedHigherLevelObjective(prev => ({
                                ...prev,
                                isDeleted: true,
                                error: 'Higher-level objective not found or inaccessible.'
                            }));
                        }
                    })
                    .catch(err => {
                        console.error('Failed to reload aligned objective:', err);
                        setSelectedHigherLevelObjective(prev => ({
                            ...prev,
                            isDeleted: true,
                            error: 'Failed to load aligned objective.'
                        }));
                    });
            }
        }
    }, [editingObjective, selectedHigherLevelObjective, setSelectedHigherLevelObjective, loadHigherLevelObjectiveDetails]);


    // Auto-fill fiscal year dates when metadata is available
    useEffect(() => {
        // Wait until we have basic user/org context AND fiscal year loaded
        if (!user?.organisationUnits?.[0]?.id || !fiscalYear.start || !fiscalYear.end) return;

        // Only fill if dates are still empty (don't override user edits)
        setObjectives(prev => {
            const needsUpdate = prev.some(obj =>
                !obj.startDate || !obj.completionDate || !obj.orgUnit
            );

            if (!needsUpdate) return prev;

            return prev.map(obj => ({
                ...obj,
                startDate: obj.startDate || fiscalYear.start,
                completionDate: obj.completionDate || fiscalYear.end,
                orgUnit: obj.orgUnit || user.organisationUnits[0].id,
            }));
        });
    }, [fiscalYear.start, fiscalYear.end, user?.organisationUnits?.[0]?.id]); // ✅ Specific dependencies


    useEffect(() => {

        if (editingObjective?.objectiveAlignment && editingObjective?.objectiveRoleAssigned) {
            console.log('🟡 [ALIGNMENT DETECTION] Backup: Found alignment data in useEffect');
        }
    }, [editingObjective?.objectiveAlignment, editingObjective?.objectiveRoleAssigned]);

    useEffect(() => {
        const checkInitialAccess = async () => {
            if (user?.organisationUnits?.[0]?.id) {
                const orgUnitId = user.organisationUnits[0].id;
                try {
                    const response = await engine.query({
                        programAssignment: {
                            resource: `programs/yiT00XKrrHP`,
                            params: {
                                fields: 'organisationUnits[id]',
                                filter: `organisationUnits.id:eq:${orgUnitId}`
                            }
                        }
                    });

                    const hasAssignment = response.programAssignment.organisationUnits?.length > 0;

                    if (!hasAssignment) {
                        console.warn('⚠️ Program not assigned to user org unit:', orgUnitId);
                        // You can show a warning here if you want
                    }
                } catch (error) {
                    console.error('Initial program access check failed:', error);
                }
            }
        };

        if (mode === 'add') {
            checkInitialAccess();
        }
    }, [user, mode, engine]);


useEffect(() => {
    const loadManagerObjectives = async () => {
        setLoadingHigherLevelObjectives(true);
        try {
            if (!currentUserPosition?.trackedEntityInstance) {
                console.log('⚠️ [LOAD MANAGER] No current user position');
                setHigherLevelObjectives([]);
                return;
            }

            const attributes = extractAttributes(currentUserPosition.attributes);
            const managerPositionTei = attributes['SjP47qi3JeB']; // REPORTING_TO UID
            
            console.log('🔍 [LOAD MANAGER] Current user position TEI:', currentUserPosition.trackedEntityInstance);
            console.log('🔍 [LOAD MANAGER] Manager position TEI (REPORTING_TO):', managerPositionTei);
            
            if (!managerPositionTei) {
                console.log('⚠️ [LOAD MANAGER] No manager found - user is top level');
                setHigherLevelObjectives([]);
                return;
            }

            // ✅ FIX: Use a broader query and filter client-side
            // The DHIS2 API sometimes doesn't respect filters properly for data elements
            const response = await engine.query({
                managerObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        fields: 'event,orgUnit,eventDate,storedBy,dataValues[dataElement,value]',
                        pageSize: 500,
                        // Remove the filter and query all accessible objectives
                    },
                },
            });

            const allEvents = response.managerObjectives?.events || [];
            console.log(`✅ [LOAD MANAGER] Loaded ${allEvents.length} total events`);

            // ✅ CRITICAL FIX: Filter client-side to ONLY get manager's objectives
            const managerEvents = allEvents.filter(event => {
                const dataValuesMap = {};
                event.dataValues?.forEach(dv => {
                    dataValuesMap[dv.dataElement] = dv.value;
                });
                
                // Get the position TEI from the objective
                const positionTei = dataValuesMap['SlgO60cMK3F'];
                
                // ✅ Only keep objectives where the position TEI matches the manager's position
                const isManagerObjective = positionTei === managerPositionTei;
                
                if (isManagerObjective) {
                    console.log('✅ [MANAGER OBJECTIVE FOUND]', {
                        eventId: event.event,
                        name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed',
                        positionTei: positionTei,
                        managerPositionTei: managerPositionTei
                    });
                }
                
                return isManagerObjective;
            });

            console.log(`✅ [LOAD MANAGER] Filtered to ${managerEvents.length} manager objectives (position: ${managerPositionTei})`);

            // If no objectives found, check what's available
            if (managerEvents.length === 0) {
                // Log all unique position TEIs found
                const uniquePositions = new Set();
                allEvents.forEach(event => {
                    const dataValuesMap = {};
                    event.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });
                    const positionTei = dataValuesMap['SlgO60cMK3F'];
                    if (positionTei) uniquePositions.add(positionTei);
                });
                
                console.log('🔍 [LOAD MANAGER] Available position TEIs in objectives:', Array.from(uniquePositions));
                console.log('🔍 [LOAD MANAGER] Looking for:', managerPositionTei);
                
                // Show a user-friendly message
                showAlert(
                    'No Manager Objectives Found',
                    'info',
                    `Your manager (${managerPositionTei}) has not created any objectives yet. Please check back later.`
                );
                
                setHigherLevelObjectives([]);
                return;
            }

            // Process the manager objectives
            const usernames = [...new Set(managerEvents.map(e => e.storedBy).filter(Boolean))];
            const userMap = {};
            
            if (usernames.length > 0) {
                try {
                    const userResponse = await engine.query({
                        users: {
                            resource: 'users',
                            params: {
                                filter: usernames.map(u => `username:eq:${u}`).join(';'),
                                fields: 'username,displayName'
                            }
                        }
                    });
                    (userResponse.users?.users || []).forEach(u => {
                        userMap[u.username] = u.displayName;
                    });
                } catch (err) {
                    console.warn('Failed to resolve user display names:', err);
                }
            }

            // Map manager objectives
            const managerObjectives = managerEvents.map(event => {
                const baseObj = mapDataValuesToObjective(event);
                
                // Get the manager's name
                let managerName = userMap[event.storedBy] || event.storedBy || 'Unknown';
                
                // Try to get from position if storedBy not available
                if (managerName === 'Unknown' || managerName === event.storedBy) {
                    try {
                        // Get the manager's position details
                        const positionAttrs = extractAttributes(currentUserPosition.attributes || []);
                        const managerPosTei = positionAttrs['SjP47qi3JeB'];
                        // You might need to fetch the position details here
                    } catch (err) {
                        console.warn('Could not get manager name from position:', err);
                    }
                }

                return {
                    ...baseObj,
                    id: event.event,
                    name: baseObj.name || 'Unnamed Objective',
                    description: baseObj.description || '',
                    createdBy: managerName,
                    storedBy: event.storedBy,
                    dataValues: event.dataValues || [],
                    companyAlignedGoal: baseObj.companyAlignedGoal,
                    objectiveAlignment: baseObj.objectiveAlignment,
                };
            });

            console.log('✅ [LOAD MANAGER] Processed manager objectives:', managerObjectives.length);
            
            // Filter by fiscal year
            const fiscalFilteredObjectives = fiscalYear.start && fiscalYear.end
                ? managerObjectives.filter(obj => {
                    const startInFy = isWithinFiscalYear(obj.startDate, fiscalYear);
                    const endInFy = isWithinFiscalYear(obj.completionDate, fiscalYear);
                    return startInFy && endInFy;
                })
                : managerObjectives;
                
            console.log('✅ [HIGHER-LEVEL OBJ] Filtered to', fiscalFilteredObjectives.length, 'of', managerObjectives.length, 'for current fiscal year');
            
            setHigherLevelObjectives(fiscalFilteredObjectives);

        } catch (error) {
            console.error('❌ Failed to load manager objectives:', error);
            setHigherLevelObjectives([]);
            showAlert('Failed to load higher-level objectives', 'error');
        } finally {
            setLoadingHigherLevelObjectives(false);
        }
    };

    if (currentUserPosition) {
        loadManagerObjectives();
    } else {
        setLoadingHigherLevelObjectives(false);
    }
}, [currentUserPosition, engine, fiscalYear.start, fiscalYear.end]);

    // Helper function to check if user can edit remarks
    const shouldShowRemarks = () => {
        return mode === 'edit'; // Only show remarks field in edit mode
    };

    const canEditRemarks = () => {
        // Only direct manager can edit
        return isUserDirectManager;
    };

    // Helper function to get user's position title/name
    const getUserPositionTitle = () => {
        if (!currentUserPosition?.attributes) return null;

        const attributes = extractAttributes(currentUserPosition.attributes);

        // Look for position name/title attribute
        return attributes['w3bwqUZoc6V'] || // Example: Position Name attribute
            attributes['g3RRIfDiQra'] || // Staff Username
            'User Position';
    };

    // Helper function to display permission context

    const renderPermissionContext = () => {
        if (mode !== 'edit') return null;

        const isCreator = isObjectiveCreator();
        const isManager = isUserDirectManager;
        const positionTitle = getUserPositionTitle();

        let message = '';
        let color = colors.grey600;
        let icon = <IconView16 />; // default icon as React node

        if (isCreator && !isManager) {
            message = `You are the objective creator (${positionTitle || 'Creator'}). You can view but not edit remarks.`;
            color = colors.blue700;
            icon = <span role="img" aria-label="clipboard"> <ClipboardList size={16} /> </span>;
        } else if (isManager) {
            message = `You are the direct manager of the objective creator. You can edit remarks for feedback.`;
            color = colors.green700;
            icon = <span role="img" aria-label="pencil"> <Pencil size={16} /> </span>;
        } else if (isCreator && isManager) {
            message = `You are the manager. You can edit remarks.`;
            color = colors.purple700;
            icon = <span role="img" aria-label="crown"> <Crown size={16} /> </span>;
        } else {
            message = `You have view-only access to remarks.`;
            color = colors.grey600;
            icon = <IconView16 />;
        }

        return (
            <div style={{
                padding: '8px 12px',
                backgroundColor: `${color}10`,
                border: `1px solid ${color}30`,
                borderRadius: '4px',
                color: color,
                fontSize: '0.85rem',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span>{icon}</span>
                <span>{message}</span>
            </div>
        );
    };


   const sendParentObjectiveNotification = async (savedObjective, parentObjectiveId, actionType = 'new') => {
    // actionType can be: 'new', 'updated', 'returned', 'resubmitted'

    const notificationKey = `email_sent_${savedObjective.id}_${parentObjectiveId}_${actionType}`;
    const alreadySent = sessionStorage.getItem(notificationKey);
    
    console.log('📧 [EMAIL] ===== STARTING EMAIL DEBUG =====');
    console.log('📧 [EMAIL] Notification key:', notificationKey);
    console.log('📧 [EMAIL] Already sent check:', alreadySent);
    
    if (alreadySent) {
        console.log('🔄 [EMAIL] Notification already sent, skipping');
        return { success: true, message: 'Notification already sent', duplicate: true };
    }

    console.log('🔍 [EMAIL DEBUG] Starting email notification', {
        savedObjectiveId: savedObjective.id,
        savedObjectiveName: savedObjective.name,
        savedObjectiveCreatedBy: savedObjective.createdBy,
        savedObjectiveStoredBy: savedObjective.storedBy,
        parentObjectiveId: parentObjectiveId,
        actionType: actionType
    });

    try {
        let recipient = null;
        let recipientSource = 'unknown';
        let debugInfo = {
            parentObjectiveFetched: false,
            storedByFound: null,
            createdByFound: null,
            positionTeiFound: null,
            userResolved: null,
            strategiesTried: []
        };

        // Get current user info FIRST
        const currentUserName = user?.displayName || user?.name || user?.username || 'Unknown User';
        console.log('✅ [EMAIL] Current user:', currentUserName);

        // ===== STRATEGY 1: Get creator of parent objective from storedBy =====
        if (!recipient && parentObjectiveId) {
            console.log('🔍 [EMAIL] STRATEGY 1: Trying to get creator of parent objective');
            debugInfo.strategiesTried.push('parent_objective');
            
            try {
                const parentObjectiveResponse = await engine.query({
                    parentObjective: {
                        resource: `events/${parentObjectiveId}`,
                        params: {
                            fields: 'event,storedBy,dataValues[dataElement,value]'
                        }
                    }
                });

                if (parentObjectiveResponse.parentObjective) {
                    const parent = parentObjectiveResponse.parentObjective;
                    debugInfo.parentObjectiveFetched = true;
                    
                    // Get storedBy (DHIS2 metadata - most reliable)
                    const storedByUsername = parent.storedBy;
                    console.log('🔍 [EMAIL] Parent objective storedBy:', storedByUsername);
                    debugInfo.storedByFound = storedByUsername;

                    // Get all dataValues for debugging
                    const dataValues = parent.dataValues || [];
                    console.log('🔍 [EMAIL] Parent objective dataValues:', dataValues.map(dv => ({
                        dataElement: dv.dataElement,
                        value: dv.value
                    })));

                    // Try storedBy first
                    if (storedByUsername) {
                        console.log('🔍 [EMAIL] Trying storedBy username:', storedByUsername);
                        try {
                            const userResponse = await engine.query({
                                users: {
                                    resource: 'users',
                                    params: {
                                        filter: `username:eq:${storedByUsername}`,
                                        fields: 'id,displayName,email,userCredentials[username]'
                                    }
                                }
                            });

                            recipient = userResponse.users?.users?.[0];
                            if (recipient) {
                                recipientSource = 'stored_by';
                                debugInfo.userResolved = { 
                                    method: 'stored_by', 
                                    id: recipient.id, 
                                    username: recipient.userCredentials?.username,
                                    email: recipient.email 
                                };
                                console.log('✅ [EMAIL] Found parent objective creator via storedBy:', {
                                    name: recipient.displayName,
                                    email: recipient.email,
                                    id: recipient.id
                                });
                            }
                        } catch (usernameError) {
                            console.warn('⚠️ [EMAIL] Failed to find user by storedBy username:', usernameError);
                        }
                    }

                    // If not found, try to find createdBy in dataValues
                    if (!recipient) {
                        const possibleCreatorFields = ['uOjIh0gQMS7', 'createdBy', 'creator', 'creatorId'];
                        let createdByValue = null;
                        let foundField = null;
                        
                        for (const fieldId of possibleCreatorFields) {
                            const found = dataValues.find(dv => dv.dataElement === fieldId);
                            if (found && found.value) {
                                createdByValue = found.value;
                                foundField = fieldId;
                                console.log(`🔍 [EMAIL] Found creator in dataElement ${fieldId}:`, createdByValue);
                                debugInfo.createdByFound = { fieldId, value: createdByValue };
                                break;
                            }
                        }

                        if (createdByValue) {
                            console.log('🔍 [EMAIL] Trying createdBy value:', createdByValue);
                            try {
                                const userResponse = await engine.query({
                                    users: {
                                        resource: 'users',
                                        params: {
                                            filter: `username:eq:${createdByValue}`,
                                            fields: 'id,displayName,email,userCredentials[username]'
                                        }
                                    }
                                });

                                recipient = userResponse.users?.users?.[0];
                                if (recipient) {
                                    recipientSource = 'created_by_data_element';
                                    debugInfo.userResolved = { 
                                        method: 'created_by_data_element', 
                                        id: recipient.id,
                                        username: recipient.userCredentials?.username,
                                        email: recipient.email 
                                    };
                                    console.log('✅ [EMAIL] Found parent objective creator via createdBy data element:', {
                                        name: recipient.displayName,
                                        email: recipient.email,
                                        id: recipient.id
                                    });
                                }
                            } catch (createdByError) {
                                console.warn('⚠️ [EMAIL] Failed to find user by createdBy data element:', createdByError);
                            }
                        }
                    }

                    // If still not found, try position TEI
                    if (!recipient) {
                        const positionTeiDV = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F');
                        const positionTeiId = positionTeiDV?.value;
                        
                        if (positionTeiId) {
                            console.log('🔍 [EMAIL] Position TEI (SlgO60cMK3F):', positionTeiId);
                            debugInfo.positionTeiFound = positionTeiId;
                            
                            try {
                                const positionResponse = await engine.query({
                                    position: {
                                        resource: `trackedEntityInstances/${positionTeiId}`,
                                        params: {
                                            fields: 'trackedEntityInstance,attributes[attribute,value]'
                                        }
                                    }
                                });

                                if (positionResponse.position) {
                                    const posAttrs = extractAttributes(positionResponse.position.attributes || []);
                                    const incumbentId = posAttrs['V66IFsw5kq6'];
                                    
                                    console.log('🔍 [EMAIL] Position incumbent ID:', incumbentId);
                                    
                                    if (incumbentId) {
                                        const userResponse = await engine.query({
                                            user: {
                                                resource: `users/${incumbentId}`,
                                                params: {
                                                    fields: 'id,displayName,email,userCredentials[username]'
                                                }
                                            }
                                        });
                                        
                                        if (userResponse.user) {
                                            recipient = userResponse.user;
                                            recipientSource = 'position_incumbent';
                                            debugInfo.userResolved = { 
                                                method: 'position_incumbent', 
                                                id: recipient.id,
                                                username: recipient.userCredentials?.username,
                                                email: recipient.email 
                                            };
                                            console.log('✅ [EMAIL] Found parent objective creator via position incumbent:', {
                                                name: recipient.displayName,
                                                email: recipient.email,
                                                id: recipient.id
                                            });
                                        }
                                    }
                                }
                            } catch (positionError) {
                                console.warn('⚠️ [EMAIL] Failed to get position incumbent:', positionError);
                            }
                        }
                    }
                }
            } catch (parentError) {
                console.error('❌ [EMAIL] Failed to fetch parent objective:', parentError);
            }
        }

        // ===== STRATEGY 2: Check savedObjective for creator =====
        if (!recipient && savedObjective) {
            console.log('🔍 [EMAIL] STRATEGY 2: Checking savedObjective for creator');
            debugInfo.strategiesTried.push('saved_objective');
            
            const savedCreator = savedObjective.createdBy || savedObjective.storedBy || savedObjective.creator;
            console.log('🔍 [EMAIL] savedObjective creator:', savedCreator);
            
            if (savedCreator) {
                try {
                    const userResponse = await engine.query({
                        users: {
                            resource: 'users',
                            params: {
                                filter: `username:eq:${savedCreator}`,
                                fields: 'id,displayName,email,userCredentials[username]'
                            }
                        }
                    });

                    const users = userResponse.users?.users || [];
                    if (users.length > 0) {
                        recipient = users[0];
                        recipientSource = 'saved_objective_creator';
                        debugInfo.userResolved = { 
                            method: 'saved_objective_creator', 
                            id: recipient.id,
                            username: recipient.userCredentials?.username,
                            email: recipient.email 
                        };
                        console.log('✅ [EMAIL] Found recipient via savedObjective creator:', {
                            name: recipient.displayName,
                            email: recipient.email,
                            id: recipient.id
                        });
                    }
                } catch (error) {
                    console.warn('⚠️ [EMAIL] Failed to resolve user from savedObjective:', error);
                }
            }
        }

        // ===== STRATEGY 3: Check reportingTo attribute in current position =====
        if (!recipient && currentUserPosition?.attributes) {
            console.log('🔍 [EMAIL] STRATEGY 3: Checking reportingTo attribute');
            debugInfo.strategiesTried.push('reporting_to');
            
            const attributes = extractAttributes(currentUserPosition.attributes);
            const reportingToValue = attributes['SjP47qi3JeB'];

            console.log('🔍 [EMAIL] Reporting To attribute value:', reportingToValue);

            if (reportingToValue) {
                // Try as User UID first
                console.log('🔍 [EMAIL] Trying reportingTo as User UID:', reportingToValue);
                try {
                    const userResponse = await engine.query({
                        user: {
                            resource: `users/${reportingToValue}`,
                            params: {
                                fields: 'id,displayName,email,userCredentials[username]'
                            }
                        }
                    });

                    if (userResponse.user) {
                        recipient = userResponse.user;
                        recipientSource = 'reporting_to_user_uid';
                        debugInfo.userResolved = { 
                            method: 'reporting_to_user_uid', 
                            id: recipient.id,
                            username: recipient.userCredentials?.username,
                            email: recipient.email 
                        };
                        console.log('✅ [EMAIL] Found manager via User UID:', {
                            name: recipient.displayName,
                            email: recipient.email,
                            id: recipient.id
                        });
                    }
                } catch (uidError) {
                    console.warn('⚠️ [EMAIL] User UID lookup failed:', uidError);
                }

                // If not found, try as Position TEI
                if (!recipient) {
                    console.log('🔍 [EMAIL] Trying reportingTo as Position TEI:', reportingToValue);
                    try {
                        const positionResponse = await engine.query({
                            position: {
                                resource: `trackedEntityInstances/${reportingToValue}`,
                                params: {
                                    fields: 'trackedEntityInstance,attributes[attribute,value]'
                                }
                            }
                        });

                        if (positionResponse.position) {
                            const posAttrs = extractAttributes(positionResponse.position.attributes || []);
                            const incumbentId = posAttrs['V66IFsw5kq6'];
                            
                            console.log('🔍 [EMAIL] Position incumbent ID:', incumbentId);

                            if (incumbentId) {
                                const userResponse = await engine.query({
                                    user: {
                                        resource: `users/${incumbentId}`,
                                        params: {
                                            fields: 'id,displayName,email,userCredentials[username]'
                                        }
                                    }
                                });

                                if (userResponse.user) {
                                    recipient = userResponse.user;
                                    recipientSource = 'reporting_to_position';
                                    debugInfo.userResolved = { 
                                        method: 'reporting_to_position', 
                                        id: recipient.id,
                                        username: recipient.userCredentials?.username,
                                        email: recipient.email 
                                    };
                                    console.log('✅ [EMAIL] Found manager via Position TEI:', {
                                        name: recipient.displayName,
                                        email: recipient.email,
                                        id: recipient.id
                                    });
                                }
                            }
                        }
                    } catch (positionError) {
                        console.warn('⚠️ [EMAIL] Position lookup failed:', positionError);
                    }
                }
            }
        }

        // ===== STRATEGY 4: Find by position hierarchy (full chain) =====
        if (!recipient && currentUserPosition?.trackedEntityInstance) {
            console.log('🔍 [EMAIL] STRATEGY 4: Trying position hierarchy');
            debugInfo.strategiesTried.push('position_hierarchy');
            
            try {
                const positionResponse = await engine.query({
                    position: {
                        resource: `trackedEntityInstances/${currentUserPosition.trackedEntityInstance}`,
                        params: {
                            fields: 'trackedEntityInstance,attributes[attribute,value]'
                        }
                    }
                });

                if (positionResponse.position) {
                    const positionAttrs = extractAttributes(positionResponse.position.attributes || []);
                    const reportingToTei = positionAttrs['SjP47qi3JeB'];

                    console.log('🔍 [EMAIL] Reporting to TEI:', reportingToTei);

                    if (reportingToTei) {
                        try {
                            const reportingPositionResponse = await engine.query({
                                reportingPosition: {
                                    resource: `trackedEntityInstances/${reportingToTei}`,
                                    params: {
                                        fields: 'trackedEntityInstance,attributes[attribute,value]'
                                    }
                                }
                            });

                            if (reportingPositionResponse.reportingPosition) {
                                const reportingAttrs = extractAttributes(reportingPositionResponse.reportingPosition.attributes || []);
                                const incumbentId = reportingAttrs['V66IFsw5kq6'];

                                if (incumbentId) {
                                    const userResponse = await engine.query({
                                        user: {
                                            resource: `users/${incumbentId}`,
                                            params: {
                                                fields: 'id,displayName,email,userCredentials[username]'
                                            }
                                        }
                                    });

                                    if (userResponse.user) {
                                        recipient = userResponse.user;
                                        recipientSource = 'position_hierarchy';
                                        debugInfo.userResolved = { 
                                            method: 'position_hierarchy', 
                                            id: recipient.id,
                                            username: recipient.userCredentials?.username,
                                            email: recipient.email 
                                        };
                                        console.log('✅ [EMAIL] Found via position hierarchy:', {
                                            name: recipient.displayName,
                                            email: recipient.email,
                                            id: recipient.id
                                        });
                                    }
                                }
                            }
                        } catch (hierarchyError) {
                            console.warn('⚠️ [EMAIL] Position hierarchy lookup failed:', hierarchyError);
                        }
                    }
                }
            } catch (positionError) {
                console.warn('⚠️ [EMAIL] Position lookup failed:', positionError);
            }
        }

        // ===== STRATEGY 5: LAST RESORT - Find in org unit =====
        if (!recipient) {
            console.log('🔍 [EMAIL] STRATEGY 5: Last resort - finding in org unit');
            debugInfo.strategiesTried.push('org_unit_fallback');
            
            try {
                const userResponse = await engine.query({
                    users: {
                        resource: 'users',
                        params: {
                            filter: `organisationUnits.id:eq:${savedObjective.orgUnit}`,
                            fields: 'id,displayName,email,userCredentials[username],userCredentials[userRoles[id,name]]',
                            pageSize: 20
                        }
                    }
                });

                const users = userResponse.users?.users || [];
                console.log('🔍 [EMAIL] Found users in org unit:', users.length);
                
                // Try to find a manager/CEO user
                const managerUser = users.find(u => 
                    u.userCredentials?.userRoles?.some(r => 
                        r.name?.toLowerCase().includes('manager') || 
                        r.name?.toLowerCase().includes('hod') ||
                        r.name?.toLowerCase().includes('chief') ||
                        r.name?.toLowerCase().includes('executive')
                    )
                );

                recipient = managerUser || (users.length > 0 ? users[0] : null);
                
                if (recipient) {
                    recipientSource = 'org_unit_fallback';
                    debugInfo.userResolved = { 
                        method: 'org_unit_fallback', 
                        id: recipient.id,
                        username: recipient.userCredentials?.username,
                        email: recipient.email,
                        isManager: !!managerUser 
                    };
                    console.log('✅ [EMAIL] Found recipient via org unit:', {
                        name: recipient.displayName,
                        email: recipient.email,
                        isManager: !!managerUser
                    });
                }
            } catch (fallbackError) {
                console.error('❌ [EMAIL] Fallback failed:', fallbackError);
            }
        }

        // ===== VALIDATION AND SUMMARY =====
        console.log('📧 [EMAIL] ===== RECIPIENT RESOLUTION SUMMARY =====');
        console.log('📧 [EMAIL] Recipient found:', !!recipient);
        console.log('📧 [EMAIL] Recipient source:', recipientSource);
        console.log('📧 [EMAIL] Strategies tried:', debugInfo.strategiesTried);
        console.log('📧 [EMAIL] Debug info:', debugInfo);

        if (!recipient) {
            console.warn('⚠️ [EMAIL] No recipient found');
            return { 
                success: false, 
                error: 'No recipient found',
                debugInfo 
            };
        }

        if (!recipient.email) {
            console.warn('⚠️ [EMAIL] Recipient has no email:', recipient);
            return { 
                success: false, 
                error: 'Recipient has no email',
                recipient: { id: recipient.id, displayName: recipient.displayName },
                debugInfo 
            };
        }

        console.log('✅ [EMAIL] FINAL RECIPIENT:', {
            id: recipient.id,
            displayName: recipient.displayName,
            email: recipient.email,
            username: recipient.userCredentials?.username,
            source: recipientSource
        });

        // ====== SEND EMAIL WITH CUSTOMIZED CONTENT ======
        let subject = '';
        let message = '';
        
        switch(actionType) {
            case 'new':
                subject = `[Performance] New Objective Created by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

${currentUserName} has created a new objective that reports to you.

**Objective Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
- Created By: ${currentUserName}
- Objective ID: ${savedObjective.id}

This objective has been created as part of your team's performance goals.

Please review and approve this objective in the Performance Management System.

Best regards,
Performance Management System
                `.trim();
                break;
                
            case 'updated':
                subject = `[Performance] Objective Updated by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

${currentUserName} has updated an existing objective that reports to you.

**Updated Objective Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
- Updated By: ${currentUserName}
- Objective ID: ${savedObjective.id}

Please review the changes to this objective in the Performance Management System.

Best regards,
Performance Management System
                `.trim();
                break;
                
            case 'returned':
                subject = `[Performance] Objective Returned for Revision by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

${currentUserName} has returned an objective for revision.

**Returned Objective Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
- Returned By: ${currentUserName}
- Feedback: ${savedObjective.remarks || 'No specific feedback provided'}
- Objective ID: ${savedObjective.id}

Please review the feedback and revise this objective accordingly.

Best regards,
Performance Management System
                `.trim();
                break;
                
            case 'resubmitted':
                subject = `[Performance] Objective Resubmitted by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

${currentUserName} has resubmitted a revised objective for your review.

**Resubmitted Objective Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
- Resubmitted By: ${currentUserName}
- Objective ID: ${savedObjective.id}

Please review the revised objective in the Performance Management System.

Best regards,
Performance Management System
                `.trim();
                break;
                
            case 'test':
                subject = `[TEST] Objective Action by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

This is a TEST email from the Performance Management System.

**Test Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Action Type: TEST
- Objective ID: ${savedObjective.id}

If you received this email, the email system is working correctly.

Best regards,
Performance Management System
                `.trim();
                break;
                
            default:
                subject = `[Performance] Objective Action by ${currentUserName}: ${savedObjective.name}`;
                message = `
Dear ${recipient.displayName || 'Manager'},

${currentUserName} has taken action on an objective that reports to you.

**Objective Details:**
- Name: ${savedObjective.name}
- Description: ${savedObjective.description || 'No description provided'}
- Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
- Objective ID: ${savedObjective.id}

Please check the Performance Management System for details.

Best regards,
Performance Management System
                `.trim();
        }
        
        const emailData = {
            to: recipient.email,
            subject: subject,
            message: message
        };

        console.log('📧 [EMAIL] ===== SENDING EMAIL =====');
        console.log('📧 [EMAIL] To:', emailData.to);
        console.log('📧 [EMAIL] Subject:', emailData.subject);
        console.log('📧 [EMAIL] Recipient ID:', recipient.id);
        console.log('📧 [EMAIL] Recipient Name:', recipient.displayName);
        console.log('📧 [EMAIL] Action Type:', actionType);

        // ===== TRY SENDING EMAIL =====
        try {
                // ✅ FIRST ATTEMPT: Use DHIS2 email API (external email)
                console.log('📧 [EMAIL] Attempt 1: Sending via DHIS2 email API...');
                
                try {
                    const result = await engine.mutate({
                        resource: 'email',  // ← This sends actual external emails
                        type: 'create',
                        data: {
                            to: emailData.to,
                            subject: emailData.subject,
                            message: emailData.message
                        }
                    });

                    console.log('✅ [EMAIL] Success! Sent via DHIS2 email API:', {
                        to: emailData.to,
                        subject: emailData.subject,
                        recipientName: recipient.displayName
                    });

                    // Mark as sent
                    sessionStorage.setItem(notificationKey, 'true');
                    setTimeout(() => sessionStorage.removeItem(notificationKey), 3600000);

                    return {
                        success: true,
                        method: 'email_api',
                        message: 'Email sent successfully via SMTP',
                        recipientName: recipient.displayName,
                        recipientEmail: recipient.email,
                        recipientId: recipient.id,
                        source: recipientSource,
                        debugInfo
                    };

                } catch (emailApiError) {
                    console.warn('⚠️ [EMAIL] DHIS2 email API failed:', emailApiError.message);
                    console.log('📧 [EMAIL] Attempt 2: Falling back to message conversations...');

                    // ✅ SECOND ATTEMPT: Use message conversations as fallback
                    try {
                        const result = await engine.mutate({
                            resource: 'messageConversations',
                            type: 'create',
                            data: {
                                subject: emailData.subject,
                                text: emailData.message,
                                users: [{ id: recipient.id }],
                                organisationUnits: [],
                                userGroups: []
                            }
                        });

                        console.log('✅ [EMAIL] Success! Sent via message conversations:', {
                            conversationId: result?.response?.uid || result?.uid || 'unknown',
                            recipientId: recipient.id,
                            recipientEmail: recipient.email,
                            recipientName: recipient.displayName
                        });

                        // Mark as sent
                        sessionStorage.setItem(notificationKey, 'true');
                        setTimeout(() => sessionStorage.removeItem(notificationKey), 3600000);

                        return {
                            success: true,
                            method: 'message_conversations',
                            message: 'Message sent successfully via internal messaging',
                            recipientName: recipient.displayName,
                            recipientEmail: recipient.email,
                            recipientId: recipient.id,
                            source: recipientSource,
                            debugInfo,
                            warning: 'Message was sent via internal DHIS2 messaging. User may not receive an external email.'
                        };

                    } catch (msgError) {
                        console.warn('⚠️ [EMAIL] Message conversations also failed:', msgError);
                        
                        // ... rest of error handling
                    }
                }
            } catch (error) {
            console.error('❌ [EMAIL] Critical error sending email:', error);
            console.error('❌ [EMAIL] Error stack:', error.stack);
            
            return {
                success: false,
                error: error.message,
                stack: error.stack,
                warning: 'Email notification failed'
            };
        }

    } catch (error) {
        console.error('❌ [EMAIL] Critical error in notification process:', error);
        console.error('❌ [EMAIL] Error stack:', error.stack);
        
        return {
            success: false,
            error: error.message,
            stack: error.stack,
            warning: 'Email notification failed'
        };
    }
};

    // Add this function to debug position data
    const debugPositionData = async () => {
        if (!currentUserPosition?.trackedEntityInstance) return;

        console.log('🔍 [POSITION DEBUG] Current position TEI:', currentUserPosition.trackedEntityInstance);
        console.log('🔍 [POSITION DEBUG] Current position attributes:', currentUserPosition.attributes);

        const attrs = extractAttributes(currentUserPosition.attributes || []);
        console.log('🔍 [POSITION DEBUG] Extracted attributes:', {
            reportingTo: attrs['SjP47qi3JeB'],
            incumbentId: attrs['V66IFsw5kq6'],
            positionName: attrs['w3bwqUZoc6V'] || attrs['g3RRIfDiQra']
        });

    }

    // Add this debug function to check program assignments
    const checkProgramAssignments = async () => {
        try {

            // Check if program is assigned to org unit
            const programResponse = await engine.query({
                programOrgUnits: {
                    resource: `programs/yiT00XKrrHP`,
                    params: {
                        fields: 'id,name,programType,organisationUnits[id,name]'
                    }
                }
            });

            const program = programResponse.programOrgUnits;

            // Check if our org unit is in the list
            const userOrgUnitId = user?.organisationUnits?.[0]?.id;
            const isAssigned = program.organisationUnits?.some(ou => ou.id === userOrgUnitId);


            return isAssigned;

        } catch (error) {
            console.error('Error checking program assignments:', error);
            return false;
        }
    };

    const checkIfHasChildObjectives = async (objectiveId) => {
        if (!objectiveId || !engine) return false;
        
        try {
            // Query for events that have this objective as a parent (via neuilg3L4d2 or plJovUlxfQ0)
            const response = await engine.query({
                childObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        filter: `neuilg3L4d2:eq:${objectiveId}`,
                        fields: 'event',
                        pageSize: 1
                    }
                }
            });
            
            const hasChildren = (response.childObjectives?.events?.length || 0) > 0;
            
            console.log('🔒 [CHILD CHECK] Objective', objectiveId, 'has children:', hasChildren);
            return hasChildren;
            
        } catch (error) {
            console.error('Error checking for child objectives:', error);
            return false;
        }
    };

    useEffect(() => {
        const checkLockStatus = async () => {
            if (mode === 'edit' && objective?.id && engine) {
                setIsFormLocked(true);
                setLockReason('Checking objective status...');
                
                try {
                    // Check if objective has child objectives (subordinate linked objectives)
                    const hasChildren = await checkIfHasChildObjectives(objective.id);
                    
                    if (hasChildren) {
                        const reason = `This objective has linked subordinate objective(s) and cannot be modified. Please ask your subordinates to unlink their objectives first.`;
                        setIsFormLocked(true);
                        setLockReason(reason);
                        alert(reason);
                        
                        // Auto-close form after 3 seconds
                        setTimeout(() => {
                            if (onCancel) onCancel();
                        }, 3000);
                        return;
                    }
                    
                    // Also check approval status
                    const response = await engine.query({
                        objective: {
                            resource: 'events',
                            id: objective.id,
                            params: {
                                fields: 'dataValues[dataElement,value]'
                            }
                        }
                    });
                    
                    const dataValues = response.objective?.dataValues || [];
                    const approvalStatus = dataValues.find(
                        dv => dv.dataElement === 'DzQorireRBf'
                    )?.value;
                    const regularStatus = dataValues.find(
                        dv => dv.dataElement === 'DL7KxNhiV0x'
                    )?.value;
                    
                    const isApproved = approvalStatus === 'approved' || regularStatus === 'approved';
                    
                    if (isApproved) {
                        setIsFormLocked(true);
                        setLockReason('This objective has been approved and cannot be modified.');
                    } else {
                        setIsFormLocked(false);
                        setLockReason('');
                    }
                    
                } catch (error) {
                    console.error('Lock check failed:', error);
                    setIsFormLocked(false);
                    setLockReason('');
                }
            }
        };
        
        checkLockStatus();
    }, [mode, objective?.id, engine, onCancel]);

    // Call this in your useEffect
    useEffect(() => {
        if (user?.organisationUnits?.[0]?.id) {
            checkProgramAssignments();
        }
    }, [user, engine]);

    // Add this debug function
    const checkProgramStage = async () => {
        try {
            const response = await engine.query({
                programStage: {
                    resource: 'programStages/VIBtpvkpHFR',
                    params: { fields: 'id,name,repeatable' }
                }
            });


            const response2 = await engine.query({
                programStage: {
                    resource: 'programStages/VIBtpvkpHFR',
                    params: { fields: 'id,name,repeatable' }
                }
            });

        } catch (error) {
            console.error('Error checking program stage:', error);
        }
    };

    // Call it
    useEffect(() => {
        checkProgramStage();
    }, []);



    const renderGoalSelection = () => {
    if (!currentUserPosition) return null;

    const attrs = extractAttributes(currentUserPosition.attributes);
    const isTopLevelUser = !attrs['SjP47qi3JeB'];
    if (!isTopLevelUser) return null;

    // Show a helpful summary instead of drag-and-drop cards
    if (loadingGoals) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '20px',
                justifyContent: 'center',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                marginBottom: '16px'
            }}>
                <CircularLoader small />
                <span>Loading company goals...</span>
            </div>
        );
    }

    if (goals.length === 0) {
        return (
            <div style={{
                padding: '16px',
                backgroundColor: colors.yellow050,
                border: `1px solid ${colors.yellow300}`,
                borderRadius: '4px',
                color: colors.yellow800,
                textAlign: 'center',
                marginBottom: '16px'
            }}>
                <p style={{ margin: 0 }}>
                    No company goals found. Please create goals in the Goals Management section first.
                </p>
            </div>
        );
    }

        return (
            <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#e8f5e9',
                borderRadius: '6px',
                border: `1px solid ${colors.green300}`
            }}>
                <div style={{ fontSize: '0.85rem', color: colors.green700 }}>
                    <IconCheckmark16 /> <strong>{goals.length}</strong> company goal(s) available for alignment.
                    Select one from the dropdown above for each objective.
                </div>
            </div>
        );
    };


    const renderHigherLevelObjectives = () => {
         return null;
    };



    const GoalSelectionSection = () => {
        return (
            <div style={{ marginBottom: '24px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '6px'
                }}>
                    <h3 style={{
                        margin: 0,
                        color: '#2e7d32',
                        fontSize: '1.15rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            fontWeight: 'bold'
                        }}>
                            1
                        </div>
                        <Goal size={16} /> Select Strategic Goal
                    </h3>
                    <span style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                    }}>
                        {goals.length} goals available
                    </span>
                </div>

                {loadingGoals ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularLoader small />
                        <p style={{ color: '#1565c0', marginTop: '8px' }}>Loading company goals...</p>
                    </div>
                ) : goals.length === 0 ? (
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffecb3',
                        borderRadius: '4px',
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: 0, color: '#b6890c' }}>
                            No company goals available. Please contact your administrator.
                        </p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '16px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        padding: '8px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px'
                    }}>
                        {goals.map(goal => (
                            <div
                                key={goal.id}
                                onClick={() => handleGoalSelect(goal.id)}
                                style={{
                                    padding: '16px',
                                    border: selectedGoal === goal.id ? '2px solid #2e7d32' : '1px solid #c8e6c9',
                                    borderRadius: '6px',
                                    backgroundColor: selectedGoal === goal.id ? '#e8f5e9' : '#ffffff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: selectedGoal === goal.id ? '0 2px 8px rgba(46, 125, 50, 0.2)' : '0 1px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold'
                                    }}>
                                        G
                                    </div>
                                    <strong style={{ color: '#1b5e20', fontSize: '1.1rem' }}>{goal.name}</strong>
                                </div>

                                {goal.description && goal.description !== goal.name && (
                                    <p style={{
                                        margin: '4px 0',
                                        fontSize: '0.9rem',
                                        color: '#555',
                                        lineHeight: 1.4,
                                        maxHeight: '60px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {goal.description}
                                    </p>
                                )}

                                {goal.startDate && goal.endDate && (
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#555',
                                        marginTop: '8px',
                                        fontStyle: 'italic'
                                    }}>
                                        Timeline: {new Date(goal.startDate).toLocaleDateString()} - {new Date(goal.endDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {selectedGoal && (
                    <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                    }}>
                        <p style={{ margin: '0', lineHeight: 1.5, color: '#0d47a1' }}>
                            <strong> Goal Selected:</strong> Select one to create your objective that supports this strategic direction.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const FilteredManagerObjectives = () => {
        // Only show manager objectives if a goal is selected
        if (!selectedGoal) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        fontSize: '48px',
                        color: '#9c27b0',
                        marginBottom: '10px'
                    }}><Goal size={36} /> </div>
                    <h4 style={{ color: '#2c6693', marginBottom: '8px', fontSize: '1.2rem' }}>
                        Select a Strategic Goal First
                    </h4>
                    <p style={{ color: '#555', maxWidth: '600px', margin: '0 auto' }}>
                        Choose a company goal above to see the manager objectives aligned to it.
                        Then you can select one to create your own supporting objective.
                    </p>
                </div>
            );
        }

        // Group objectives by manager name for better organization
        const groupedObjectives = filteredManagerObjectives.reduce((groups, objective) => {
            const managerName = objective.createdBy || 'Unknown Manager';
            if (!groups[managerName]) {
                groups[managerName] = [];
            }
            groups[managerName].push(objective);
            return groups;
        }, {});

        return (
            <div style={{ marginBottom: '24px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
                    <h3 style={{ margin: 0, color: '#0d47a1', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '28px', height: '28px', backgroundColor: '#2196f3', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            2
                        </div>
                        👥 Manager Objectives for Selected Goal
                    </h3>
                    <span style={{
                        backgroundColor: '#e3f2fd',
                        color: '#0d47a1',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                    }}>
                        {filteredManagerObjectives.length} objectives found
                    </span>
                </div>

                {loadingFilteredObjectives ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularLoader small />
                        <p style={{ color: '#1565c0', marginTop: '8px' }}>Loading manager objectives aligned to this goal...</p>
                    </div>
                ) : filteredManagerObjectives.length === 0 ? (
                    <div style={{
                        padding: '24px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '36px',
                            color: '#e65100',
                            marginBottom: '16px'
                        }}>⚠️</div>
                        <h4 style={{ color: '#856404', marginBottom: '8px', fontSize: '1.1rem' }}>
                            No Manager Objectives Found
                        </h4>
                        <p style={{ color: '#856404', maxWidth: '600px', margin: '0 auto' }}>
                            No manager objectives are currently aligned to this strategic goal.
                            You may need to:
                            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '24px', lineHeight: 1.5 }}>
                                <li>Select a different company goal</li>
                                <li>Contact your manager to create objectives for this goal</li>
                                <li>Check back later as objectives are added</li>
                            </ul>
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Goal context banner */}
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: '6px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '28px',
                                    height: '28px',
                                    backgroundColor: '#2e7d32',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold'
                                }}>
                                    G
                                </div>
                                <div>
                                    <strong style={{ color: '#1b5e20', fontSize: '1.1rem' }}>Strategic Goal:</strong>
                                    <p style={{
                                        margin: '6px 0 0 0',
                                        fontSize: '1.15rem',
                                        fontWeight: 'bold',
                                        color: '#2e7d32',
                                        fontStyle: 'italic'
                                    }}>
                                        "{goals.find(g => g.id === selectedGoal)?.name}"
                                    </p>
                                    <p style={{
                                        margin: '8px 0 0 0',
                                        fontSize: '0.95rem',
                                        color: '#555'
                                    }}>
                                        Select a manager objective below to create your supporting objective
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Grouped objectives by manager */}
                        {Object.entries(groupedObjectives).map(([managerName, objectives]) => (
                            <div key={managerName} style={{ marginBottom: '24px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '12px',
                                    padding: '8px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '4px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#2196f3',
                                        color: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold'
                                    }}>
                                        M
                                    </div>
                                    <strong style={{ color: '#0d47a1', fontSize: '1.05rem' }}>
                                        {managerName}'s Objectives ({objectives.length})
                                    </strong>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '16px'
                                }}>
                                    {objectives.map(objective => (
                                        <div
                                            key={objective.id}
                                            onClick={() => handleHigherLevelObjectiveSelect(objective)}
                                            style={{
                                                padding: '16px',
                                                border: selectedHigherLevelObjective?.id === objective.id ? '2px solid #2196f3' : '1px solid #bbdefb',
                                                borderRadius: '6px',
                                                backgroundColor: selectedHigherLevelObjective?.id === objective.id ? '#e3f2fd' : '#ffffff',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <div style={{
                                                    minWidth: '24px',
                                                    height: '24px',
                                                    backgroundColor: '#9c27b0',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.8rem',
                                                    flexShrink: 0
                                                }}>
                                                    M
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{
                                                        color: '#4a148c',
                                                        fontSize: '1.1rem',
                                                        display: 'block',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {objective.name}
                                                    </strong>
                                                    {objective.description && (
                                                        <p style={{
                                                            margin: '0 0 4px 0',
                                                            fontSize: '0.9rem',
                                                            color: '#555',
                                                            lineHeight: 1.4,
                                                            maxHeight: '60px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}>
                                                            {objective.description}
                                                        </p>
                                                    )}
                                                    <div style={{
                                                        fontSize: '0.8rem',
                                                        color: '#555',
                                                        marginTop: '8px',
                                                        display: 'flex',
                                                        gap: '12px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <span>👤 Owner: {objective.createdBy}</span>
                                                        <span>📅 Created: {objective.dateCreated ? new Date(objective.dateCreated).toLocaleDateString() : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Clear selection button */}
                        {selectedHigherLevelObjective && (
                            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                <Button
                                    destructive
                                    onClick={handleClearGoalSelection}
                                    style={{ padding: '8px 16px' }}
                                >
                                    Clear Selection & Choose Different Goal
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Fetch status options
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const statusResponse = await engine.query({
                    optionSet: {
                        resource: 'optionSets/U8X3RbnJrYs',
                        params: { fields: 'options[name,code]' }
                    }
                });
                setStatusOptions(statusResponse.optionSet.options);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };
        fetchInitialData();
    }, [engine]);

    useEffect(() => {
        const detectAndSetGoal = async () => {
            if (!editingObjective || loadingGoals || !goals.length) return;

            let goalId = editingObjective.companyAlignedGoal;

            // Check rawDataValues if no direct goal
            if (!goalId && editingObjective.rawDataValues) {
                const goalDataValue = editingObjective.rawDataValues.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                );
                goalId = goalDataValue?.value;
            }

            // Set goal if found and valid
            if (goalId && goals.some(g => g.id === goalId) && selectedGoal !== goalId) {
                console.log('🎯 [GOAL SET] Setting goal:', goalId);
                setSelectedGoal(goalId);

                // Update form state once
                updateObjectiveField(0, 'companyAlignedGoal', goalId);
            }
        };

        detectAndSetGoal();
    }, [editingObjective, loadingGoals, goals]); // Remove unnecessary dependencies


    useEffect(() => {
        let isMounted = true;
        let hasRun = false;

        const loadExistingData = async () => {
            if (!currentUserPosition?.trackedEntityInstance) {
                return;
            }

            if (!fiscalYear.start || !fiscalYear.end) {
                return;
            }

            if (!objective || !objective.id || !isMounted || !mounted || hasRun) return;
            hasRun = true;

            try {
                setUiMode('edit');

                // Use the unified service to fetch objective with all details
                const { objective: objectiveDetails, kras, kpis } = await fetchObjectiveWithDetails(
                    objective.id,
                    engine
                );

                if (fiscalYearLoaded && fiscalYear.start && fiscalYear.end) {
                    const isHistorical =
                        !isWithinFiscalYear(objectiveDetails.startDate, fiscalYear) &&
                        !isWithinFiscalYear(objectiveDetails.completionDate, fiscalYear);

                    if (isHistorical) {
                        console.warn('⚠️ [LOAD EXISTING] Blocking historical objective:', objectiveDetails.id);
                        showAlert('Historical Objective', 'info', 'This objective is from a previous fiscal year and cannot be viewed or edited.');
                        onCancel?.(); // Close the form
                        return; // Stop loading
                    }
                }

                if (!isMounted || !mounted) return;

                // Handle case where objectiveDetails might not have rawDataValues
                const rawDataValues = objectiveDetails.rawDataValues || objectiveDetails.dataValues || [];

                const managerGoalId = rawDataValues.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                )?.value;

                const managerGoalName = managerGoalId ? goalNameById[managerGoalId] : null;

                // Normalize detailed KPI/KRA payload once
                const normalizedKras = normalizeKraEntries(kras, objectiveDetails.id);

                const normalizedKpis = normalizeKpiEntries(kpis, objectiveDetails.id);

                console.log('🎯 [LOAD EXISTING DEBUG] Editing objective enrollment:', objectiveDetails.enrollmentId || objectiveDetails.enrollment);

                const enrollmentId = objectiveDetails.enrollmentId || objectiveDetails.enrollment;
                if (enrollmentId) {
                    try {
                        console.log('🎯 [LOAD EXISTING] Loading group from enrollment:', enrollmentId);
                        const grouped = await loadObjectivesInEnrollment(engine, enrollmentId);
                        if (grouped.length) {
                            setWeightGroupEnrollment(enrollmentId);
                            setObjectives(grouped);
                        } else {
                            console.log('⚠️ [LOAD EXISTING] Enrollment returned no objectives, falling back to single row');
                            setObjectives([{
                                id: objectiveDetails.id || null,
                                name: objectiveDetails.name || '',
                                _managerGoalName: managerGoalName,
                                description: objectiveDetails.description || '',
                                weight: objectiveDetails.weight || 0,
                                startDate: objectiveDetails.startDate || fiscalYear.start,
                                completionDate: objectiveDetails.completionDate || fiscalYear.end,
                                orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
                                remarks: objectiveDetails.remarks || '',
                                score: objectiveDetails.score || 0,
                                performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
                                workType: objectiveDetails.workType || '',
                                companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
                                objectiveAlignment: objectiveDetails.objectiveAlignment || '',
                                objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned || '',
                                kras: normalizedKras,
                                kpis: normalizedKpis,
                            }]);
                        }
                    } catch (groupError) {
                        console.error('❌ [LOAD EXISTING] Failed to hydrate group from enrollment:', groupError);
                        setObjectives([{
                            id: objectiveDetails.id || null,
                            name: objectiveDetails.name || '',
                            _managerGoalName: managerGoalName,
                            description: objectiveDetails.description || '',
                            weight: objectiveDetails.weight || 0,
                            startDate: objectiveDetails.startDate || fiscalYear.start,
                            completionDate: objectiveDetails.completionDate || fiscalYear.end,
                            orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
                            remarks: objectiveDetails.remarks || '',
                            score: objectiveDetails.score || 0,
                            performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
                            workType: objectiveDetails.workType || '',
                            companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
                            objectiveAlignment: objectiveDetails.objectiveAlignment || '',
                            objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned || '',
                            kras: normalizedKras,
                            kpis: normalizedKpis,
                        }]);
                    }
                } else {
                    setObjectives([{
                        id: objectiveDetails.id || null,
                        name: objectiveDetails.name || '',
                        _managerGoalName: managerGoalName,
                        description: objectiveDetails.description || '',
                        weight: objectiveDetails.weight || 0,
                        startDate: objectiveDetails.startDate || fiscalYear.start,
                        completionDate: objectiveDetails.completionDate || fiscalYear.end,
                        orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
                        remarks: objectiveDetails.remarks || '',
                        score: objectiveDetails.score || 0,
                        performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
                        workType: objectiveDetails.workType || '',
                        companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
                        objectiveAlignment: objectiveDetails.objectiveAlignment || '',
                        objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned || '',
                        kras: normalizedKras,
                        kpis: normalizedKpis,
                    }]);
                }

               
                // CRITICAL FIX: Handle objective alignment for non-CEO users
                if (objectiveDetails.objectiveAlignment) {
                    console.log('🎯 [LOAD EXISTING] Loading aligned objective:', objectiveDetails.objectiveAlignment);

                    try {
                        const alignedObjective = await loadHigherLevelObjectiveDetails(objectiveDetails.objectiveAlignment);

                        // ✅ ADD NULL CHECK BEFORE ACCESSING .id
                        if (isMounted && mounted && alignedObjective && alignedObjective.id) {
                            // ✅ FIX: Make sure we're setting the FULL OBJECT, not just the ID
                            console.log('✅ [LOAD EXISTING] Setting full aligned objective:', {
                                id: alignedObjective.id,
                                name: alignedObjective.name,
                                type: typeof alignedObjective
                            });

                            // Set the full object in state
                            setSelectedHigherLevelObjective(alignedObjective);

                            // Also set the goal name for the aligned objective if available
                            const alignedGoalId = alignedObjective.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (alignedGoalId && goals.length > 0) {
                                const alignedGoal = goals.find(g => g.id === alignedGoalId);
                                if (alignedGoal && isMounted && mounted) {
                                    // Update the objective with the manager goal name
                                    setObjectives(prev => prev.map(obj => ({
                                        ...obj,
                                        _managerGoalName: alignedGoal.name
                                    })));
                                }
                            }

                            // Also update form fields for consistency
                            updateObjectiveField(0, 'objectiveAlignment', alignedObjective.id);
                            updateObjectiveField(0, 'objectiveRoleAssigned', objectiveDetails.objectiveRoleAssigned);
                            updateObjectiveField(0, 'higherLevelObjectiveId', alignedObjective.id);
                            updateObjectiveField(0, 'higherLevelObjectiveName', alignedObjective.name);
                        } else if (isMounted && mounted) {
                            console.warn('⚠️ [LOAD EXISTING] Aligned objective not found or failed to load');
                            // Set a placeholder if the objective can't be loaded
                            setSelectedHigherLevelObjective({
                                id: objectiveDetails.objectiveAlignment,
                                name: `Objective ${objectiveDetails.objectiveAlignment.substring(0, 8)}...`,
                                description: 'Unable to load objective details',
                                isUnavailable: true
                            });
                        }
                    } catch (error) {
                        console.error('❌ [LOAD EXISTING] Error loading aligned objective:', error);
                        if (isMounted && mounted) {
                            // Set error placeholder
                            setSelectedHigherLevelObjective({
                                id: objectiveDetails.objectiveAlignment,
                                name: `Objective ${objectiveDetails.objectiveAlignment.substring(0, 8)}...`,
                                description: 'Error loading objective details',
                                error: error.message,
                                isUnavailable: true
                            });
                        }
                    }
                }

                // Handle goal linkage for CEO users
                if (user?.roleIds?.includes('nLSSLch316I') && objectiveDetails.companyAlignedGoal) {
                    console.log('🎯 [LOAD EXISTING] CEO user with goal:', objectiveDetails.companyAlignedGoal);
                    // Goal setting will be handled by the consolidated effect
                }

            } catch (error) {
                console.error('❌ [LOAD EXISTING] Error loading existing data:', error);
                if (isMounted && mounted) {
                    showAlert('Failed to load existing objective data', 'error', error.message);
                }
            }
        };

        loadExistingData();

        return () => {
            isMounted = false;
        };
    }, [objective, currentUserPosition?.trackedEntityInstance, fiscalYear.start, fiscalYear.end, engine, mounted]);

    const objectiveIds = useMemo(() => objectives.map(o => o.id), [objectives]);
    const kpiCounts = useMemo(() => objectives.map(o => o.kpis?.length || 0), [objectives]);
    const kraCounts = useMemo(() => objectives.map(o => o.kras?.length || 0), [objectives]);


    // Sync orgUnit with context
    useEffect(() => {
        if (currentOrgUnit?.id) {
            setObjectives(prev => {
                // Only update if orgUnit actually changed
                const needsUpdate = prev.some(obj => obj.orgUnit !== currentOrgUnit.id);
                if (!needsUpdate) return prev;

                return prev.map(obj => ({
                    ...obj,
                    orgUnit: currentOrgUnit.id
                }));
            });
        }
    }, [currentOrgUnit?.id]); // Only when ID changes

    // Handle higher-level objective selection
    // This captures BOTH:
    // 1. Direct Manager's Objective (who they report to)
    // 2. Company Goal alignment (which strategic goal supports)
    const handleHigherLevelObjectiveSelect = (objective) => {
        setSelectedHigherLevelObjective(objective);

        // Extract the company goal ID - use _goalId (which includes inherited) or fall back to dataValues
        const managerGoalId = objective._goalId ||
            objective.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;

        // Extract the parent objective ID (for linking to higher-level)
        const parentObjectiveId = objective.dataValues?.find(
            dv => dv.dataElement === 'neuilg3L4d2' || dv.dataElement === 'plJovUlxfQ0'
        )?.value;

        console.log('🔗 [OBJECTIVE SELECTION] Capturing linkage info:', {
            selectedObjectiveId: objective.id,
            selectedObjectiveName: objective.name,
            companyGoalId: managerGoalId,
            parentObjectiveId: parentObjectiveId,
            goalSource: objective._goalId ? 'inherited from parent' : 'direct alignment'
        });

        if (objectives.length > 0) {
            const targetIndex = mode === 'add' ? objectives.length - 1 : 0;
            updateObjectiveField(targetIndex, 'description', objective.description || '');

            // 1. Link to direct manager's objective
            updateObjectiveField(targetIndex, 'objectiveAlignment', objective.id);
            updateObjectiveField(targetIndex, 'objectiveRoleAssigned', objective.creatorRole || objective.userRole);

            // 2. Set the higher-level objective ID
            updateObjectiveField(targetIndex, 'higherLevelObjectiveId', objective.id);
            updateObjectiveField(targetIndex, 'higherLevelObjectiveName', objective.name);

            // 3. AUTO-POPULATE Company Goal if the selected objective has one
            if (managerGoalId) {
                console.log('🎯 [AUTO-GOAL] Setting company goal from manager objective:', managerGoalId);
                setSelectedGoal(managerGoalId);
                updateObjectiveField(targetIndex, 'companyAlignedGoal', managerGoalId);

                // Also set the manager's goal name for display
                const managerGoalName = objective._managerGoalName ||
                    goalItems.find(g => g.id === managerGoalId)?.name || 'Unknown Goal';
                updateObjectiveField(targetIndex, '_managerGoalName', managerGoalName);
            }

            updateObjectiveField(targetIndex, 'orgUnit', user?.organisationUnits?.[0]?.id || '');

            console.log('✅ [OBJECTIVE LINKAGE] Captured:', {
                reportsTo: objective.name,
                companyGoal: managerGoalId || 'Not set',
                parentObjective: parentObjectiveId || 'None (top-level)'
            });
        }
    };

    useEffect(() => {
        let isMounted = true;

        return () => {
            isMounted = false;
        };
    }, []);

    // Create goal linkage
    const createGoalLinkage = async (savedObjective, goalId) => {
        try {
            const LINKAGE_PROGRAM_STAGE = 'IEsUtu84bQU';

            if (!savedObjective.id) {
                console.warn('Cannot create linkage: objective has no ID');
                return { status: 'warning', message: 'Objective ID missing' };
            }

            const eventDate = savedObjective.startDate || new Date().toISOString().slice(0, 10);

            const linkageData = {
                program: ORG_GOALS_PROGRAM,
                programStage: LINKAGE_PROGRAM_STAGE,
                orgUnit: savedObjective.orgUnit,
                trackedEntityInstance: savedObjective.trackedEntityInstance,
                enrollment: savedObjective.enrollment,
                eventDate: eventDate,
                status: 'ACTIVE',
                dataValues: [
                    { dataElement: 'tKCq1kNpebc', value: savedObjective.id },
                    { dataElement: 'iZfRwpspva7', value: goalId },
                    { dataElement: 'ie2a6Q686VZ', value: 'i7BLYsMDzT0' }
                ]
            };

            console.log('🔍 [GOAL LINKAGE] Final linkage data:', linkageData);


            const result = await saveObjectiveEvent(linkageData, engine, false, 'linkage');
            console.log('Goal linkage created:', result);
            return result;
        } catch (error) {
            console.warn('Failed to create goal linkage (may already exist):', error);

            return { status: 'warning', message: 'Goal linkage may already exist' };
        }
    };


    useEffect(() => {
        if (editingObjective && user?.roleIds?.includes('nLSSLch316I')) {

            // ✅ FIX: Check rawDataValues instead of dataValues
            if (editingObjective.rawDataValues) {
                const goalDataValue = editingObjective.rawDataValues.find(dv =>
                    dv.dataElement === 'VhrIQQIVpB9'
                );

            }
        }
    }, [editingObjective, user]);



    useEffect(() => {
        if (editingObjective && user?.roleIds?.includes('nLSSLch316I') && !loadingGoals) {

            // Set the selectedGoal when we have goal linkage data AND goals are loaded
            if (editingObjective.companyAlignedGoal && goals.length > 0) {
                const goalExists = goals.some(g => g.id === editingObjective.companyAlignedGoal);
                if (goalExists) {
                    setSelectedGoal(editingObjective.companyAlignedGoal);
                    console.log('🎯 [GOAL SYNC] Set selectedGoal:', editingObjective.companyAlignedGoal);
                } else {
                    console.warn('🎯 [GOAL SYNC] Goal ID not found in available goals:', editingObjective.companyAlignedGoal);
                }
            }
        }
    }, [editingObjective, user, setSelectedGoal, goals, loadingGoals]); // Added goals and loadingGoals dependencies

    // Auto-fill dates for newly added objectives with empty dates
    useEffect(() => {
        // Only run if fiscal year is loaded
        if (!fiscalYear.start || !fiscalYear.end) return;

        // Check if any objectives have empty dates that should be filled
        const needsUpdate = objectives.some(obj =>
            (!obj.startDate || obj.startDate === '') &&
            (!obj.completionDate || obj.completionDate === '')
        );

        if (needsUpdate) {
            setObjectives(prev => prev.map(obj => ({
                ...obj,
                // Only fill if truly empty (not just falsy)
                startDate: (!obj.startDate || obj.startDate === '') ? fiscalYear.start : obj.startDate,
                completionDate: (!obj.completionDate || obj.completionDate === '') ? fiscalYear.end : obj.completionDate,
                orgUnit: obj.orgUnit || user?.organisationUnits?.[0]?.id || '',
            })));
        }
    }, [objectives.length, fiscalYear.start, fiscalYear.end]); // Watch objectives.length for new additions

    // Add this after your other useEffects - CLEANS UP HISTORICAL OBJECTIVES
    useEffect(() => {
        // Only run after fiscal year is loaded
        if (!fiscalYear.start || !fiscalYear.end) return;
        if (objectives.length === 0) return;
        
        // Filter out objectives that are from past fiscal years
        const currentFYObjectives = objectives.filter(obj => {
            // If objective has no dates, assume it's new (keep it)
            if (!obj.startDate || !obj.completionDate) return true;
            
            // Check if objective belongs to current fiscal year
            const startInFY = isWithinFiscalYear(obj.startDate, fiscalYear);
            const endInFY = isWithinFiscalYear(obj.completionDate, fiscalYear);
            
            // Keep if EITHER date is in current FY (allows for objectives that span FY boundary)
            // OR if it's a draft without dates
            const shouldKeep = startInFY || endInFY;
            
            if (!shouldKeep) {
                console.log(`🧹 [CLEANUP] Removing historical objective: ${obj.name} (${obj.startDate} to ${obj.completionDate})`);
            }
            
            return shouldKeep;
        });
        
        // If we filtered out any objectives, update state
        if (currentFYObjectives.length !== objectives.length) {
            console.log(`🧹 [CLEANUP] Removed ${objectives.length - currentFYObjectives.length} historical objectives`);
            setObjectives(currentFYObjectives);
            
            // Also clear any selected alignments if they were from filtered objectives
            if (currentFYObjectives.length === 0) {
                setSelectedHigherLevelObjective(null);
                setSelectedGoal('');
            }
        }
    }, [fiscalYear.start, fiscalYear.end, objectives.length]); // Runs when fiscal year loads or objectives change

    const checkExistingGoalLinkage = async (objectiveId) => {
        try {
            // Check if goal linkage already exists in the main objective data
            const response = await engine.query({
                event: {
                    resource: `events/${objectiveId}`,
                    params: {
                        fields: 'dataValues[dataElement,value]'
                    }
                }
            });

            // Look for existing goal alignment in the objective's dataValues
            const existingGoalValue = response.event.dataValues?.find(
                dv => dv.dataElement === dataElementMapping.companyAlignedGoal && dv.value
            );

            console.log('🔍 [GOAL LINKAGE CHECK]', {
                objectiveId,
                hasExistingGoal: !!existingGoalValue,
                existingGoalId: existingGoalValue?.value
            });

            return !!existingGoalValue;

        } catch (error) {
            console.error('❌ Error checking existing goal linkage:', error);
            return false; // Default to false if we can't check
        }
    };

const handleSubmit = async (e, mode = 'final') => {
        if (e?.preventDefault) {
            e.preventDefault();
        }

    const attributes = {};
        currentUserPosition?.attributes?.forEach(attr => {
            attributes[attr.attribute] = attr.value;
        });
    const isTopLevelUser = !attributes['SjP47qi3JeB'];
    const userRoleId = user?.userCredentials?.userRoles?.[0]?.id;
    const isCEO = userRoleId === 'nLSSLch316I';

    if (mode === 'edit' && objective?.id) {
        if (protectedObjectiveIds.has(objective.id)) {
            const childCount = childCounts[objective.id] || 0;
            showAlert(
                'Cannot Edit - Has Linked Objectives',
                'error',
                `This objective has ${childCount} linked subordinate objective(s) and cannot be modified. Please unlink them first.`
            );
            return;
        }
        
        // Also check approval status
        try {
            const existingObjective = await engine.query({
                objective: {
                    resource: 'events',
                    id: objective.id,
                    params: {
                        fields: 'dataValues[dataElement,value]'
                    }
                }
            });
            
            const existingDataValues = existingObjective.objective?.dataValues || [];
            const approvalStatus = existingDataValues.find(
                dv => dv.dataElement === 'DzQorireRBf'
            )?.value;
            
            if (approvalStatus === 'approved') {
                showAlert('Cannot Edit', 'error', 'This objective has been approved and is locked');
                return;
            }
        } catch (error) {
            console.error('Error checking lock status:', error);
        }
    }

    // ===== VALIDATION ONLY FOR FINAL MODE =====
    if (mode === 'final') {
        
        // ===== GOAL LINKAGE VALIDATION - MANDATORY FOR ALL USERS =====
        // ALL objectives (with content) must have goal linkages
        const objectivesWithContent = objectives.filter(obj => obj.name && obj.name.trim() !== '');
        
        // Find objectives without goal linkage
        const unlinkedObjectives = objectivesWithContent.filter(obj => {
            return !obj.companyAlignedGoal && !obj._tempGoalId;
        });
        
        if (unlinkedObjectives.length > 0) {
            const objectiveNames = unlinkedObjectives.map(o => `"${o.name}"`).join(', ');
            showAlert(
                'Cannot Finalize - Goal Linkage Required',
                'error',
                `The following objective(s) need to be linked to a company goal before finalizing:\n\n${objectiveNames}\n\nPlease expand each objective and select a goal, then try again.`
            );
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }
        
        // Check for invalid goal references
        const invalidGoalLinkages = objectivesWithContent.filter(obj => {
            const goalId = obj.companyAlignedGoal || obj._tempGoalId;
            if (!goalId) return false;
            const goalExists = goals.some(g => g.id === goalId);
            if (!goalExists) {
                console.warn(`Invalid goal ID: ${goalId} for objective "${obj.name}"`);
                return true;
            }
            return false;
        });
        
        if (invalidGoalLinkages.length > 0) {
            const objectiveNames = invalidGoalLinkages.map(o => `"${o.name}"`).join(', ');
            showAlert(
                'Invalid Goal Linkage',
                'error',
                `The following objective(s) have invalid goal references. Please reselect a goal:\n\n${objectiveNames}`
            );
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }
        
        // ===== WEIGHT VALIDATION (All users) =====
        const currentTotalWeight = objectives.reduce((sum, o) => {
            const w = parseInt(o.weight) || 0;
            return sum + w;
        }, 0);
        
        if (currentTotalWeight !== 100) {
            showAlert(
                'Weight Requirement Not Met',
                'error',
                `Total objective weight must equal exactly 100% (current: ${currentTotalWeight}%). Please adjust your objectives before finalizing.`
            );
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }
        
        // ===== KPI VALIDATION (All users) =====
        const objectivesMissingKPI = objectives.filter(obj => {
            if (!obj.name?.trim()) return false;
            return !obj.kpis?.some(k => k.name?.trim());
        });
        
        if (objectivesMissingKPI.length > 0) {
            const objectiveNames = objectivesMissingKPI.map(o => `"${o.name}"`).join(', ');
            showAlert(
                'Cannot Finalize - KPI Required',
                'error',
                `The following objective(s) need at least one KPI before finalizing:\n\n${objectiveNames}`
            );
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }
    }

    // ===== DRAFT MODE: Only basic validation (no linkage checks) =====
    if (mode === 'draft') {
        const objectivesWithContent = objectives.filter(obj => obj.name && obj.name.trim() !== '');
        
        if (objectivesWithContent.length === 0) {
            showAlert('Empty Draft', 'warning', 'No objectives with content to save. Please add at least one objective.');
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }
        
     if (isCEO || isTopLevelUser) {
            const unlinkedCount = objectivesWithContent.filter(obj => {
                return !obj.companyAlignedGoal && !obj._tempGoalId;
            }).length;
            
            if (unlinkedCount > 0) {
                // Warning only - does not block save
                console.log(`⚠️ Draft saved with ${unlinkedCount} unlinked objectives`);
            }
        }
    }

    // ===== REMOVED THE DUPLICATE VALIDATION BLOCK (lines 4410-4460 are GONE) =====

    // Prevent creating objectives for past fiscal years
    if (mode === 'add' || !editingObjective?.id) {
        const isValidFiscalYear = fiscalYear.start && fiscalYear.end;

        if (isValidFiscalYear) {
            const today = new Date();
            const fiscalEnd = new Date(fiscalYear.end);
            const isFiscalYearEnded = today > fiscalEnd;

            if (isFiscalYearEnded) {
                showAlert(
                    'Fiscal Year Ended',
                    'error',
                    `Cannot create new objectives for fiscal year ${fiscalYear.start} to ${fiscalYear.end}. This period has ended.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }
        }
    }
        const currentObjectives = [...objectives]; // Create fresh copy
        const currentTotalWeight = currentObjectives.reduce((sum, o) => {
            const w = parseInt(o.weight) || 0;
            return sum + w;
        }, 0);

        // FIX: REUSE EXISTING ENROLLMENT + FILTER BY FINANCIAL YEAR
        if (mode === 'add' && !weightGroupEnrollment) {
            try {
                console.log('🔍 [ENROLLMENT] Fetching existing active enrollment for position:', currentUserPosition?.trackedEntityInstance);

                // ALWAYS fetch existing enrollment FIRST (never create new one)
                const existingEnrollments = await engine.query({
                    enrollments: {
                        resource: 'enrollments',
                        params: {
                            trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
                            program: 'yiT00XKrrHP',
                            status: 'ACTIVE',
                            fields: 'enrollment',
                            ouMode: 'ACCESSIBLE'
                        }
                    }
                });

                const enrollmentsList = existingEnrollments.enrollments?.enrollments || [];

                if (enrollmentsList.length === 0) {
                    // CRITICAL: Only create if NONE exists (should rarely happen)
                    console.log('🔄 [ENROLLMENT] NO active enrollment found - creating FIRST enrollment');
                    const enrollmentData = {
                        trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
                        program: 'yiT00XKrrHP',
                        orgUnit: user?.organisationUnits?.[0]?.id,
                        enrollmentDate: new Date().toISOString().slice(0, 10),
                        incidentDate: new Date().toISOString().slice(0, 10),
                        status: 'ACTIVE'
                    };

                    const createResponse = await engine.mutate({
                        resource: 'enrollments',
                        type: 'create',
                        data: enrollmentData
                    });

                    let newEnrollmentId =
                        createResponse.response?.importSummaries?.[0]?.reference ||
                        createResponse.response?.enrollment ||
                        createResponse.enrollment;

                    if (!newEnrollmentId) {
                        throw new Error('Failed to obtain enrollment ID after creation');
                    }
                    setWeightGroupEnrollment(newEnrollmentId);
                    console.log('✅ [ENROLLMENT] Created FIRST enrollment:', newEnrollmentId);
                } else {
                    // CORRECT BEHAVIOR: REUSE EXISTING ENROLLMENT
                    const enrollmentId = enrollmentsList[0].enrollment;
                    setWeightGroupEnrollment(enrollmentId);
                    console.log(`✅ [ENROLLMENT] Reusing existing enrollment (${enrollmentsList.length} found):`, enrollmentId);

                    // Warn if multiple active enrollments exist (data cleanup needed)
                    if (enrollmentsList.length > 1) {
                        console.warn('⚠️ [ENROLLMENT] MULTIPLE active enrollments found! Using first one. Contact admin to clean up.');
                        showAlert(
                            'Data Warning',
                            'warning',
                            `Multiple active enrollments detected. Using first one. Contact admin to consolidate.`
                        );
                    }
                }
            } catch (err) {
                console.error('❌ [ENROLLMENT] Fatal error:', err);
                showAlert(
                    'Enrollment Error',
                    'error',
                    `Failed to initialize weight group: ${err.message || 'Unknown error'}`
                );
                return;
            }
        }

        const totalWeight = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);

        // ===== HIERARCHICAL VALIDATION =====
        if (mode === 'final') {
            
            // For CEO/Top-Level Users: Check all goals have at least one objective
            if (isCEO || isTopLevelUser) {
                // Get all goals that are currently in the system (active for current FY)
                const activeGoals = goals; // Already loaded
                
                // Get all objectives that have goal linkages
                const linkedGoalIds = new Set();
                objectives.forEach(obj => {
                    if (obj.companyAlignedGoal) {
                        linkedGoalIds.add(obj.companyAlignedGoal);
                    }
                });
                
                // Find goals without any linked objectives
                const unlinkedGoals = activeGoals.filter(goal => !linkedGoalIds.has(goal.id));
                
                if (unlinkedGoals.length > 0) {
                    const goalNames = unlinkedGoals.map(g => `• ${g.name}`).join('\n');
                    showAlert(
                        'Cannot Finalize - Unlinked Goals',
                        'error',
                        `The following company goals need at least one objective linked to them:\n\n${goalNames}\n\nPlease create objectives for these goals before finalizing.`
                    );
                    submissionInProgress.current = false;
                    setIsSubmitting(false);
                    return;
                }
                
                console.log('✅ [HIERARCHY CHECK] All goals have at least one objective linked');
            }
            
            // COMMENT OUT OR REMOVE THIS ENTIRE BLOCK - This is causing the error
            // For Staff Users: Check all manager objectives have at least one staff objective linked
            // if (!isTopLevelUser && !isCEO) {
            //     // Get all manager objectives (from higherLevelObjectives)
            //     const managerObjectives = higherLevelObjectives;
            //     
            //     // Get all staff objectives that have manager alignment
            //     const linkedManagerIds = new Set();
            //     objectives.forEach(obj => {
            //         if (obj.objectiveAlignment) {
            //             linkedManagerIds.add(obj.objectiveAlignment);
            //         }
            //     });
            //     
            //     // Find manager objectives without any staff objective linked
            //     const unlinkedManagerObjectives = managerObjectives.filter(managerObj => 
            //         !linkedManagerIds.has(managerObj.id)
            //     );
            //     
            //     if (unlinkedManagerObjectives.length > 0) {
            //         const managerObjNames = unlinkedManagerObjectives.map(m => `• ${m.name} (created by: ${m.createdBy || 'Manager'})`).join('\n');
            //         showAlert(
            //             'Cannot Finalize - Unlinked Manager Objectives',
            //             'error',
            //             `The following manager objectives need at least one staff objective linked to them:\n\n${managerObjNames}\n\nPlease create objectives aligned to these manager objectives before finalizing.`
            //         );
            //         submissionInProgress.current = false;
            //         setIsSubmitting(false);
            //         return;
            //     }
            //     
            //     console.log('✅ [HIERARCHY CHECK] All manager objectives have at least one staff objective linked');
            // }
        }

        // Use the pre-calculated currentTotalWeight
        if (mode === 'final' && currentTotalWeight !== 100) {
            showAlert(
                'Weight Requirement Not Met',
                'error',
                `Total objective weight must equal exactly 100% (current: ${currentTotalWeight}%).
            Please adjust your objectives before saving.`
            );
            submissionInProgress.current = false;
            setIsSubmitting(false);
            return;
        }

        // ===== STAFF VALIDATION FOR IMPORTED OBJECTIVES =====
        if (mode === 'final' && !isTopLevelUser) {
            // Check if there are any imported objectives that haven't been linked to a manager objective
            const unlinkedImportedObjectives = objectives.filter(obj => 
                obj._isImported && !obj.objectiveAlignment && obj.name && obj.name.trim() !== ''
            );
            
            if (unlinkedImportedObjectives.length > 0) {
                const objectiveNames = unlinkedImportedObjectives.map(o => `• ${o.name}`).join('\n');
                showAlert(
                    'Cannot Finalize - Link Manager Objectives',
                    'error',
                    `The following imported objective(s) need to be linked to a manager objective before finalizing:\n\n${objectiveNames}\n\nPlease expand each objective and select a manager objective.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }
            
            // Also check if any objective has objectiveAlignment but the manager objective no longer exists
            const invalidManagerLinks = objectives.filter(obj => 
                obj.objectiveAlignment && !higherLevelObjectives.some(h => h.id === obj.objectiveAlignment)
            );
            
            if (invalidManagerLinks.length > 0) {
                const objectiveNames = invalidManagerLinks.map(o => `• ${o.name}`).join('\n');
                showAlert(
                    'Cannot Finalize - Invalid Manager Link',
                    'error',
                    `The following objective(s) are linked to manager objectives that no longer exist in the current fiscal year:\n\n${objectiveNames}\n\nPlease re-select a manager objective for each.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }
        }

        // For draft mode: allow any weight, but warn if completely empty
        if (mode === 'draft' && currentTotalWeight === 0 && currentObjectives.every(o => !o.name?.trim())) {
            showAlert('Empty Draft', 'warning', 'No objectives with weight defined. Add at least one objective before saving as draft.');
        }

        console.log('🟢 Starting form submission');

        //  COMPREHENSIVE DUPLICATE SUBMISSION PREVENTION
        if (submissionInProgress.current) {
            console.warn('🚫 Submission already in progress — blocked duplicate');
            return;
        }

        submissionInProgress.current = true;
        setIsSubmitting(true);

        clearNotification();

        currentUserPosition?.attributes?.forEach(attr => {
            attributes[attr.attribute] = attr.value;
        });
        
        // ADD THIS BEFORE THE OBJECTIVE PROCESSING LOOP
        if (mode === 'add' && !weightGroupEnrollment) {
            showAlert('Error', 'error', 'Enrollment not initialized. Please wait a moment and try again.');
            setIsSubmitting(false);
            submissionInProgress.current = false;
            return;
        }

        // FIX: Validate each objective's alignment individually
        for (let i = 0; i < objectives.length; i++) {
            const objective = objectives[i];

            // Skip validation for existing objectives that already have alignment
            if (objective.id && objective.objectiveAlignment) {
                continue; // Already has alignment, no need to validate
            }

            // For new objectives or existing ones without alignment
            if (!isTopLevelUser && !selectedHigherLevelObjective) {
                showAlert(
                    'Validation Error',
                    'error',
                    `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires a higher-level objective alignment. Please select one before saving.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }

            if (isTopLevelUser && !selectedGoal) {
                showAlert(
                    'Validation Error',
                    'error',
                    `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires a company goal linkage. Please select a goal before saving.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }

            if (!objective.kpis?.some(k => k?.name?.trim())) {
                showAlert(
                    'Validation Error',
                    'error',
                    `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires at least one KPI.`

                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }
        }

        setSubmissionError(null);

        // Track results for better reporting
        const saveResults = {
            objectives: { created: 0, updated: 0, failed: 0 },
            kras: { success: 0, failed: 0 },
            kpis: { success: 0, failed: 0 },
            linkages: { success: 0, failed: 0 },
            notifications: { success: 0, failed: 0 }
        };

        // FIX: Track all successfully saved objective IDs and their weights
        const savedObjectiveWeights = [];

        try {
            // FIX: Process ALL objectives (both new and existing for editing)
            const processedTempIds = new Set();
            const allObjectivesToProcess = objectives.filter(obj =>
                !processedTempIds.has(obj._tempId || obj.id)
            );

            // Separate into new and existing objectives
            const newObjectives = allObjectivesToProcess.filter(obj => !obj.id);
            const existingObjectives = allObjectivesToProcess.filter(obj => obj.id);

            console.log('🎯 Saving objectives:', {
                new: newObjectives.length,
                existing: existingObjectives.length,
                total: objectives.length
            });

            submissionInProgress.current = true;
            setIsSubmitting(true);
            clearNotification();

            for (const [index, objectiveData] of allObjectivesToProcess.entries()) {
                // CRITICAL: Mark as processed IMMEDIATELY (prevents reprocessing in same submission)
                processedTempIds.add(objectiveData._tempId || objectiveData.id);

                // Get position TEI (keep existing validation)
                const positionTeiId = currentUserPosition?.trackedEntityInstance;
                if (!positionTeiId) {
                    showAlert('Error', 'error', 'Position not assigned. Contact admin.');
                    saveResults.objectives.failed++;
                    continue;
                }

                // Preserve existing alignment if it exists, otherwise use selected values
                const enhancedObjectiveData = {
                    ...objectiveData,
                    positionTeiId,
                    orgUnit: user?.organisationUnits?.[0]?.id,
                    enrollment: weightGroupEnrollment || objectiveData.enrollment,
                };

                if (isTopLevelUser) {
                    // Use the objective's own goal, not the global selectedGoal
                    const objectiveGoalId = objectiveData.companyAlignedGoal;
                    
                    if (objectiveGoalId) {
                        enhancedObjectiveData.companyAlignedGoal = objectiveGoalId;
                        const selectedGoalObj = goals.find(g => g.id === objectiveGoalId);
                        if (selectedGoalObj) {
                            enhancedObjectiveData._managerGoalName = selectedGoalObj.name;
                            enhancedObjectiveData.linkedGoalId = selectedGoalObj.id;
                            enhancedObjectiveData.linkedGoalName = selectedGoalObj.name;
                        }
                    }
                }

                const statusValue = mode === 'draft' ? 'draft' : 'pending'; // Match your Option Set codes in U8X3RbnJrYs
                enhancedObjectiveData.status = statusValue;
                enhancedObjectiveData._statusDataElement = 'DL7KxNhiV0x'; // Store DE UID for payload injection

                // Preserve existing alignment for existing objectives
                if (objectiveData.id) {
                    // This is an existing objective - preserve ALL its original alignment data
                    enhancedObjectiveData.objectiveAlignment = objectiveData.objectiveAlignment;
                    enhancedObjectiveData.companyAlignedGoal = objectiveData.companyAlignedGoal;
                    enhancedObjectiveData.higherLevelObjectiveId = objectiveData.higherLevelObjectiveId;
                    enhancedObjectiveData.higherLevelObjectiveName = objectiveData.higherLevelObjectiveName;
                    enhancedObjectiveData.linkedGoalId = objectiveData.linkedGoalId;
                    enhancedObjectiveData.linkedGoalName = objectiveData.linkedGoalName;
                    enhancedObjectiveData._managerGoalName = objectiveData._managerGoalName;

                } else {
                    // ===== THIS IS A NEW OBJECTIVE (NO ID) =====
                    
                    // ----- FOR STAFF USERS (Non-CEO) -----
                    if (!isTopLevelUser) {
                        // FIRST: Use the objective's own alignment from import dropdown
                        if (objectiveData.objectiveAlignment) {
                            enhancedObjectiveData.objectiveAlignment = objectiveData.objectiveAlignment;
                            enhancedObjectiveData.higherLevelObjectiveId = objectiveData.higherLevelObjectiveId;
                            enhancedObjectiveData.higherLevelObjectiveName = objectiveData.higherLevelObjectiveName;
                            enhancedObjectiveData.objectiveRoleAssigned = objectiveData.objectiveRoleAssigned;
                            console.log('✅ [STAFF] Using per-objective manager alignment:', {
                                name: objectiveData.name,
                                managerId: objectiveData.objectiveAlignment
                            });
                        } 
                                                
                        // Auto-populate company goal from the selected manager objective
                        if (enhancedObjectiveData.objectiveAlignment) {
                            const selectedManagerObj = higherLevelObjectives.find(h => h.id === enhancedObjectiveData.objectiveAlignment);
                            if (selectedManagerObj?.companyAlignedGoal) {
                                enhancedObjectiveData.companyAlignedGoal = selectedManagerObj.companyAlignedGoal;
                                const linkedGoal = goals.find(g => g.id === selectedManagerObj.companyAlignedGoal);
                                if (linkedGoal) {
                                    enhancedObjectiveData._managerGoalName = linkedGoal.name;
                                }
                            }
                        }
                    }
                    
                    // ----- FOR CEO USERS -----
                    if (isTopLevelUser) {
                        // Use the objective's own goal from import dropdown
                        if (objectiveData.companyAlignedGoal) {
                            enhancedObjectiveData.companyAlignedGoal = objectiveData.companyAlignedGoal;
                            const selectedGoalObj = goals.find(g => g.id === objectiveData.companyAlignedGoal);
                            if (selectedGoalObj) {
                                enhancedObjectiveData._managerGoalName = selectedGoalObj.name;
                                enhancedObjectiveData.linkedGoalId = selectedGoalObj.id;
                                enhancedObjectiveData.linkedGoalName = selectedGoalObj.name;
                            }
                            console.log('✅ [CEO] Using per-objective goal:', {
                                name: objectiveData.name,
                                goalId: objectiveData.companyAlignedGoal
                            });
                        }
                        // Fallback to global selectedGoal (existing behavior)
                        else if (selectedGoal) {
                            enhancedObjectiveData.companyAlignedGoal = selectedGoal;
                            const selectedGoalObj = goals.find(g => g.id === selectedGoal);
                            if (selectedGoalObj) {
                                enhancedObjectiveData._managerGoalName = selectedGoalObj.name;
                                enhancedObjectiveData.linkedGoalId = selectedGoalObj.id;
                                enhancedObjectiveData.linkedGoalName = selectedGoalObj.name;
                            }
                        }
                    }
                }

                let savedObjective;
                try {
                    if (objectiveData.id) {
                        // UPDATE EXISTING OBJECTIVE
                        console.log('🔄 [EDIT MODE] Updating existing objective:', objectiveData.id, objectiveData.name);

                        const userTeiId = positionTeiId || currentUserPosition?.trackedEntityInstance;

                        if (!userTeiId) {
                            throw new Error('Position TEI not found');
                        }

                        // Step 1: Fetch the EXISTING event from database to get its ACTUAL enrollment
                        console.log('🔍 [UPDATE] Fetching existing event to get actual enrollment...');
                        let actualEnrollmentId = null;
                        let actualOrgUnit = null;
                        let originalCreatedBy = null;
                        
                        try {
                            const eventResponse = await engine.query({
                                existingEvent: {
                                    resource: 'events',
                                    id: objectiveData.id,
                                    params: {
                                        fields: 'event,enrollment,orgUnit,storedBy,dataValues[dataElement,value]'
                                    }
                                }
                            });

                            if (eventResponse?.existingEvent) {
                                actualEnrollmentId = eventResponse.existingEvent.enrollment;
                                actualOrgUnit = eventResponse.existingEvent.orgUnit;

                                // Extract the ORIGINAL createdBy from dataValues
                                const dataValues = eventResponse.existingEvent.dataValues || [];
                                const createdByDV = dataValues.find(dv => dv.dataElement === 'uOjIh0gQMS7');

                                originalCreatedBy = createdByDV?.value || eventResponse.existingEvent.storedBy;

                                console.log(`✅ [UPDATE] Event exists with enrollment: ${actualEnrollmentId}, orgUnit: ${actualOrgUnit}`);
                            } else {
                                console.warn('⚠️ [UPDATE] Event not found in DHIS2');
                            }
                        } catch (fetchError) {
                            console.error('❌ [UPDATE] Failed to fetch event:', fetchError);
                        }

                        // Step 2: Use the actual enrollment from the event, or try to get from TEI
                        let validEnrollmentId = actualEnrollmentId;

                        if (!validEnrollmentId || validEnrollmentId === 'undefined') {
                            console.log('🔍 [UPDATE] Event has no enrollment, fetching from TEI...');
                            try {
                                const teiResponse = await engine.query({
                                    teiEnrollments: {
                                        resource: 'trackedEntityInstances',
                                        id: userTeiId,
                                        params: {
                                            fields: 'enrollments[enrollment,program,orgUnit,status]'
                                        }
                                    }
                                });

                                const enrollments = teiResponse.teiEnrollments?.enrollments || [];
                                console.log(`✅ [UPDATE] Found ${enrollments.length} enrollments`);

                                const objectivesProgramId = 'yiT00XKrrHP';
                                const validEnrollment = enrollments.find(e =>
                                    e.program === objectivesProgramId &&
                                    e.status === 'ACTIVE'
                                ) || enrollments.find(e => e.status === 'ACTIVE');

                                if (validEnrollment) {
                                    validEnrollmentId = validEnrollment.enrollment;
                                    console.log(`✅ [UPDATE] Using enrollment from TEI: ${validEnrollmentId}`);
                                } else {
                                    console.warn('⚠️ [UPDATE] No enrollment found, will update without enrollment');
                                    validEnrollmentId = undefined;
                                }
                            } catch (enrollmentError) {
                                console.error('❌ [UPDATE] Failed to get enrollment:', enrollmentError);
                                validEnrollmentId = undefined;
                            }
                        }

                        // Fetch the original objective data
                        const originalObjective = await fetchObjectiveWithDetails(objectiveData.id, engine);
                                               
                        console.log('🔄 [UPDATE] Original creator:', originalCreatedBy);

                        // ===== FETCH EXISTING EVENT DATA TO PRESERVE PRIVATE NOTES =====
                        if (!userTeiId) {
                            throw new Error('Position TEI not found');
                        }

                        // ===== FETCH EXISTING EVENT TO PRESERVE DATA =====
                        let existingPrivateNotes = {
                            managerPrivateNote: '',
                            noteCreatedBy: '',
                            noteCreatedAt: ''
                        };

                        try {
                            console.log('🔍 [UPDATE] Fetching existing event to preserve data...');
                            const eventResponse = await engine.query({
                                existingEvent: {
                                    resource: 'events',
                                    id: objectiveData.id,
                                    params: {
                                        fields: 'event,enrollment,orgUnit,storedBy,dataValues[dataElement,value]'
                                    }
                                }
                            });

                            if (eventResponse?.existingEvent) {
                                const event = eventResponse.existingEvent;
                                const dataValues = event.dataValues || [];

                                // Get enrollment and org unit
                                actualEnrollmentId = event.enrollment;
                                actualOrgUnit = event.orgUnit;
                                originalCreatedBy = event.storedBy;

                                // ✅ PRESERVE PRIVATE NOTES
                                const noteDV = dataValues.find(dv => dv.dataElement === 'rJZyovd5akX');
                                const noteCreatedByDV = dataValues.find(dv => dv.dataElement === 'x56k8a6rLss');
                                const noteCreatedAtDV = dataValues.find(dv => dv.dataElement === 'TlhIr80b8Vp');

                                existingPrivateNotes = {
                                    managerPrivateNote: noteDV?.value || '',
                                    noteCreatedBy: noteCreatedByDV?.value || '',
                                    noteCreatedAt: noteCreatedAtDV?.value || ''
                                };

                                console.log('✅ [UPDATE] Preserved private notes:', existingPrivateNotes);
                            }
                        } catch (fetchError) {
                            console.error('❌ [UPDATE] Failed to fetch event:', fetchError);
                        }
                          // ===== PRESERVE PRIVATE NOTES =====
                        const privateNotes = await preservePrivateNotes(objectiveData.id, engine);
                        console.log('✅ [UPDATE] Preserved private notes:', privateNotes);

                        // ===== 3: BUILD UPDATE PAYLOAD WITH PRESERVED PRIVATE NOTES =====
                        const updatePayload = {
                            ...enhancedObjectiveData,
                            // PRESERVE original creator
                            createdBy: originalCreatedBy || enhancedObjectiveData.createdBy || objectiveData.createdBy,
                            // PRESERVE private notes
                            managerPrivateNote: existingPrivateNotes.managerPrivateNote,
                            noteCreatedBy: existingPrivateNotes.noteCreatedBy,
                            noteCreatedAt: existingPrivateNotes.noteCreatedAt,
                            // Use actual enrollment if found
                            ...(validEnrollmentId && { enrollment: validEnrollmentId }),
                            orgUnit: actualOrgUnit || enhancedObjectiveData.orgUnit
                        };

                        // Step 4: Create event payload

                        const updateEventPayload = createEventPayload(
                            'objective',
                            {
                                ...updatePayload,
                                // Explicitly include all alignment fields
                                objectiveAlignment: updatePayload.objectiveAlignment,
                                higherLevelObjectiveId: updatePayload.higherLevelObjectiveId,
                                companyAlignedGoal: updatePayload.companyAlignedGoal,
                                linkedGoalId: updatePayload.linkedGoalId,
                                _managerGoalName: updatePayload._managerGoalName, // This won't be saved (not in mapping)

                                ManagerPrivateNote: updatePayload.managerPrivateNote,
                                NoteCreatedBy: updatePayload.noteCreatedBy,
                                NoteCreatedAt: updatePayload.noteCreatedAt,

                            },
                            objectiveData.id,
                            updatePayload.orgUnit,
                            userTeiId,
                            validEnrollmentId || undefined
                        );

                        // === MANUALLY INJECT STATUS FOR UPDATES ===
                        if (!updateEventPayload.dataValues) updateEventPayload.dataValues = [];
                        // Remove any existing status value to avoid duplicates
                        updateEventPayload.dataValues = updateEventPayload.dataValues.filter(
                            dv => dv.dataElement !== 'DL7KxNhiV0x'
                        );
                        // Add the new status value
                        updateEventPayload.dataValues.push({
                            dataElement: 'DL7KxNhiV0x',
                            value: statusValue
                        });

                        // Add event ID to the payload for UPDATE operations
                        updateEventPayload.event = objectiveData.id;

                        // If event has actual enrollment, use it directly
                        if (actualEnrollmentId && actualEnrollmentId !== 'undefined') {
                            updateEventPayload.enrollment = actualEnrollmentId;
                        } else {
                            delete updateEventPayload.enrollment;
                        }

                        console.log('🔄 [UPDATE] Payload:', {
                            event: updateEventPayload.event,
                            enrollment: updateEventPayload.enrollment,
                            orgUnit: updateEventPayload.orgUnit
                        });

                        // Call saveObjectiveEvent with isUpdate=true
                        const updateResult = await saveObjectiveEvent(
                            updateEventPayload,
                            engine,
                            true, // isUpdate = true
                            'objective'
                        );

                        if (updateResult?.success) {
                            savedObjective = {
                                ...enhancedObjectiveData,
                                id: objectiveData.id,
                                orgUnit: updateEventPayload.orgUnit || actualOrgUnit || enhancedObjectiveData.orgUnit,
                                trackedEntityInstance: userTeiId,
                                enrollment:
                                    actualEnrollmentId ||
                                    validEnrollmentId ||
                                    objectiveData.enrollment ||
                                    enhancedObjectiveData.enrollment
                            };
                            saveResults.objectives.updated++;
                            console.log('✅ [EDIT MODE] Objective updated successfully:', objectiveData.id);

                            // Update state if we found a valid enrollment
                            if (validEnrollmentId) {
                                setWeightGroupEnrollment(validEnrollmentId);
                            }
                        } else {
                            throw new Error('Update operation failed');
                        }
                    } else {

                        // CREATE NEW OBJECTIVE
                        console.log('🆕 [CREATE MODE] Creating new objective:', objectiveData.name, 'with status:', statusValue);

                        const createPayload = {
                            ...enhancedObjectiveData,
                            positionTeiId,
                            // Ensure status data element is included
                            _statusValue: statusValue,
                            _statusDataElement: 'DL7KxNhiV0x',
                            companyAlignedGoal: enhancedObjectiveData.companyAlignedGoal || '',
                        };

                        savedObjective = await handleSaveObjective(createPayload);

                        // POST-SAVE: If status wasn't saved via payload, manually update the event
                        if (savedObjective?.id && mode === 'draft') {
                            try {
                                // Verify status was saved by fetching the event
                                const verifyResponse = await engine.query({
                                    verifyEvent: {
                                        resource: `events/${savedObjective.id}`,
                                        params: { fields: 'dataValues[dataElement,value]' }
                                    }
                                });

                                const savedStatus = verifyResponse.verifyEvent?.dataValues?.find(
                                    dv => dv.dataElement === 'DL7KxNhiV0x'
                                )?.value;

                                if (String(savedStatus || '').toLowerCase() !== 'draft') {
                                    console.log('🔧 [STATUS FIX] Manually updating status to draft');
                                    await engine.mutate({
                                        resource: 'events',
                                        type: 'update',
                                        id: savedObjective.id,
                                        data: {
                                            dataValues: [{ dataElement: 'DL7KxNhiV0x', value: 'draft' }]
                                        }
                                    });
                                }
                            } catch (verifyError) {
                                console.warn('⚠️ Could not verify/update draft status:', verifyError);
                            }
                        }

                        saveResults.objectives.created++;

                    }
                } catch (error) {
                    console.error('❌ Error saving objective:', error);
                    showAlert('Save Failed', 'error', error.message || 'Unknown error during save');
                    saveResults.objectives.failed++;
                    continue;
                }

                if (!savedObjective?.id) {
                    console.error('❌ Failed to save Objective', index);
                    saveResults.objectives.failed++;
                    continue;
                }

                // CRITICAL: Track the saved objective weight (for both CREATE and UPDATE)
                savedObjectiveWeights.push({
                    id: savedObjective.id,
                    weight: objectiveData.weight,
                    name: objectiveData.name
                });

                if (objectiveData.id) {
                    // UPDATE MODE: Update the objective in state with modified data
                    console.log('🔄 [EDIT MODE] Updating objective in state:', objectiveData.id);
                    setObjectives(prev => prev.map(obj => {
                        if (obj.id === objectiveData.id) {
                            return {
                                ...obj,
                                ...objectiveData,
                                status: statusValue,
                                // Preserve the original ID and enrollment
                                id: objectiveData.id,
                                enrollment: obj.enrollment || objectiveData.enrollment
                            };
                        }
                        return obj;
                    }));
                } else {
                    // CREATE MODE: Update the objective in state with the NEW ID
                    console.log('🆕 [CREATE MODE] Adding new objective ID to state:', savedObjective.id);
                    setObjectives(prev => prev.map(obj => {
                        // Match by weight and name (since we don't have ID yet)
                        if (!obj.id && obj.weight === objectiveData.weight && obj.name === objectiveData.name) {
                            return { ...obj, id: savedObjective.id, status: statusValue };
                        }
                        return obj;
                    }));

                    setObjectives(prev => prev.map(obj =>
                        obj._tempId === objectiveData._tempId ? { ...obj, id: savedObjective.id, status: statusValue } : obj
                    ));
                }

                // Resolve parent objective from objective-level data first so email still works
                // when global selectedHigherLevelObjective is not populated.
                const parentObjectiveIdForNotification =
                    objectiveData.objectiveAlignment ||
                    objectiveData.higherLevelObjectiveId ||
                    objectives[index]?.objectiveAlignment ||
                    objectives[index]?.higherLevelObjectiveId ||
                    selectedHigherLevelObjective?.id ||
                    null;

                // Send when:
                // 1. NEW objective with parent, OR
                // 2. EXISTING objective that was returned/resubmitted and is now being finalized
                const isReturnedForCorrection =
                    isResubmittedObjective(objectiveData) ||
                    isResubmittedObjective(objectives[index]);

                const shouldSendEmail = mode === 'final' &&
                    !!parentObjectiveIdForNotification &&
                    !emailNotificationSent.current;

                if (shouldSendEmail) {
                        emailNotificationSent.current = true;
                        try {
                            let actionType = 'new';
                            
                            if (objectiveData.id) {
                                const isReturnedForCorrection = isResubmittedObjective(objectiveData) || isResubmittedObjective(objectives[index]);
                                actionType = isReturnedForCorrection ? 'resubmitted' : 'updated';
                            } else {
                                actionType = 'new';
                            }
                            
                            if (objectiveData.approvalStatus === 'returned' || objectives[index]?.approvalStatus === 'returned') {
                                actionType = 'returned';
                            }
                            
                            console.log('📧 [FORM] Sending email notification via EmailService', {
                                objectiveId: savedObjective.id,
                                parentObjectiveId: parentObjectiveIdForNotification,
                                actionType
                            });

                            // Use the new email service
                            const notificationResult = await sendObjectiveNotification(
                                savedObjective,
                                parentObjectiveIdForNotification,
                                actionType
                            );

                            if (notificationResult?.success) {
                                saveResults.notifications.success++;
                                console.log('✅ [FORM] Email notification sent successfully');
                                
                                const emailType = actionType === 'resubmitted' ? 'Resubmission' : 
                                                actionType === 'returned' ? 'Returned' :
                                                actionType === 'updated' ? 'Update' : 'New';
                                
                                showAlert(
                                    'Email Sent',
                                    'success',
                                    `${emailType} notification sent to ${notificationResult.results?.length || 0} recipient(s)`
                                );
                            } else {
                                saveResults.notifications.failed++;
                                console.warn('⚠️ [FORM] Email notification failed:', notificationResult?.error);
                                showAlert(
                                    'Email Notification Failed',
                                    'warning',
                                    `Objective saved but email notification failed: ${notificationResult?.error || 'Unknown error'}`
                                );
                            }
                        } catch (err) {
                            console.error('❌ [FORM] Email notification exception:', err);
                            saveResults.notifications.failed++;
                            showAlert('Email Error', 'warning', `Objective saved but email failed: ${err.message || 'Unknown error'}`);
                        } finally {
                            setTimeout(() => {
                                emailNotificationSent.current = false;
                            }, 30000);
                        }
                    }
                else if (objectiveData.id && parentObjectiveIdForNotification) {
                    // Log when we skip email for existing non-returned objectives
                    console.log(`🔄 [EXISTING OBJECTIVE ${index}] Skipping email notification`);
                }

                const childOrgUnitId =
                    savedObjective.orgUnit ||
                    enhancedObjectiveData.orgUnit ||
                    objectiveData.orgUnit;
                const childPositionTeiId =
                    savedObjective.trackedEntityInstance ||
                    currentUserPosition?.trackedEntityInstance ||
                    positionTeiId;
                const childEnrollmentId =
                    savedObjective.enrollment ||
                    enhancedObjectiveData.enrollment ||
                    objectiveData.enrollment ||
                    weightGroupEnrollment;

                // FIXED: Save KRAs (both new and existing) - only save if they have content
                for (const [kraIndex, kra] of objectiveData.kras.filter(k => k.name && k.name.trim()).entries()) {
                    try {
                        const kraData = {
                            name: kra.name,
                            description: kra.description || '',
                            weight: kra.weight || 0,
                            // Use the objective's alignment, not the global selection
                            objectiveAlignment: objectiveData.objectiveAlignment || enhancedObjectiveData.objectiveAlignment,
                            objectiveRoleAssigned: objectiveData.objectiveRoleAssigned || enhancedObjectiveData.objectiveRoleAssigned
                        };
                        const isKraUpdate = !!kra.id;
                        const kraEventData = createEventPayload(
                            'kra',
                            kraData,
                            savedObjective.id,
                            childOrgUnitId,
                            childPositionTeiId,
                            childEnrollmentId
                        );
                        if (isKraUpdate) {
                            kraEventData.event = kra.id;
                        }
                        const result = await saveObjectiveEvent(kraEventData, engine, isKraUpdate, 'kra');
                        if (result?.success) {
                            saveResults.kras.success++;
                            // CRITICAL: Update form state with new ID for CREATE operations
                            if (!isKraUpdate && result.eventId) {
                                console.log('🟡 Updating KRA ID in state:', {
                                    oldId: kra.id,
                                    newId: result.eventId,
                                    kraName: kra.name
                                });
                                // Update the specific KRA in the objectives state
                                setObjectives(prev => prev.map(obj => {
                                    if (obj.id === savedObjective.id) {
                                        const updatedKras = [...obj.kras];
                                        if (updatedKras[kraIndex]) {
                                            updatedKras[kraIndex] = {
                                                ...updatedKras[kraIndex],
                                                id: result.eventId
                                            };
                                        }
                                        return { ...obj, kras: updatedKras };
                                    }
                                    return obj;
                                }));
                            }
                        } else {
                            console.error('❌ KRA save failed:', kra.name, result);
                            saveResults.kras.failed++;
                        }
                    } catch (error) {
                        console.error('❌ Error saving KRA:', kra.name, error);
                        saveResults.kras.failed++;
                    }
                }

                // FIXED: Save KPIs (both new and existing) - only save if they have content
                for (const [kpiIndex, kpi] of objectiveData.kpis.filter(k => k.name && k.name.trim()).entries()) {
                    try {
                        const kpiData = {
                            name: kpi.name,
                            description: kpi.description || '',
                            target: kpi.target || '',
                            // Use the objective's alignment, not the global selection
                            objectiveAlignment: objectiveData.objectiveAlignment || enhancedObjectiveData.objectiveAlignment,
                            objectiveRoleAssigned: objectiveData.objectiveRoleAssigned || enhancedObjectiveData.objectiveRoleAssigned
                        };
                        const isKpiUpdate = !!kpi.id;
                        const kpiEventData = createEventPayload(
                            'kpi',
                            kpiData,
                            savedObjective.id,
                            childOrgUnitId,
                            childPositionTeiId,
                            childEnrollmentId
                        );
                        if (isKpiUpdate) {
                            kpiEventData.event = kpi.id;
                        }
                        const result = await saveObjectiveEvent(kpiEventData, engine, isKpiUpdate, 'kpi');
                        if (result?.success) {
                            console.log('✅ KPI saved successfully:', kpi.name);
                            saveResults.kpis.success++;
                            // CRITICAL: Update form state with new ID for CREATE operations
                            if (!isKpiUpdate && result.eventId) {
                                console.log('🟡 Updating KPI ID in state:', {
                                    oldId: kpi.id,
                                    newId: result.eventId,
                                    kpiName: kpi.name
                                });
                                // Update the specific KPI in the objectives state
                                setObjectives(prev => prev.map(obj => {
                                    if (obj.id === savedObjective.id) {
                                        const updatedKpis = [...obj.kpis];
                                        if (updatedKpis[kpiIndex]) {
                                            updatedKpis[kpiIndex] = {
                                                ...updatedKpis[kpiIndex],
                                                id: result.eventId
                                            };
                                        }
                                        return { ...obj, kpis: updatedKpis };
                                    }
                                    return obj;
                                }));
                            }
                        } else {
                            console.error('❌ KPI save failed:', kpi.name, result);
                            saveResults.kpis.failed++;
                        }
                    } catch (error) {
                        console.error('❌ Error saving KPI:', kpi.name, error);
                        saveResults.kpis.failed++;
                    }
                }

                // Create goal linkage only for brand new objectives (not imported ones)
                if (!objectiveData.id && !objectiveData._isImported && user?.roleIds?.includes('nLSSLch316I') && selectedGoal) {
                    try {
                        // ADD THIS CHECK HERE - before creating goal linkage
                        const hasExistingGoalLinkage = await checkExistingGoalLinkage(savedObjective.id);
                        if (!hasExistingGoalLinkage) {
                            console.log('🟡 Creating new goal linkage for non-imported objective...');
                            const linkageResult = await createGoalLinkage(savedObjective, selectedGoal);
                            if (linkageResult?.status === 'warning') {
                                saveResults.linkages.failed++;
                            } else {
                                saveResults.linkages.success++;
                            }
                        } else {
                            console.log('🟡 Goal linkage already exists, skipping creation');
                            saveResults.linkages.success++; // Count as success since linkage exists
                        }
                    } catch (error) {
                        console.error('❌ Error checking/creating goal linkage:', error);
                        saveResults.linkages.failed++;
                    }
                } else if (objectiveData._isImported) {
                    console.log('📋 Skipping goal linkage creation for imported objective (user will link manually):', objectiveData.name);
                }

                console.groupEnd(); // End objective processing group
            }

            // This accounts for ALL objectives (both newly created and updated ones)
            // For updated objectives: they already have IDs, so they're in the objectives array
            // For new objectives: their IDs were added to savedObjectiveWeights during processing
            const updatedObjectiveIds = new Set(savedObjectiveWeights.filter(sw =>
                objectives.some(obj => obj.id === sw.id)
            ).map(sw => sw.id));

            const allObjectiveWeights = [
                ...objectives.filter(obj => obj.id && !updatedObjectiveIds.has(obj.id)).map(obj => getObjectiveWeight(obj)), // Already saved (not just updated)
                ...savedObjectiveWeights.map(sw => getObjectiveWeight(sw)) // Just saved OR just updated
            ];
            const totalWeight = allObjectiveWeights.reduce((sum, w) => sum + w, 0);
            console.log('📊 [WEIGHT CALCULATION] Save results:', {
                created: saveResults.objectives.created,
                updated: saveResults.objectives.updated,
                totalProcessed: allObjectivesToProcess.length,
                totalWeight: totalWeight,
                weightBreakdown: allObjectiveWeights
            });

            if (mode === 'draft') {
                showAlert('Draft Saved', 'success', 'Objective saved as draft. You can finalize it later.');

                // Keep form open with current objectives (don't add blank one)
                submissionInProgress.current = false;
                setIsSubmitting(false);

                // Optionally refresh parent list to show draft status
                if (onComplete && typeof onComplete === 'function') {
                    onComplete();
                }

                // Don't close form - let user continue editing
                return;
            }

            // CRITICAL: Check if total weight equals 100% before closing form
            if (totalWeight === 100) {
                // REACHED 100% - Close form and refresh
                console.log('✅ [FORM SUBMISSION] Total weight = 100%, closing form');

                // Build success message with details
                const createdMsg = saveResults.objectives.created > 0 ? `${saveResults.objectives.created} created` : '';
                const updatedMsg = saveResults.objectives.updated > 0 ? `${saveResults.objectives.updated} updated` : '';
                const separator = createdMsg && updatedMsg ? ', ' : '';
                const objectiveMsg = `${createdMsg}${separator}${updatedMsg}`.trim() || 'objectives';

                // Show success message
                showAlert(
                    'Success',
                    'success',
                    `All ${objectiveMsg} saved successfully with 100% total weight!`
                );

                // Clear all form state
                setObjectives([]);
                setSelectedGoal('');
                setSelectedHigherLevelObjective(null);

                // CALL onComplete FIRST to trigger parent refresh
                if (onComplete && typeof onComplete === 'function') {
                    console.log('✅ [FORM] Triggering parent refresh via onComplete');
                    onComplete(); // Parent will refetch objectives
                }

                // ALSO CALL refreshObjectives to ensure data is updated
                if (refreshObjectives && typeof refreshObjectives === 'function') {
                    console.log('✅ [FORM] Triggering context refresh');
                    refreshObjectives().catch(error => {
                        console.warn('⚠️ [FORM] Context refresh failed:', error);
                    });
                }

                // CALL onSave IMMEDIATELY to close form
                if (onSave && typeof onSave === 'function') {
                    console.log('✅ [FORM] Closing form via onSave');
                    onSave(); // This sets uiMode='list' in parent
                } else {
                    console.warn('⚠️ onSave callback missing - using fallback');
                    if (setUiMode) setUiMode('list');
                }

                // Reset submission guard
                setTimeout(() => {
                    submissionInProgress.current = false;
                }, 1000);

                return; // EXIT IMMEDIATELY
            } else {
                // ❌ NOT 100% YET - Keep form open with new blank objective

                console.log('🟡 [FORM SUBMISSION] Total weight < 100%, keeping form open', {
                    currentWeight: totalWeight,
                    remaining: 100 - totalWeight
                });

                // Keep saved objectives and add new blank one
                setObjectives(prev => [
                    ...prev.filter(obj => obj.id), // Keep saved objectives with IDs
                    {
                        id: null,
                        _tempId: `temp_${Date.now()}_${prev.length}`,
                        name: '',
                        description: '',
                        weight: 0,
                        startDate: fiscalYear.start,
                        completionDate: fiscalYear.end,
                        orgUnit: user?.organisationUnits?.[0]?.id || '',
                        remarks: '',
                        score: 0,
                        performanceYear: new Date().getFullYear(),
                        workType: '',
                        companyAlignedGoal: '',
                        kras: [],
                        kpis: [{ name: '', description: '', target: '' }],
                    }
                ]);

                // Show informational message
                showAlert(
                    'Continue Adding Objectives',
                    'info',
                    `Saved ${savedObjectiveWeights.length} objective(s). Current total: ${totalWeight}%. 
                            Please add more objectives to reach exactly 100%. 
                            Remaining: ${100 - totalWeight}%`
                );

                // Reset submission guard but keep form open
                submissionInProgress.current = false;
                setIsSubmitting(false);

                // Scroll to top of form for better UX
                window.scrollTo({ top: 0, behavior: 'smooth' });

                return;
            }
        } catch (error) {
            console.error('❌ [FORM SUBMISSION CRITICAL ERROR]', error);
            showAlert(
                'Submission Failed',
                'error',
                `Failed to save objectives: ${error.message || 'Unknown error'}`
            );
        } finally {
            submissionInProgress.current = false;
            setIsSubmitting(false);
            console.log('🏁 [FORM SUBMISSION FINISHED]');
        }
    };

    if (mode === 'edit' && objective?.id && protectedObjectiveIds?.has(objective.id)) {
        const childCount = childCounts?.[objective.id] || 0;
        return (
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
                <h3 style={{ color: '#856404', marginBottom: '10px' }}>Objective Locked</h3>
                <p style={{ color: '#856404', marginBottom: '20px' }}>
                    This objective has {childCount} linked subordinate objective(s) and cannot be edited.
                    <br />
                    Please ask your subordinates to unlink their objectives first.
                </p>
                <Button onClick={onCancel} primary>
                    Back to Objectives List
                </Button>
            </div>
        );
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <WeightEditorModal />
            <form onSubmit={handleSubmit} style={{
                maxWidth: '1200px',
                padding: '24px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
                <h4 style={{
                    marginTop: 0,
                    marginBottom: '24px',
                    color: '#06910fff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    paddingBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    {isEditMode ? (
                        <>
                            <span style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: colors.blue600,
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2rem'
                            }}>
                                {objectives.length}
                            </span>
                            Edit Weight Group ({objectives.length} Objectives)
                        </>
                    ) : (
                        title
                    )}
                </h4>

                {/* ===== THE IMPORT/EXPORT ACTIONS HERE ===== */}
               <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap'
                }}>
                {/* Import Button */}
                <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: colors.blue600,
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0 6px 12px rgba(18, 36, 200, 0.5)'
                }}>
                 <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => {
                        if (e.target.files?.[0]) {
                        handleImportFile(e.target.files[0]);
                        }
                        e.target.value = ''; // Reset input
                    }}
                    style={{ display: 'none' }}
                    />
                    <IconUpload16 /> Import Objectives
                </label>
               
               {/* Export Dropdown */}
                <div style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: colors.green600,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0 6px 12px rgba(18, 106, 7, 0.5)'
                    }}
                >
                    <IconDownload16 /> Export Objectives 
                </button>
                
                {showExportMenu && (
                    <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: `1px solid ${colors.grey500}`,
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '450px'
                    }}>
                                        
                    {/* Template Download */}
                    <button
                        onClick={() => {
                        downloadObjectiveTemplate(fiscalYear);
                        setShowExportMenu(false);
                        }}
                        style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: `1px solid ${colors.grey400}`
                        }}
                    >
                        📄 Download Template <span style={{ fontSize: '11px', color: '#666', marginLeft: '8px' }}>(Blank template for new objectives)</span>
                    </button>
                    
                    {/* Past FY Export - STRIP IDs */}
                    <button
                        onClick={() => {
                        handleExportPastYearObjectives();
                        setShowExportMenu(false);
                        }}
                        style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                        }}
                    >
                        📅 Export Past Year Objectives <span style={{ fontSize: '11px', color: '#ff9800', marginLeft: '8px' }}>(Strips goal links - for migration to new FY)</span>
                    </button>
                    </div>
                )}
              </div>
              </div>                  

               {/* Import Preview Modal */}
                {showImportModal && importPreview.length > 0 && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    width: '90%',
                    maxWidth: '900px',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    padding: '24px'
                    }}>
                    <h3><IconUpload16 /> Import Preview ({importPreview.length} objectives)</h3>
                    
                    {/* ⚠️ IMPORTANT WARNING - Keep this first */}
                    <div style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffecb5',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>⚠️</span>
                        <strong style={{ color: '#856404' }}>Important: Linkages Removed</strong>
                        </div>
                        <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                        All goal linkages and manager objective alignments from the imported file have been removed.
                        You will need to manually link each objective to:
                        </p>
                        <ul style={{ margin: '8px 0 0 20px', color: '#856404', fontSize: '13px' }}>
                        <li>Current fiscal year's company goals (if you're a CEO/Top-level user)</li>
                        <li>Your manager's current objectives (if you're a staff member)</li>
                        </ul>
                    </div>
                    
                    {/* ✅ INSERT WEIGHT CALCULATION BLOCK HERE - RIGHT AFTER THE WARNING */}
                    <div style={{
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Current Total Weight:</span>
                        <strong>{objectives.reduce((sum, o) => sum + (o.weight || 0), 0)}%</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Imported Weight:</span>
                        <strong style={{ color: '#ff9800' }}>{importPreview.reduce((sum, o) => sum + (o.weight || 0), 0)}%</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #bbdefb' }}>
                        <span>New Total:</span>
                        <strong style={{ 
                            color: objectives.reduce((sum, o) => sum + (o.weight || 0), 0) + importPreview.reduce((sum, o) => sum + (o.weight || 0), 0) > 100 
                            ? '#f44336' 
                            : '#4caf50' 
                        }}>
                            {objectives.reduce((sum, o) => sum + (o.weight || 0), 0) + importPreview.reduce((sum, o) => sum + (o.weight || 0), 0)}%
                        </strong>
                        </div>
                        {objectives.reduce((sum, o) => sum + (o.weight || 0), 0) + importPreview.reduce((sum, o) => sum + (o.weight || 0), 0) > 100 && (
                        <div style={{ marginTop: '8px', color: '#f44336', fontSize: '12px' }}>
                            ⚠️ Weight limit exceeded! Please adjust weights before importing.
                        </div>
                        )}
                    </div>
                    
                    {/* Objectives List - Keep this after the weight calculation */}
                    <div style={{ marginBottom: '16px', maxHeight: '400px', overflowY: 'auto' }}>
                        {importPreview.map((obj, idx) => (
                        <div key={idx} style={{
                            padding: '12px',
                            border: `1px solid ${colors.grey300}`,
                            borderRadius: '4px',
                            marginBottom: '8px',
                            backgroundColor: obj._isImported ? '#fff8e1' : 'white'
                        }}>
                            <strong>{obj.name || `Objective ${idx + 1}`}</strong>
                            {obj._importedFrom === 'past_fy' && (
                            <span style={{
                                marginLeft: '8px',
                                fontSize: '10px',
                                padding: '2px 6px',
                                backgroundColor: '#ffc107',
                                borderRadius: '12px'
                            }}>
                                Past FY Import
                            </span>
                            )}
                            <div style={{ fontSize: '12px', color: colors.grey600, marginTop: '4px' }}>
                            Weight: {obj.weight}% | Start: {obj.startDate} | End: {obj.completionDate}
                            </div>
                            <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                            ⚠️ Goal linkage removed - needs re-linking
                            </div>
                            {obj.kpis?.length > 0 && (
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                KPIs: {obj.kpis.filter(k => k.name).length}
                            </div>
                            )}
                        </div>
                        ))}
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <Button 
                          destructive
                          icon={ <GiCancel size={16} /> }
                          onClick={() => setShowImportModal(false)}>                             
                          Cancel
                        </Button>
                        <Button 
                            primary 
                            icon={<IconUpload16 />}
                            onClick={handleConfirmImport}
                            disabled={objectives.reduce((sum, o) => sum + (o.weight || 0), 0) + importPreview.reduce((sum, o) => sum + (o.weight || 0), 0) > 100}
                            style={{ 
                                backgroundColor: objectives.reduce((sum, o) => sum + (o.weight || 0), 0) + importPreview.reduce((sum, o) => sum + (o.weight || 0), 0) > 100 
                                ? colors.grey400 
                                : '#ffc107', 
                                color: '#856404',
                                                               
                            }}
                        >
                          Import {importPreview.length} Objective(s) as Draft
                        </Button>
                    </div>
                    </div>
                </div>
                )}

                {renderPermissionContext()}

                {/* SHOWS FOR ALL OBJECTIVES WITH FEEDBACK */}
                {isEditMode && objectives.some(obj => obj.remarks) && (
                    <div style={{
                        marginTop: '24px',
                        marginBottom: '16px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        padding: '16px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                            color: '#856404',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                        }}>
                            <span role="img" aria-label="warning">⚠️</span>
                            <span>Manager Feedback Required</span>
                        </div>

                        {objectives
                            .filter(obj => obj.remarks)
                            .map((obj, idx) => (
                                <div
                                    key={`global-remarks-${obj.id || idx}`}
                                    style={{
                                        padding: '14px',
                                        backgroundColor: '#fff9e6',
                                        borderRadius: '6px',
                                        borderLeft: '3px solid #ffc107',
                                        marginBottom: idx === objectives.length - 1 ? '0' : '12px',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        color: '#5f4d00'
                                    }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: colors.blue600,
                                            color: 'white',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.85rem',
                                            flexShrink: 0
                                        }}>
                                            {idx + 1}
                                        </div>
                                        <span>"{obj.name || `Objective ${idx + 1}`}"</span>
                                    </div>

                                    <div style={{
                                        fontStyle: 'italic',
                                        color: '#333',
                                        lineHeight: 1.5,
                                        paddingLeft: '32px'
                                    }}>
                                        {obj.remarks}
                                    </div>

                                    {obj.approvalStatus === 'returned' && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '4px 10px',
                                            backgroundColor: '#ffe0b2',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            color: '#e65100',
                                            display: 'inline-block',
                                            fontWeight: '500'
                                        }}>
                                            🔄 Status: Returned for revision
                                        </div>
                                    )}
                                </div>
                            ))}

                        <div style={{
                            marginTop: '16px',
                            padding: '10px',
                            backgroundColor: '#ffe0b2',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            color: '#5f4d00',
                            borderLeft: '3px solid #e65100'
                        }}>
                            <strong>💡 Action Required:</strong> Address manager feedback above before resubmitting objectives.
                        </div>
                    </div>
                )}

                {notification && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '200px',
                        zIndex: 10000,
                        width: '500px'
                    }}>
                        <AlertBar
                            {...notification.type === 'success' ? { success: true } : { critical: true }}
                            duration={8000}
                            onHidden={clearNotification}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span>{notification.message}</span>
                                {notification.details && (
                                    <span style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                        {notification.details}
                                    </span>
                                )}
                            </div>
                        </AlertBar>
                    </div>
                )}


                {/* CONDITIONAL RENDERING BASED ON MODE AND SELECTION */}
               
                {(() => {

                if (mode === 'edit' && selectedHigherLevelObjective?.id) {
                        return (
                            <>
                                {renderHigherLevelObjectives()}
                            </>
                        );
                    }

                    // ADD MODE - No drag-and-drop, just the working dropdowns
                    // The dropdowns are already inside the objective cards and the main form
                    return null;
                })()}

                {/* Draft Filter Toggle */}
                {objectives.some(o => String(o.status || '').toLowerCase() === 'draft') && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: colors.grey050,
                        borderRadius: '6px',
                        border: `1px solid ${colors.grey300}`
                    }}>

                        {showDraftsOnly && (
                            <Button
                                small
                                secondary
                                onClick={() => setShowDraftsOnly(false)}
                                style={{ marginLeft: 'auto' }}
                            >
                                Show All
                            </Button>
                        )}
                    </div>
                )}

                {isEditMode ? (
                    <EditModeObjectiveList
                        objectives={objectives}
                        expandedObjectiveIndex={expandedObjectiveIndex}
                        totalWeight={totalWeight}
                        isSubmitting={isSubmitting}
                        onCancel={onCancel}
                        handleSubmit={handleSubmit}
                        handleRemoveObjectiveInEditMode={handleRemoveObjectiveInEditMode}
                        handleAddObjectiveInEditMode={handleAddObjectiveInEditMode}
                        updateObjectiveField={updateObjectiveField}
                        handleEditObjectiveWeightChange={handleEditObjectiveWeightChange}
                        setExpandedObjectiveIndex={setExpandedObjectiveIndex}
                        goals={goals}
                        loadingGoals={loadingGoals}
                        isTopLevelUser={isTopLevelUser}
                        onGoalSelect={(goalId, objectiveIndex) => {
                            // Optional: Add any additional logic when goal is selected
                            console.log(`Goal ${goalId} selected for objective ${objectiveIndex}`);
                        }}
                        higherLevelObjectives={higherLevelObjectives}
                        loadingHigherLevel={loadingHigherLevelObjectives}
                        onHigherLevelObjectiveSelect={(objective, objectiveIndex) => {
                            // Optional: Add any additional logic when higher-level objective is selected
                            console.log(`Higher-level objective ${objective.id} selected for objective ${objectiveIndex}`);
                        }}
                        userRole={user?.roleIds}
                        handleKraChange={handleKraChange}

                        handleAddKra={handleAddKra}
                        handleRemoveKra={handleRemoveKra}
                        handleKpiChange={handleKpiChange}
                        handleAddKpi={handleAddKpi}
                        handleRemoveKpi={handleRemoveKpi}
                    />
                ) : (
                    <>
                        {/* Existing ADD mode UI - keep your current implementation */}
                        <TotalWeightTracker
                            currentWeight={sumObjectiveWeights(objectives)}
                            isComplete={sumObjectiveWeights(objectives) === 100}
                        />

                        {/* === STEP 1: OBJECTIVE === */}
                        {currentStep === 'objective' && objectives.length > 0 && (
                            <div style={{ marginBottom: '24px', padding: '16px', border: `1px solid ${colors.grey300}`, borderRadius: '6px' }}>

                        {/* Previously Added Objectives - Show ALL imported objectives */}
                                {(() => {
                                    // 🔥 FIX: Show ALL objectives, not just previous ones
                                    // Filter out any empty/untitled objectives
                                    const allValidObjectives = objectives.filter(obj => {
                                        if (!obj.name?.trim()) return false; // Skip empty objectives
                                        if (!fiscalYear.start || !fiscalYear.end) return true;
                                        const startInFy = isWithinFiscalYear(obj.startDate, fiscalYear);
                                        const endInFy = isWithinFiscalYear(obj.completionDate, fiscalYear);
                                        return startInFy && endInFy;
                                    });
                                    
                                    if (allValidObjectives.length === 0) return null;
                                    
                                    return (
                                        <div style={{
                                            marginBottom: '20px',
                                            padding: '12px',
                                            backgroundColor: colors.grey050,
                                            border: `1px solid ${colors.grey300}`,
                                            borderRadius: '6px'
                                        }}>
                                            <h6 style={{ margin: '0 0 12px 0', color: colors.grey700 }}>
                                                Imported Objectives - Need Linking ({allValidObjectives.length})
                                            </h6>
                                            
                                            {allValidObjectives.map((obj, idx) => {
                                                const originalIndex = objectives.indexOf(obj);
                                                const isExpanded = expandedObjectiveIndex === originalIndex;
                                                const hasManagerLinkage = !isTopLevelUser ? !!obj.objectiveAlignment : false;
                                                const hasGoalLinkage = isTopLevelUser ? (obj.companyAlignedGoal || obj._tempGoalId) : false;
                                                const isLinked = isTopLevelUser ? hasGoalLinkage : hasManagerLinkage;
                                                
                                                return (
                                                    <div key={obj.id || obj._tempId || idx} style={{
                                                        marginBottom: '12px',
                                                        border: `1px solid ${isLinked ? colors.green300 : colors.orange300}`,
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        backgroundColor: isLinked ? colors.green050 : '#fff8e1',
                                                    }}>
                                                        {/* Header */}
                                                        <div 
                                                            onClick={() => setExpandedObjectiveIndex(isExpanded ? null : originalIndex)}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                padding: '12px 16px',
                                                                backgroundColor: isExpanded ? colors.blue050 : colors.grey100,
                                                                cursor: 'pointer',
                                                                flexWrap: 'wrap',
                                                                gap: '8px'
                                                            }}
                                                        >
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                    <strong>{obj.name || 'Untitled'}</strong>
                                                                    
                                                                    {/* Badges based on user type */}
                                                                    {isTopLevelUser ? (
                                                                        !hasGoalLinkage ? (
                                                                            <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: '#ff9800', color: 'white', borderRadius: '12px' }}>
                                                                                ⚠️ Needs Goal Linking
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: colors.green600, color: 'white', borderRadius: '12px' }}>
                                                                                ✓ Goal Linked
                                                                            </span>
                                                                        )
                                                                    ) : (
                                                                        !hasManagerLinkage ? (
                                                                            <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: '#f44336', color: 'white', borderRadius: '12px' }}>
                                                                                ⚠️ Needs Manager Link
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: colors.green600, color: 'white', borderRadius: '12px' }}>
                                                                                ✓ Manager Linked
                                                                            </span>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: '0.85rem', color: colors.grey600, marginTop: '4px' }}>
                                                                    Weight: {obj.weight}% | KPIs: {obj.kpis?.filter(k => k.name?.trim()).length || 0}
                                                                </div>
                                                                {isTopLevelUser && hasGoalLinkage && (
                                                                    <div style={{ fontSize: '0.75rem', color: colors.green700, marginTop: '4px' }}>
                                                                        🎯 Linked to: {goals.find(g => g.id === obj.companyAlignedGoal)?.name}
                                                                    </div>
                                                                )}
                                                                {!isTopLevelUser && hasManagerLinkage && (
                                                                    <div style={{ fontSize: '0.75rem', color: colors.green700, marginTop: '4px' }}>
                                                                        👥 Aligned to: {obj.higherLevelObjectiveName}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingWeightFor(originalIndex);
                                                                        setWeightEditValue(obj.weight || 0);
                                                                    }}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        color: colors.blue600,
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                    }}
                                                                >
                                                                    <IconEdit16 /> Edit Weight
                                                                </button>
                                                                <span>{isExpanded ? <IconChevronDown24 /> : <IconChevronRight24 />}</span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Expanded Content */}
                                                        {isExpanded && (
                                                            <div style={{ padding: '16px', backgroundColor: 'white' }}>
                                                                {/* For CEO */}
                                                                {isTopLevelUser && (
                                                                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.blue050, borderRadius: '6px' }}>
                                                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                            <strong>Link to Company Goal {!obj.companyAlignedGoal && <span style={{ color: 'red' }}>* Required</span>}</strong>
                                                                            <select
                                                                                value={obj.companyAlignedGoal || ''}
                                                                                onChange={(e) => {
                                                                                    const goalId = e.target.value;
                                                                                    setObjectives(prev => prev.map((o, i) => {
                                                                                        if (i === originalIndex) {
                                                                                            return { ...o, companyAlignedGoal: goalId, _tempGoalId: goalId };
                                                                                        }
                                                                                        return o;
                                                                                    }));
                                                                                }}
                                                                                style={{ padding: '10px', width: '100%' }}
                                                                            >
                                                                                <option value="">-- Select a Company Goal --</option>
                                                                                {goals.map(goal => (
                                                                                    <option key={goal.id} value={goal.id}>{goal.name}</option>
                                                                                ))}
                                                                            </select>
                                                                        </label>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* For Staff */}
                                                                {!isTopLevelUser && (
                                                                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: colors.purple050, borderRadius: '6px' }}>
                                                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                            <strong>Link to Manager Objective {!obj.objectiveAlignment && <span style={{ color: 'red' }}>* Required</span>}</strong>
                                                                            <select
                                                                                value={obj.objectiveAlignment || ''}
                                                                                onChange={(e) => {
                                                                                    const managerObjId = e.target.value;
                                                                                    const selectedManagerObj = higherLevelObjectives.find(o => o.id === managerObjId);
                                                                                    setObjectives(prev => prev.map((o, i) => {
                                                                                        if (i === originalIndex) {
                                                                                            return { 
                                                                                                ...o, 
                                                                                                objectiveAlignment: selectedManagerObj?.id || '',
                                                                                                higherLevelObjectiveId: selectedManagerObj?.id || '',
                                                                                                higherLevelObjectiveName: selectedManagerObj?.name || '',
                                                                                                companyAlignedGoal: selectedManagerObj?.companyAlignedGoal || ''
                                                                                            };
                                                                                        }
                                                                                        return o;
                                                                                    }));
                                                                                }}
                                                                                style={{ padding: '10px', width: '100%' }}
                                                                            >
                                                                                <option value="">-- Select a Manager Objective --</option>
                                                                                {higherLevelObjectives.map(managerObj => (
                                                                                    <option key={managerObj.id} value={managerObj.id}>
                                                                                        {managerObj.name} (by: {managerObj.createdBy})
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </label>
                                                                        
                                                                        {obj.objectiveAlignment && (
                                                                            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: colors.green100, borderRadius: '4px' }}>
                                                                                ✅ Aligned to: {obj.higherLevelObjectiveName}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* KRAs and KPIs display */}
                                                                {obj.kras?.filter(k => k.name?.trim()).length > 0 && (
                                                                    <div style={{ marginBottom: '16px' }}>
                                                                        <strong>Key Result Areas:</strong>
                                                                        <ul style={{ margin: '8px 0 0 20px' }}>
                                                                            {obj.kras.filter(k => k.name?.trim()).map((kra, kidx) => (
                                                                                <li key={kidx}><strong>{kra.name}</strong>{kra.weight > 0 && ` (${kra.weight}%)`}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                
                                                                {obj.kpis?.filter(k => k.name?.trim()).length > 0 && (
                                                                    <div>
                                                                        <strong>Key Performance Indicators:</strong>
                                                                        <ul style={{ margin: '8px 0 0 20px' }}>
                                                                            {obj.kpis.filter(k => k.name?.trim()).map((kpi, kidx) => (
                                                                                <li key={kidx}><strong>{kpi.name}</strong>{kpi.target && <span> → Target: {kpi.target}</span>}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
    
                                            
                                        </div>
                                    );
                                })()}

                                {/* Form Fields */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '16px',
                                }}>
                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Objective Name*</strong>
                                        <input
                                            type="text"
                                            value={objectives[objectives.length - 1]?.name || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'name', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>
                                </div>

                                {/* Grouped Weight and Dates in Single Row */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '16px',
                                    marginBottom: '16px',
                                    alignItems: 'end'
                                }}>
                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Weight (%) *</strong>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            required
                                            value={objectives[objectives.length - 1]?.weight || ''}
                                            onChange={e => {
                                                const rawValue = e.target.value.trim();
                                                if (rawValue === '' || rawValue === '0') {
                                                    updateObjectiveField(objectives.length - 1, 'weight', null);
                                                    return;
                                                }
                                                const val = parseInt(rawValue, 10);
                                                if (!isNaN(val) && val >= 1 && val <= 100) {
                                                    updateObjectiveField(objectives.length - 1, 'weight', val);
                                                } else {
                                                    updateObjectiveField(objectives.length - 1, 'weight', null);
                                                }
                                            }}
                                            style={{
                                                padding: '8px',
                                                marginTop: '4px',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                borderColor: objectives[objectives.length - 1]?.weight < 1 || objectives[objectives.length - 1]?.weight > 100
                                                    ? 'red'
                                                    : 'inherit'
                                            }}
                                            placeholder="Enter 1-100"
                                        />
                                        {objectives[objectives.length - 1]?.weight !== null &&
                                            (objectives[objectives.length - 1].weight < 1 || objectives[objectives.length - 1].weight > 100) && (
                                                <span style={{ color: 'red', fontSize: '0.85em', marginTop: '4px' }}>
                                                    ⚠️ Between 1-100%
                                                </span>
                                            )}
                                    </label>

                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Objective Start Date</strong>
                                        <input
                                            type="date"
                                            value={objectives[objectives.length - 1]?.startDate || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'startDate', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>

                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Objective Completion Date</strong>
                                        <input
                                            type="date"
                                            value={objectives[objectives.length - 1]?.completionDate || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'completionDate', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>
                                </div>

                                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                                    <strong>Objective Description</strong>
                                    <textarea
                                        value={objectives[objectives.length - 1]?.description || ''}
                                        onChange={e => updateObjectiveField(objectives.length - 1, 'description', e.target.value)}
                                        rows="3"
                                        style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                                    />
                                </label>

                               {/* ===== GOAL ALIGNMENT SECTION - ADD MODE ===== */}
                                {!isTopLevelUser ? (
                                    /* Staff: Show BOTH Company Goal AND Manager Objective */
                                    <div style={{ marginBottom: '16px' }}>
                                        {/* 1. Company Goal Alignment (Mandatory for ALL staff) */}
                                        <div style={{ 
                                            marginBottom: '16px', 
                                            padding: '12px', 
                                            backgroundColor: colors.blue050, 
                                            borderRadius: '6px',
                                            border: `1px solid ${colors.blue300}`
                                        }}>
                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <Goal size={16} />
                                                    Link to Company Goal 
                                                    {!objectives[objectives.length - 1]?.companyAlignedGoal && !selectedGoal && (
                                                        <span style={{ color: 'red', fontSize: '12px' }}>* Required</span>
                                                    )}
                                                </strong>
                                                
                                                <select
                                                    value={objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal || ''}
                                                    onChange={(e) => {
                                                        const goalId = e.target.value;
                                                        updateObjectiveField(objectives.length - 1, 'companyAlignedGoal', goalId);
                                                        updateObjectiveField(objectives.length - 1, '_tempGoalId', goalId);
                                                        setSelectedGoal(goalId);
                                                        
                                                        // Auto-populate goal name
                                                        const goal = goals.find(g => g.id === goalId);
                                                        if (goal) {
                                                            updateObjectiveField(objectives.length - 1, '_managerGoalName', goal.name);
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '10px',
                                                        border: `1px solid ${(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? colors.green400 : colors.orange400}`,
                                                        borderRadius: '6px',
                                                        fontSize: '0.95rem',
                                                        backgroundColor: (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? colors.green050 : 'white',
                                                        fontWeight: (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? 'bold' : 'normal',
                                                        width: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    disabled={loadingGoals || isSubmitting}
                                                >
                                                    <option value="">-- Select a Company Goal --</option>
                                                    {goals.map(goal => (
                                                        <option key={goal.id} value={goal.id}>
                                                            {goal.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                                            </label>
                                            
                                            {/* Show selected goal details */}
                                            {(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) && (
                                                <div style={{
                                                    marginTop: '8px',
                                                    padding: '8px 12px',
                                                    backgroundColor: colors.green100,
                                                    borderLeft: `3px solid ${colors.green600}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    🎯 <strong>Aligned Goal:</strong> {goals.find(g => g.id === (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal))?.name || 'Unknown Goal'}
                                                </div>
                                            )}
                                        </div>

                                        {/* 2. Higher-Level Objective Alignment (Optional) */}
                                        <div style={{ 
                                            padding: '12px', 
                                            backgroundColor: colors.purple050, 
                                            borderRadius: '6px',
                                            border: `1px solid ${colors.purple300}`
                                        }}>
                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    👥 Link to Manager Objective 
                                                    <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>(Optional)</span>
                                                </strong>
                                                
                                                <select
                                                    value={objectives[objectives.length - 1]?.objectiveAlignment || selectedHigherLevelObjective?.id || ''}
                                                    onChange={(e) => {
                                                        const objectiveId = e.target.value;
                                                        const selectedObj = higherLevelObjectives.find(obj => obj.id === objectiveId);
                                                        
                                                        if (selectedObj && objectiveId !== '') {
                                                            updateObjectiveField(objectives.length - 1, 'objectiveAlignment', selectedObj.id);
                                                            updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveId', selectedObj.id);
                                                            updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveName', selectedObj.name);
                                                            setSelectedHigherLevelObjective(selectedObj);
                                                            
                                                            // Auto-populate goal if manager objective has one AND user hasn't selected one
                                                            if (selectedObj.companyAlignedGoal && !objectives[objectives.length - 1]?.companyAlignedGoal && !selectedGoal) {
                                                                updateObjectiveField(objectives.length - 1, 'companyAlignedGoal', selectedObj.companyAlignedGoal);
                                                                updateObjectiveField(objectives.length - 1, '_tempGoalId', selectedObj.companyAlignedGoal);
                                                                setSelectedGoal(selectedObj.companyAlignedGoal);
                                                                const goal = goals.find(g => g.id === selectedObj.companyAlignedGoal);
                                                                if (goal) {
                                                                    updateObjectiveField(objectives.length - 1, '_managerGoalName', goal.name);
                                                                }
                                                            }
                                                        } else {
                                                            // Clear selection
                                                            updateObjectiveField(objectives.length - 1, 'objectiveAlignment', '');
                                                            updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveId', '');
                                                            updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveName', '');
                                                            updateObjectiveField(objectives.length - 1, '_managerGoalName', '');
                                                            setSelectedHigherLevelObjective(null);
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '10px',
                                                        border: `1px solid ${objectives[objectives.length - 1]?.objectiveAlignment ? colors.green400 : colors.purple300}`,
                                                        borderRadius: '6px',
                                                        fontSize: '0.95rem',
                                                        backgroundColor: objectives[objectives.length - 1]?.objectiveAlignment ? colors.green050 : 'white',
                                                        fontWeight: objectives[objectives.length - 1]?.objectiveAlignment ? 'bold' : 'normal',
                                                        width: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    disabled={loadingHigherLevelObjectives || isSubmitting}
                                                >
                                                    <option value="">-- Not Linked (Optional) --</option>
                                                    {higherLevelObjectives.map(obj => (
                                                        <option key={obj.id} value={obj.id}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingHigherLevelObjectives && <small style={{ color: colors.blue600 }}>Loading manager objectives...</small>}
                                                {higherLevelObjectives.length === 0 && !loadingHigherLevelObjectives && (
                                                    <small style={{ color: colors.orange700, marginTop: '4px', display: 'block' }}>
                                                        ⚠️ No manager objectives available. You can link directly to a company goal above.
                                                    </small>
                                                )}
                                            </label>
                                            
                                            {/* Show selected manager objective details */}
                                            {(objectives[objectives.length - 1]?.objectiveAlignment || selectedHigherLevelObjective?.id) && (
                                                <div style={{
                                                    marginTop: '8px',
                                                    padding: '8px 12px',
                                                    backgroundColor: colors.green100,
                                                    borderLeft: `3px solid ${colors.purple600}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    👥 <strong>Aligned to:</strong> {objectives[objectives.length - 1]?.higherLevelObjectiveName || selectedHigherLevelObjective?.name}
                                                    {(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) && (
                                                        <div style={{ marginTop: '4px', fontSize: '0.8rem' }}>
                                                            🎯 <strong>Company Goal:</strong> {goals.find(g => g.id === (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal))?.name}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {/* Help text */}
                                            <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                                                Linking to a manager objective is optional. You must link to a company goal above.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* CEO/Top-Level: Direct Goal Selection Only */
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ 
                                            padding: '12px', 
                                            backgroundColor: colors.blue050, 
                                            borderRadius: '6px',
                                            border: `1px solid ${colors.blue300}`
                                        }}>
                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <Goal size={16} />
                                                    Company Goal Alignment *
                                                </strong>
                                                <select
                                                    value={objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal || ''}
                                                    onChange={(e) => {
                                                        const goalId = e.target.value;
                                                        updateObjectiveField(objectives.length - 1, 'companyAlignedGoal', goalId);
                                                        setSelectedGoal(goalId);
                                                    }}
                                                    style={{
                                                        padding: '10px',
                                                        border: `1px solid ${(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? colors.green400 : colors.orange400}`,
                                                        borderRadius: '4px',
                                                        fontSize: '0.95rem',
                                                        backgroundColor: (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? colors.green050 : 'white',
                                                        fontWeight: (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) ? 'bold' : 'normal',
                                                        width: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    disabled={loadingGoals || isSubmitting}
                                                >
                                                    <option value="">-- Select a Company Goal --</option>
                                                    {goals.map(goal => (
                                                        <option key={goal.id} value={goal.id}>
                                                            {goal.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                                            </label>
                                            
                                            {(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) && (
                                                <div style={{
                                                    marginTop: '8px',
                                                    padding: '8px 12px',
                                                    backgroundColor: colors.green100,
                                                    borderLeft: `3px solid ${colors.green600}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    🎯 <strong>Aligned Goal:</strong> {goals.find(g => g.id === (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal))?.name || 'Unknown Goal'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                               {/* ===== END GOAL ALIGNMENT SECTION ===== */}

                                {/* Remarks field with position-based permissions */}
                                {shouldShowRemarks() && (
                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        backgroundColor: canEditRemarks() ? '#f8f9fa' : '#f0f2f5',
                                        border: `1px solid ${canEditRemarks() ? colors.grey400 : colors.grey300}`,
                                        borderRadius: '4px',
                                        opacity: isSubmitting ? 0.6 : 1
                                    }}>
                                        <label style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <strong style={{
                                                    color: canEditRemarks() ? colors.grey900 : colors.grey700
                                                }}>
                                                    Remarks / Feedback
                                                </strong>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        fontSize: '0.75rem',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        backgroundColor: canEditRemarks() ? colors.blue100 : colors.grey200,
                                                        color: canEditRemarks() ? colors.blue700 : colors.grey600
                                                    }}>
                                                        {canEditRemarks() ? 'Editable' : 'View-only'}
                                                    </span>
                                                    {mode === 'edit' && (
                                                        <span style={{
                                                            fontSize: '0.7rem',
                                                            color: colors.grey500,
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {isObjectiveCreator() ? 'Creator' : isUserDirectManager ? 'Manager' : 'Viewer'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <textarea
                                                value={objectives[objectives.length - 1]?.remarks || ''}
                                                onChange={e => {
                                                    if (canEditRemarks()) {
                                                        updateObjectiveField(objectives.length - 1, 'remarks', e.target.value);
                                                    }
                                                }}
                                                rows="3"
                                                style={{
                                                    padding: '8px',
                                                    marginTop: '4px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    resize: 'vertical',
                                                    fontStyle: 'italic',
                                                    border: `1px solid ${canEditRemarks() ? colors.grey400 : colors.grey300}`,
                                                    borderRadius: '4px',
                                                    backgroundColor: canEditRemarks() ? '#ffffff' : '#f8f9fa',
                                                    cursor: canEditRemarks() ? 'text' : 'not-allowed'
                                                }}
                                                placeholder={
                                                    mode === 'add'
                                                        ? "Add initial remarks for this objective..."
                                                        : canEditRemarks()
                                                            ? "Provide feedback or remarks for objective approval..."
                                                            : "View-only remarks (only direct managers can edit feedback)"
                                                }
                                                readOnly={!canEditRemarks() || isSubmitting}
                                                disabled={!canEditRemarks() || isSubmitting}
                                            />

                                            <small style={{
                                                color: colors.grey600,
                                                marginTop: '4px',
                                                fontStyle: 'italic'
                                            }}>
                                                {mode === 'add'
                                                    ? "You can add initial remarks for this objective."
                                                    : canEditRemarks()
                                                        ? "As the direct manager, you can provide feedback and remarks."
                                                        : "Only the direct manager can edit remarks for feedback and approval."
                                                }
                                            </small>
                                        </label>
                                    </div>
                                )}
                                {/* ===== KRAs & KPIs FOR CURRENT OBJECTIVE ===== */}
                                
                                <div style={{
                                    marginTop: '24px',
                                    marginBottom: '16px',
                                    padding: '16px',
                                    border: `1px solid ${colors.grey300}`,
                                    borderRadius: '6px',
                                    backgroundColor: colors.grey200
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '16px',
                                        paddingBottom: '12px',
                                        borderBottom: `2px solid ${colors.grey300}`
                                    }}>
                                  </div>

                                    {/* KRAs Section (OPTIONAL) */}
                                    {/*
                                    <div style={{ marginBottom: '24px' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: colors.grey100,
                                            borderRadius: '4px'
                                        }}>
                                            <div>
                                                <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                    Key Result Areas
                                                </label>
                                                <p style={{
                                                    margin: '4px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey600,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Optional - High-level areas of focus
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                    style={{
                                                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                        color: 'white',
                                                        backgroundColor: 'rgb(35, 59, 240)',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        width: '100px',
                                                        height: '35px',
                                                        cursor: 'pointer',
                                                        margin: '10px'
                                                    }}
                                                onClick={() => {
                                                    if (objectives.length === 0) return; // SAFETY CHECK
                                                    const updated = [...objectives];
                                                    if (updated[objectives.length - 1]) {
                                                        if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                        updated[objectives.length - 1].kras.push({ name: '', description: '', weight: 0 });
                                                        setObjectives(updated);
                                                    }
                                                }}
                                            >
                                                <IconAdd16 /> Add KRA
                                            </button>
                                        </div>

                                        {objectives[objectives.length - 1]?.kras?.map((kra, kraIndex) => (
                                            <div key={kraIndex} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 100px 45px',
                                                gap: '12px',
                                                marginBottom: '12px',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <input
                                                        placeholder="e.g., Improve customer satisfaction"
                                                        value={kra?.name || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                updated[objectives.length - 1].kras[kraIndex].name = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                    {kra?.name?.trim() && (
                                                        <textarea
                                                            placeholder="Describe this KRA in detail..."
                                                            value={kra?.description || ''}
                                                            onChange={(e) => {
                                                                if (objectives.length === 0) return; // SAFETY CHECK
                                                                const updated = [...objectives];
                                                                if (updated[objectives.length - 1]) {
                                                                    if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                    if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                    updated[objectives.length - 1].kras[kraIndex].description = e.target.value;
                                                                    setObjectives(updated);
                                                                }
                                                            }}
                                                            rows="2"
                                                            style={{
                                                                padding: '6px 10px',
                                                                border: `1px solid ${colors.grey200}`,
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                        Weight (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={kra?.weight || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                const weightValue = Number(e.target.value) || 0;
                                                                if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                updated[objectives.length - 1].kras[kraIndex].weight = weightValue;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        min="0"
                                                        max="100"
                                                        style={{
                                                            padding: '8px',
                                                            border: `1px solid ${colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            textAlign: 'center',
                                                            width: '100%',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    type="button"
                                                    small
                                                    destructive
                                                    onClick={() => {
                                                        if (objectives.length === 0) return; // SAFETY CHECK
                                                        const updated = [...objectives];
                                                        if (updated[objectives.length - 1]) {
                                                            if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                            updated[objectives.length - 1].kras.splice(kraIndex, 1);
                                                            setObjectives([...updated]);
                                                        }
                                                    }}
                                                    style={{ minWidth: '40px', height: '40px' }}
                                                >
                                                    <IconDelete16 />
                                                </Button>
                                            </div>
                                        ))}

                                        {objectives[objectives.length - 1]?.kras?.length === 0 && (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '20px',
                                                backgroundColor: colors.grey050,
                                                borderRadius: '4px',
                                                border: `1px dashed ${colors.grey300}`
                                            }}>
                                                <p style={{
                                                    margin: '8px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey300,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Click "Add KRA" above if you want to define key result areas
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    */}

                                    {/* KPIs Section (MANDATORY) */}
                                    <div style={{ borderTop: `2px solid ${colors.grey300}`, paddingTop: '20px' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: colors.orange050,
                                            borderRadius: '4px',
                                            border: `1px solid ${colors.orange300}`
                                        }}>
                                            <div>
                                                <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                    Key Performance Indicators <span style={{ color: colors.red500 }}>*</span>
                                                </label>
                                                <p style={{
                                                    margin: '4px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey600,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Required - Specific, measurable metrics
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                style={{
                                                    boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                    color: 'white',
                                                    backgroundColor: 'rgb(35, 59, 240)',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    width: '100px',
                                                    height: '35px',
                                                    cursor: 'pointer',
                                                    margin: '10px'
                                                }}
                                                onClick={() => {
                                                    if (objectives.length === 0) return; // SAFETY CHECK
                                                    const updated = [...objectives];
                                                    if (updated[objectives.length - 1]) {
                                                        if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                        updated[objectives.length - 1].kpis.push({ name: '', description: '', target: '' });
                                                        setObjectives(updated);
                                                    }
                                                }}
                                            >
                                                <IconAdd16 /> Add KPI
                                            </button>
                                        </div>

                                        {objectives[objectives.length - 1]?.kpis?.map((kpi, kpiIndex) => (
                                            <div key={kpiIndex} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr 100px 45px',
                                                gap: '12px',
                                                marginBottom: '12px',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: 'white',
                                                border: kpi?.name?.trim() ? `1px solid ${colors.green300}` : `1px solid ${colors.grey300}`,
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <input
                                                        placeholder="e.g., Revenue growth, Response time"
                                                        value={kpi?.name || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                updated[objectives.length - 1].kpis[kpiIndex].name = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        required
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${kpi?.name?.trim() ? colors.green300 : colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            fontWeight: kpi?.name?.trim() ? 500 : 400,
                                                            backgroundColor: kpi?.name?.trim() ? colors.green050 : 'white'
                                                        }}
                                                    />
                                                    {kpi?.name?.trim() && (
                                                        <textarea
                                                            placeholder="Describe how this KPI will be measured..."
                                                            value={kpi?.description || ''}
                                                            onChange={(e) => {
                                                                if (objectives.length === 0) return; // SAFETY CHECK
                                                                const updated = [...objectives];
                                                                if (updated[objectives.length - 1]) {
                                                                    if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                    if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                    updated[objectives.length - 1].kpis[kpiIndex].description = e.target.value;
                                                                    setObjectives(updated);
                                                                }
                                                            }}
                                                            rows="2"
                                                            style={{
                                                                padding: '6px 10px',
                                                                border: `1px solid ${colors.grey200}`,
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                        Measure
                                                    </label>
                                                    <input
                                                        placeholder="e.g., 90%, 15%, 24 hours"
                                                        value={kpi?.target || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                updated[objectives.length - 1].kpis[kpiIndex].target = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        required
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${kpi?.target?.trim() ? colors.green300 : colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            backgroundColor: kpi?.target?.trim() ? colors.green050 : 'white'
                                                        }}
                                                    />
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: kpi?.name?.trim() && kpi?.target?.trim() ? colors.green100 : colors.grey100,
                                                    borderRadius: '50%',
                                                    width: '40px',
                                                    height: '40px'
                                                }}>
                                                    {kpi?.name?.trim() && kpi?.target?.trim() ? (
                                                        <span style={{ color: colors.green900, fontSize: '1.2rem' }}>✓</span>
                                                    ) : (
                                                        <span style={{ color: colors.grey500, fontSize: '0.9rem' }}>%</span>
                                                    )}
                                                </div>
                                                <Button
                                                    type="button"
                                                    small
                                                    destructive
                                                    onClick={() => {
                                                        if (objectives.length === 0) return; // SAFETY CHECK
                                                        const updated = [...objectives];
                                                        if (updated[objectives.length - 1]) {
                                                            if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                            updated[objectives.length - 1].kpis.splice(kpiIndex, 1);
                                                            setObjectives([...updated]);
                                                        }
                                                    }}
                                                    style={{ minWidth: '40px', height: '40px' }}
                                                >
                                                    <IconDelete16 />
                                                </Button>
                                            </div>
                                        ))}

                                        {/* KPI Validation Warning */}
                                        {!objectives[objectives.length - 1]?.kpis?.some(k => k?.name?.trim()) && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '12px',
                                                backgroundColor: colors.orange050,
                                                border: `1px solid ${colors.orange500}`,
                                                borderRadius: '4px',
                                                fontSize: '0.9rem',
                                                color: colors.orange800,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                                <div>
                                                    <strong>At least one KPI is required</strong>
                                                </div>
                                            </div>
                                        )}

                                        {/* KPI Summary */}
                                        {objectives[objectives.length - 1]?.kpis?.filter(k => k?.name?.trim()).length > 0 && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '10px',
                                                backgroundColor: colors.green050,
                                                border: `1px solid ${colors.green300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                marginTop: '10px'
                                            }}>
                                                <strong>{objectives[objectives.length - 1]?.kpis?.filter(k => k?.name?.trim()).length} KPI(s) defined:</strong>
                                                <ul style={{ margin: '6px 0 0 20px', paddingLeft: 0 }}>
                                                    {objectives[objectives.length - 1]?.kpis
                                                        .filter(k => k?.name?.trim())
                                                        .map((kpi, idx) => (
                                                            <li key={idx} style={{ margin: '2px 0' }}>
                                                                <strong>{kpi?.name}</strong> - Target: {kpi?.target || 'Not set'}
                                                            </li>
                                                        ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{display: 'flex'}}>
                                {totalWeight < 100 && (
                                   <button
                                          onClick={() => {
                                            const remaining = 100 - totalWeight;
                                            const defaultWeight = remaining > 0 ? Math.min(remaining, 25) : 25;
                                            setObjectives(prev => [
                                                ...prev,
                                                {
                                                    id: null,
                                                    _tempId: `temp_${Date.now()}_${prev.length}`,
                                                    name: '',
                                                    description: '',
                                                    weight: defaultWeight,
                                                    startDate: fiscalYear.start || '',
                                                    completionDate: fiscalYear.end || '',
                                                    orgUnit: user?.organisationUnits?.[0]?.id || '',
                                                    remarks: '',
                                                    score: 0,
                                                    performanceYear: new Date().getFullYear(),
                                                    workType: '',
                                                    companyAlignedGoal: '',
                                                    kras: [],
                                                    kpis: [{ name: '', description: '', target: '' }],
                                                }
                                            ]);
                                            setExpandedObjectiveIndex(objectives.length);
                                            showAlert('New Objective Added', 'success', `You have ${remaining}% remaining to allocate`);
                                                }}
                                                style={{
                                                boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                color: 'white',
                                                backgroundColor: (isSubmitting || canSaveAsFinal()) ? colors.grey600 : 'rgb(7, 34, 240)',
                                                borderRadius: '8px',
                                                border: 'none',
                                                width: 'auto',
                                                height: '35px',
                                                cursor: (isSubmitting || canSaveAsFinal()) ? 'not-allowed' : 'pointer',
                                                margin: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                padding: '10px',
                                            }}
                                        disabled={isSubmitting || canSaveAsFinal()}
                                    >
                                        <IconAdd16 /> Add New Objective ({100 - totalWeight}% remaining)
                                    </button>
                                )}
                       {/* Save Button Section */}
                         <button
                                    style={{
                                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                        color: 'white',
                                        backgroundColor: (isSubmitting || canSaveAsFinal()) ? colors.grey600 : 'rgb(7, 34, 240)',
                                        borderRadius: '8px',
                                        border: 'none',
                                        width: 'auto',
                                        height: '35px',
                                        cursor: (isSubmitting || canSaveAsFinal()) ? 'not-allowed' : 'pointer',
                                        margin: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        padding: '10px',
                                    }}
                                    onClick={() => {
                                        // First, check if canSaveAsFinal conditions are met
                                        if (canSaveAsFinal()) {
                                            let errorMessage = 'Cannot save as final. Please fix:\n';
                                            const totalWeight = getOwnObjectivesWeight();
                                            
                                            if (totalWeight !== 100) {
                                                errorMessage += `• Total weight must be 100% (currently ${totalWeight}%)\n`;
                                            }

                                             // Check ALL objectives for missing goal linkages (mandatory for all)
                                            const objectivesMissingGoalLinkage = objectives.filter(o => {
                                                if (!o.name?.trim()) return false; // Skip empty objectives
                                                return !o.companyAlignedGoal && !o._tempGoalId;
                                            });

                                            if (objectivesMissingGoalLinkage.length > 0) {
                                                const objectiveNames = objectivesMissingGoalLinkage.map(o => o.name).join(', ');
                                                errorMessage += `• Link each objective to a company goal: ${objectiveNames}\n`;
                                            }
                                            
                                                                              
                                            // Check for missing KPIs
                                            const missingKPI = objectives.some(o => 
                                                o.name?.trim() && !o.kpis?.some(k => k.name?.trim())
                                            );
                                            if (missingKPI) {
                                                errorMessage += `• Each objective needs at least one KPI`;
                                            }
                                            
                                            showAlert('Cannot Save as Final', 'warning', errorMessage);
                                            return;
                                        }
                                        
                                        const obj = objectives[objectives.length - 1];
                                        const totalWeight = getOwnObjectivesWeight();
                                        
                                        // Check ALL objectives for missing linkages (not just the last one)
                                        // This handles imported objectives that haven't been linked
                                        const objectivesMissingLinkage = objectives.filter(o => {
                                            if (!o.name?.trim()) return false; // Skip empty objectives
                                            if (isTopLevelUser) {
                                                // CEO: Check for goal linkage
                                                return !o.companyAlignedGoal && !o._tempGoalId;
                                            } else {
                                                
                                                // For imported objectives, check their own alignment
                                                if (o._isImported) {
                                                    return !o.objectiveAlignment;
                                                }
                                                // For new objectives, check global selection
                                                return !o.objectiveAlignment && !selectedHigherLevelObjective?.id;
                                            }
                                        });
                                        
                                        if (objectivesMissingLinkage.length > 0) {
                                            const objectiveNames = objectivesMissingLinkage.map(o => o.name).join(', ');
                                            showAlert(
                                                'Validation Error', 
                                                'error', 
                                                `The following objective(s) need to be linked: ${objectiveNames}. Please select a ${isTopLevelUser ? 'company goal' : 'manager objective'} for each before finalizing.`
                                            );
                                            return;
                                        }
                                        
                                        // Check ALL objectives for KPIs (not just the last one)
                                        const objectivesMissingKPI = objectives.filter(o => {
                                            if (!o.name?.trim()) return false; // Skip empty objectives
                                            return !o.kpis?.some(k => k.name?.trim());
                                        });
                                        
                                        if (objectivesMissingKPI.length > 0 && totalWeight === 100) {
                                            const objectiveNames = objectivesMissingKPI.map(o => o.name).join(', ');
                                            showAlert(
                                                'Validation Error', 
                                                'error', 
                                                `The following objective(s) need at least one KPI: ${objectiveNames}`
                                            );
                                            return;
                                        }

                                        // Validation for the current objective (last one)
                                        if (!isTopLevelUser && !obj.objectiveAlignment && !selectedHigherLevelObjective) {
                                            showAlert('Validation Error', 'error', 'Select a higher-level objective for this objective.');
                                            return;
                                        }
                                        if (isTopLevelUser && !obj.companyAlignedGoal && !selectedGoal) {
                                            showAlert('Validation Error', 'error', 'Select a company goal for this objective.');
                                            return;
                                        }

                                        // Strict Validation for Final
                                        if (totalWeight !== 100) {
                                            showAlert('Validation Error', 'error', `Total weight must be 100% for final submission. Current: ${totalWeight}%`);
                                            return;
                                        }

                                        // VALIDATION for current objective
                                        if (obj.weight === undefined || obj.weight === null || obj.weight <= 0) {
                                            showAlert('Validation Error', 'error', 'Weight is required and must be greater than 0%.');
                                            return;
                                        }
                                        if (!obj.name?.trim()) {
                                            showAlert('Validation Error', 'error', 'Objective name is required.');
                                            return;
                                        }
                                        if (!obj.startDate || !obj.completionDate) {
                                            showAlert('Validation Error', 'error', 'Both dates are required.');
                                            return;
                                        }
                                        if (!isTopLevelUser && !selectedHigherLevelObjective) {
                                            showAlert('Validation Error', 'error', 'Select a higher-level objective.');
                                            return;
                                        }
                                        if (isTopLevelUser && !selectedGoal) {
                                            showAlert('Validation Error', 'error', 'Select a company goal.');
                                            return;
                                        }
                                        if (!obj.kpis?.some(k => k.name?.trim())) {
                                            showAlert('Validation Error', 'error', 'At least one KPI is required for this objective.');
                                            return;
                                        }

                                        // HANDLE WEIGHT FLOW (Preserves existing behavior)
                                        if (totalWeight < 100) {
                                            setObjectives(prev => [
                                                ...prev,
                                                {
                                                    id: null,
                                                    name: '',
                                                    description: '',
                                                    weight: 0,
                                                    startDate: fiscalYear.start || '',
                                                    completionDate: fiscalYear.end || '',
                                                    orgUnit: user?.organisationUnits?.[0]?.id || '',
                                                    remarks: '',
                                                    score: 0,
                                                    performanceYear: new Date().getFullYear(),
                                                    workType: '',
                                                    companyAlignedGoal: '',
                                                    enrollment: weightGroupEnrollment || '',
                                                    kras: [],
                                                    kpis: [{ name: '', description: '', target: '' }],
                                                }
                                            ]);
                                            showAlert('Objective Added', 'success', `Total weight: ${totalWeight}%. Add more objectives to reach 100%.`);
                                            return;
                                        }

                                        if (totalWeight > 100) {
                                            showAlert('Validation Error', 'error', `Total weight cannot exceed 100% (${totalWeight}%)`);
                                            return;
                                        }

                                        handleSubmit();
                                    }}
                                    disabled={isSubmitting || canSaveAsFinal()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <CircularLoader small />
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        getOwnObjectivesWeight() < 100 ? 'Add Objective & Continue' : 'Reach 100% - Save as Final'
                                    )}
                                </button>
                              
                            </div>
                         </div>
                        )}
                        
                        {/* Handle empty objectives state during save/clear */}
                        {currentStep === 'objective' && objectives.length === 0 && (
                            <div style={{
                                textAlign: 'center',
                                padding: '32px',
                                border: `1px solid ${colors.grey300}`,
                                borderRadius: '6px',
                                backgroundColor: colors.grey050
                            }}>
                                <CircularLoader small />
                                <p style={{ marginTop: '16px', color: colors.grey700, fontSize: '1.1rem' }}>
                                    Saving complete. Closing form...
                                </p>
                            </div>
                        )}


                        {/* Only show global save in edit mode */}
                        {mode === 'edit' && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                marginTop: '24px',
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '20px'
                            }}>
                                <Button onClick={onCancel} secondary disabled={isSubmitting} style={{ minWidth: '100px' }}>
                                    Cancel
                                </Button>
                                <Button primary type="submit" disabled={isSubmitting} style={{ minWidth: '120px' }} icon={isSubmitting ? <CircularLoader small /> : null}>
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </form>
        </DndProvider>
    );
};

export default ObjectiveForm;
